import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import axios from 'axios';

import ManagedBilling from './components/ManagedBilling';
import TrialStatusNotice from './components/TrialStatusNotice';
import { fetchLivePlan } from './services/livePlan';
import LivePlanBadge from './components/LivePlanBadge';
import { authFetch } from './shopify/authFetch';
import { redirectToShopifyBilling } from './utils/billingRedirect';
import ReauthModal from './components/ReauthModal';
import MultiOriginSettings from './components/MultiOriginSettings';
import PlanSelectionOnboarding from './components/PlanSelectionOnboarding';
import EmbeddedOnboarding from './components/EmbeddedOnboarding';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_KEY = process.env.REACT_APP_API_KEY;

// Feature flags for safe deployments
const FEATURE_FLAGS = {
  enhanced_analytics: true,
  store_health: true,
  country_performance: false, // Keep disabled for now
  value_metrics: true
};

const api = axios.create({
  baseURL: BACKEND_URL + '/api',
  headers: {
    'x-api-key': API_KEY,
    'Content-Type': 'application/json'
  },
  timeout: 10000 // 10 second timeout
});

// Function to extract session token from URL parameters
const getSessionToken = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('id_token');
};

// Function to get session token from URL hash (App Bridge format)
const getSessionTokenFromHash = () => {
  if (window.location.hash) {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    return hashParams.get('id_token');
  }
  return null;
};

// Function to get session token from App Bridge with retries
const getAppBridgeSessionToken = async (maxRetries = 5, retryDelay = 300) => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (window.shopify && typeof window.shopify.idToken === 'function') {
      try {
        const token = await window.shopify.idToken();
        if (token) {
          return token;
        }
      } catch (e) {
        console.warn('App.js: App Bridge idToken() attempt ' + (attempt + 1) + ' failed:', e.message);
      }
    }
    if (attempt < maxRetries - 1) {
      await new Promise(resolve => setTimeout(resolve, retryDelay));
    }
  }
  return null;
};

// Function to perform token exchange for App Store managed installations
const performTokenExchange = async (sessionToken) => {
  
  try {
    const response = await fetch(`${BACKEND_URL}/api/shopify/token-exchange`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        session_token: sessionToken
      })
    });
    
    if (response.ok) {
      const data = await response.json();
      return { success: true, data };
    } else {
      const errorText = await response.text();
      console.error('❌ App.js: Token exchange failed:', response.status, errorText);
      return { success: false, error: errorText };
    }
  } catch (error) {
    console.error('❌ App.js: Token exchange error:', error);
    return { success: false, error: error.message };
  }
};

// Function to ensure shop is registered (for App Store managed installations).
// An installed shop may deliberately have no origin until the merchant configures it in Setup.
const ensureShopRegistered = async (shop) => {
  // First check if shop exists
  try {
    const checkUrl = `${BACKEND_URL}/api/shipping-origin/${encodeURIComponent(shop)}`;
    const response = await fetch(checkUrl, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (response.ok) {
      return { registered: true, existing: true };
    }
    
    // Shop not registered - try token exchange
    
    // Try to get session token from App Bridge (with retries)
    const sessionToken = await getAppBridgeSessionToken();
    
    if (sessionToken) {
      const result = await performTokenExchange(sessionToken);
      if (result.success) {
        return { registered: true, existing: false, viaTokenExchange: true };
      }
    }
    
    return { registered: false, error: 'Could not register shop' };
  } catch (error) {
    console.error('Error ensuring shop registration:', error);
    return { registered: false, error: error.message };
  }
};

// Function to get API headers with session token for billing endpoints
const getBillingHeaders = () => {
  const sessionToken = getSessionToken();
  const headers = {
    'x-api-key': API_KEY,
    'Content-Type': 'application/json'
  };
  
  // Add session token for Shopify authentication if available
  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  }
  
  return headers;
};

// Enhanced Error Handling and Empty State Components
const ErrorBoundary = ({ children, fallback }) => {
  const [hasError, setHasError] = useState(false);
  
  useEffect(() => {
    const handleError = (error) => {
      console.error('Error caught by boundary:', error);
      setHasError(true);
    };
    
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);
  
  if (hasError) {
    return fallback || <div className="error-boundary">Something went wrong. Please refresh the page.</div>;
  }
  
  return children;
};

const LoadingSpinner = ({ size = 'medium', text = 'Loading...' }) => (
  <div className={`loading-spinner ${size}`}>
    <div className="spinner"></div>
    <span className="loading-text">{text}</span>
  </div>
);

const EmptyState = ({ 
  title = 'No Data Available', 
  description = 'There is no data to display at the moment.',
  icon = '📊',
  action = null,
  className = ''
}) => (
  <div className={`empty-state ${className}`}>
    <div className="empty-icon">{icon}</div>
    <h3 className="empty-title">{title}</h3>
    <p className="empty-description">{description}</p>
    {action && (
      <div className="empty-action">
        {action}
      </div>
    )}
  </div>
);

const ErrorState = ({ 
  title = 'Something went wrong',
  description = 'We encountered an error while loading data.',
  onRetry = null,
  className = ''
}) => (
  <div className={`error-state ${className}`}>
    <div className="error-icon">⚠️</div>
    <h3 className="error-title">{title}</h3>
    <p className="error-description">{description}</p>
    {onRetry && (
      <button 
        className="btn btn-primary retry-btn"
        onClick={onRetry}
      >
        🔄 Try Again
      </button>
    )}
  </div>
);

const NetworkErrorBanner = ({ show, onDismiss }) => {
  if (!show) return null;
  
  return (
    <div className="network-error-banner">
      <div className="banner-content">
        <span className="banner-icon">🌐</span>
        <span className="banner-text">Network connection issues detected. Some data may be outdated.</span>
        <button className="banner-close" onClick={onDismiss}>×</button>
      </div>
    </div>
  );
};

// Helper function to map server response to billingData (single source of truth)
function mapUsageResponseToBilling(d) {
  const used = Number(d?.usage?.used ?? 0);
  const limit = Number(d?.plan?.limit ?? 0);
  const usage_percentage = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  
  return {
    success: true,
    shop_domain: d?.shop || "",
    plan_name: d?.plan?.name || "Starter",
    plan_handle: d?.plan?.handle || "starter", 
    calculations_used: used,
    calculations_limit: limit,
    usage_percentage: usage_percentage,
    effective_status: d?.effective_status || 'inactive',  // Single source of truth for UI decisions
    trial: d?.trial || null,  // Trial data for completeness
    gating: {
      accessAllowed: !!d?.gating?.accessAllowed,
      isOverLimit: !!d?.gating?.isOverLimit,
      atWarning: !!d?.gating?.atWarning,
      atSoft: !!d?.gating?.atSoft,
      shouldPromptUpgrade: !!d?.gating?.shouldPromptUpgrade,
      message: d?.gating?.message || ""
    },
    ui_today: d?.ui_today || {
      calculations: 0,
      revenue: 0,
      avg_cost: 0,
      day_start_utc: null
    },
    // Additional fields needed by UI components - use server flags
    percentUsed: usage_percentage,
    at_warning: !!d?.gating?.atWarning,  // Use server-provided flag
    at_limit: !!d?.gating?.isOverLimit,   // Use server-provided flag
    paused: false
  };
}

// Main App Component
function App() {
  // Embedded onboarding policy: Setup is always the first visible tab. Merchants
  // deliberately continue to Dashboard from the guide once they are ready.
  const postBillingSetupRef = useRef(false);
  const resolveInitialTabRef = useRef(true);
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const postBillingTab = localStorage.getItem('tdk_post_billing_tab');
      if (postBillingTab) {
        localStorage.removeItem('tdk_post_billing_tab'); // consume the flag — one-time use
        console.log('[TDK] Post-billing redirect: opening tab =', postBillingTab);
        postBillingSetupRef.current = postBillingTab === 'setup';
        return postBillingTab;
      }
    } catch(e) {}
    // Render Setup while store configuration is loading to prevent a dashboard flash.
    return 'setup';
  });
  
  // Plan selection onboarding — shown on first install before setup
  const [showPlanSelectionScreen, setShowPlanSelectionScreen] = useState(false);
  // Ref mirrors showPlanSelectionScreen so it is synchronously readable inside async callbacks
  // (React state updates are async and may not be visible inside the same render cycle)
  const planSelectionRef = useRef(false);
  // Gate: true once the plan_selected check has resolved (prevents dashboard flash)
  const [planCheckComplete, setPlanCheckComplete] = useState(false);
  
  // State management
  const [stores, setStores] = useState([]);
  const [currentStorePlan, setCurrentStorePlan] = useState(null);
  const [vatRates, setVatRates] = useState([]);
  // The legacy global duty-rate library is not required to render the dashboard.
  // Load it only when a trade tool needs it, rather than blocking every boot.
  const [dutyRates, setDutyRates] = useState([]);
  const [dutyRatesLoaded, setDutyRatesLoaded] = useState(false);
  const [categories, setCategories] = useState([]);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [shippingOrigins, setShippingOrigins] = useState({});
  // cartIntegrationSettings state removed - controlled by App Embed only
  const [networkError, setNetworkError] = useState(false);
  const [dataLoadingStates, setDataLoadingStates] = useState({
    stores: false,
    vatRates: false,
    dutyRates: false,
    categories: false,
    pricing: false,
    analytics: false
  });
  
  // Analytics period selector (1, 7, 30, 90 days)
  const [analyticsPeriod, setAnalyticsPeriod] = useState(7);
  // "Hide before" date filter — excludes test data from display without deleting records
  const [analyticsStartDate, setAnalyticsStartDate] = useState('');
  // Loading state for analytics fetches (period change, start date change, initial load)
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  // Rich analytics data from the new /usage/analytics endpoint
  const [analyticsDetail, setAnalyticsDetail] = useState(null);

  // Enhanced analytics state
  const [analyticsData, setAnalyticsData] = useState({
    today_calculations: 0,
    total_calculations: 0,
    add_to_cart_value_today: 0,
    total_add_to_cart_value: 0,
    total_revenue_impact: 0, // Keep for legacy compatibility
    average_import_cost: 0,
    top_countries: [],
    recent_activity: [],
    domestic_vs_international: { domestic: 0, international: 0 },
    trend_direction: "flat"
  });
  const [storeState, setStoreStateData] = useState({}); // Track individual store loading/error states
  const [billingData, setBillingData] = useState({}); // Track billing data for stores
  
  // Reauth modal state
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [reauthShop, setReauthShop] = useState('');
  const [reauthError, setReauthError] = useState(null);

  // Helper to update individual store state
  const updateStoreState = (shop, updates) => {
    setStoreStateData(prev => ({
      ...prev,
      [shop]: { ...prev[shop], ...updates }
    }));
  };

  // Function to fetch usage for a single shop with request ID guard and fallback prevention
  const fetchUsageForShop = async (shop) => {
    const reqId = Date.now();
    updateStoreState(shop, { 
      ...(storeState[shop] || {}), 
      isLoading: true, 
      pendingReqId: reqId 
    });
    
    // Use authFetch with session token, no ?shop in production
    const nodeEnv = process.env.NODE_ENV || 'development';
    const url = nodeEnv === 'production' 
      ? `${BACKEND_URL}/api/usage/current`  // No query params in production
      : `${BACKEND_URL}/api/usage/current?shop=${encodeURIComponent(shop)}`;
    
    const fallbackTimer = setTimeout(() => {
      const st = storeState[shop];
      if (st?.pendingReqId === reqId && st?.isLoading) {
        console.warn("⚠️ Using fallback for", shop);
        const fallbackBilling = mapUsageResponseToBilling({});  // Use mapper for consistency
        
        setBillingData(prev => ({ ...prev, [shop]: fallbackBilling }));
        updateStoreState(shop, { 
          hasBilling: true, 
          isLoading: false, 
          hasError: false, 
          billingData: fallbackBilling 
        });
      }
    }, 8000);
    
    try {
      const res = await authFetch(url, {
        headers: {
          'x-api-key': API_KEY,
          'Cache-Control': 'no-store',  // Prevent caching
          'Pragma': 'no-cache'
        },
        credentials: "include"
      });
      
      if (storeState[shop]?.pendingReqId !== reqId) return;
      
      // Handle 429 (over-limit) responses
      if (res.status === 429) {
        const errorData = await res.json();
        console.warn("🚫 Usage limit exceeded for", shop, errorData);
        
        // Show blocking modal/banner for upgrade
        const overLimitData = {
          isOverLimit: true,
          plan: errorData.detail?.plan || 'unknown',
          used: errorData.detail?.used || 0,
          limit: errorData.detail?.limit || 0,
          periodEndUtc: errorData.detail?.period_end_utc,
          upgradeUrl: errorData.detail?.upgrade_url,
          retryAfter: res.headers.get('Retry-After')
        };
        
        updateStoreState(shop, { 
          hasBilling: true, 
          isLoading: false, 
          hasError: false,
          isOverLimit: true,
          overLimitData 
        });
        
        return; // Don't process as normal response
      }
      
      const data = await res.json();
      clearTimeout(fallbackTimer);
      
      
      // Check if store needs reauthorization
      if (data?.reauth?.needs_reauth === true) {
        console.warn("🔐 Store needs reauthorization:", shop);
        setReauthShop(shop);
        setReauthError(data.reauth.error_details);
        setShowReauthModal(true);
      }
      
      const billing = mapUsageResponseToBilling(data);
      
      setBillingData(prev => ({ ...prev, [shop]: billing }));
      updateStoreState(shop, { 
        hasBilling: true, 
        isLoading: false, 
        hasError: false, 
        billingData: billing 
      });

      // Enrich with live Admin plan (name/interval/price) if available
      try {
        const live = await fetchLivePlan(shop);
        setBillingData(prev => {
          const current = prev[shop] || {};
          return {
            ...prev,
            [shop]: {
              ...current,
              plan_name: live?.name || current.plan_name,
              live_pricing: live?.pricing || null,
              live_status: live?.status || null,
            }
          };
        });
      } catch (e) {
        // Non-fatal: keep usage-derived plan if live plan fails
        console.warn('Live plan fetch failed for', shop, e?.message || e);
      }
      
    } catch (err) {
      if (storeState[shop]?.pendingReqId !== reqId) return;
      
      clearTimeout(fallbackTimer);
      
      // Check if this is a 429 error from fetch exception
      if (err.name === 'HTTPError' && err.response?.status === 429) {
        try {
          const errorData = await err.response.json();
          console.warn("🚫 Usage limit exceeded (fetch error) for", shop, errorData);
          
          const overLimitData = {
            isOverLimit: true,
            plan: errorData.detail?.plan || 'unknown',
            used: errorData.detail?.used || 0,
            limit: errorData.detail?.limit || 0,
            periodEndUtc: errorData.detail?.period_end_utc,
            upgradeUrl: errorData.detail?.upgrade_url,
            retryAfter: err.response.headers.get('Retry-After')
          };
          
          updateStoreState(shop, { 
            hasBilling: true, 
            isLoading: false, 
            hasError: false,
            isOverLimit: true,
            overLimitData 
          });
          
          return;
        } catch (parseError) {
          console.error("Failed to parse 429 error response:", parseError);
        }
      }
      
      console.error("❌ Usage fetch error for", shop, err);
      updateStoreState(shop, { 
        hasBilling: true, 
        isLoading: false, 
        hasError: true 
      });
    }
  };

  // Function to load billing/usage data for all stores
  const loadUsageData = async () => {
    if (!stores || stores.length === 0) {
      return;
    }

    
    // Load usage data for each store in parallel using the new fetch function
    const usagePromises = stores.map(store => fetchUsageForShop(store.shop_domain));
    await Promise.all(usagePromises);
    
  };
  
  // Load initial data
  useEffect(() => {
    // Await the plan_selected check BEFORE calling loadData so the dashboard
    // cannot flash before we know whether to show the plan picker.
    const urlParams = new URLSearchParams(window.location.search);
    const shopParam = urlParams.get('shop');

    console.log('[TDK] App mount — shop param:', shopParam);
    console.log('[TDK] Full URL:', window.location.href);

    const init = async () => {
      // Plan selection is now handled in index.js BEFORE App mounts.
      // If we reach here, planSelected===true and we can load the dashboard.
      setPlanCheckComplete(true);
      loadData();
    };

    init();
    
    // Listen for reauth events from child components
    const handleNeedsReauth = (event) => {
      console.warn("🔐 Reauth event received:", event.detail);
      setReauthShop(event.detail.shop);
      setReauthError(event.detail.errorDetails);
      setShowReauthModal(true);
    };
    
    window.addEventListener('needsReauth', handleNeedsReauth);
    
    return () => {
      window.removeEventListener('needsReauth', handleNeedsReauth);
    };
  }, []);

  const setDataLoadingState = (key, state) => {
    setDataLoadingStates(prev => ({ ...prev, [key]: state }));
  };

  const handleApiError = (error, context = '') => {
    console.error(`API Error ${context}:`, error);
    
    if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
      setNetworkError(true);
      return `Network connection failed${context ? ` while loading ${context}` : ''}`;
    }
    
    if (error.response?.status === 401) {
      return 'Authentication failed. Please check your API key.';
    }
    
    if (error.response?.status === 403) {
      return 'Access denied. You may not have permission for this resource.';
    }
    
    if (error.response?.status >= 500) {
      return `Server error occurred${context ? ` while loading ${context}` : ''}. Please try again later.`;
    }
    
    return error.response?.data?.detail || error.message || `Failed to load ${context || 'data'}`;
  };

  const loadDutyRates = async (force = false) => {
    if (!force && (dutyRatesLoaded || dataLoadingStates.dutyRates)) return;

    setDataLoadingState('dutyRates', true);
    try {
      const result = await api.get('/duty-rates');
      setDutyRates(result.data || []);
      setDutyRatesLoaded(true);
    } catch (err) {
      console.warn('Failed to load duty rates:', err.message);
      setError(handleApiError(err, 'duty rates'));
    } finally {
      setDataLoadingState('dutyRates', false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Load basic data with individual error handling
      // Get shop parameter from URL for per-merchant filtering
      const urlParams = new URLSearchParams(window.location.search);
      // Always get shop parameter from URL since this is a Shopify embedded app
      // Shopify always includes ?shop= parameter in embedded app URLs
      const shopParam = urlParams.get('shop');
      
      // For App Store managed installations, ensure shop is registered via token exchange
      // This handles the case where OAuth callback was never called
      if (shopParam) {
        const registrationResult = await ensureShopRegistered(shopParam);
        
        if (registrationResult.viaTokenExchange) {
        }
      }
      
      const loadPromises = [
        {
          key: 'stores',
          promise: shopParam ? api.get(`/stores?shop=${shopParam}`) : api.get('/stores'),
          fallback: []
        },
        {
          key: 'vatRates', 
          promise: api.get('/vat-rates'),
          fallback: []
        },
        {
          key: 'categories',
          promise: api.get('/product-categories?limit=500'),
          fallback: []
        },
        {
          key: 'pricing',
          promise: api.get('/pricing'),
          fallback: []
        }
      ];
      
      const results = {};
      const errors = {};
      
      // Load each data source with individual error handling
      for (const { key, promise, fallback } of loadPromises) {
        setDataLoadingState(key, true);
        try {
          const result = await promise;
          results[key] = result.data || fallback;
        } catch (err) {
          console.warn(`Failed to load ${key}:`, err.message);
          results[key] = fallback;
          errors[key] = handleApiError(err, key);
        } finally {
          setDataLoadingState(key, false);
        }
      }
      
      // Set data from results
      setStores(results.stores);
      setVatRates(results.vatRates);
      setCategories(results.categories);
      setPricingTiers(results.pricing);
      
      // Load analytics data with feature flag check
      if (FEATURE_FLAGS.enhanced_analytics) {
        setDataLoadingState('analytics', true);
        try {
          const firstStore = results.stores[0];
          const shopDomain = firstStore?.shop_domain;
          

          
          if (shopDomain) {
            try {
              // Use the usage endpoint as single source of truth since it's reliable
              const usageRes = await api.get(`/usage/current?shop=${shopDomain}`);
              
              if (usageRes.status === 200 && usageRes.data) {
                const usageData = usageRes.data;
                
                // Transform usage data into analytics format
                const analyticsData = {
                  // Core calculations
                  todays_calculations: usageData.ui_today?.calculations || 0,
                  today_calculations: usageData.ui_today?.calculations || 0, // Legacy compatibility
                  
                  // Cart value data (more accurate than "revenue")
                  // This represents the total value of items in cart when calculator was used
                  // TEMPORARY FIX: Calculate reasonable cart value from recent activity
                  add_to_cart_value_today: (() => {
                    const recentTotal = (usageData.recent || [])
                      .filter(item => {
                        const itemDate = new Date(item.created_at).toDateString();
                        const todayDate = new Date().toDateString();
                        return itemDate === todayDate;
                      })
                      .reduce((sum, item) => sum + (item.amount || 0), 0);
                    
                    // Use raw amount values directly - no adjustment needed
                    return recentTotal;
                  })(),
                  
                  assisted_revenue: usageData.ui_today?.revenue || 0, // Keep for compatibility
                  total_add_to_cart_value: usageData.ui_today?.revenue || 0, // More accurate naming
                  total_revenue_impact: usageData.ui_today?.revenue || 0, // Legacy compatibility
                  
                  // Import cost
                  average_import_cost: usageData.ui_today?.avg_cost || 0,
                  
                  // Recent activity uses the actual destination captured by the
                  // calculation service. Never infer a country from currency:
                  // EUR, USD, and GBP are all used by multiple destinations.
                  recent_activity: (usageData.recent || []).map(item => {
                    const rawCountry = String(item.destination_country || item.customer_country || '').trim().toUpperCase();
                    const destinationCountry = rawCountry.length === 2 && /^[A-Z]{2}$/.test(rawCountry)
                      ? rawCountry
                      : null;
                    return {
                      timestamp: item.created_at,
                      total_taxes: item.import_costs || 0,
                      cart_value: item.amount || 0,
                      currency: item.currency || 'USD',
                      customer_country: destinationCountry,
                      shop_domain: item.shop || 'unknown',
                      calc_id: item.calc_id,
                      id: item.id
                    };
                  }),
                  
                  // Fallback country ranking before the richer period endpoint
                  // arrives. Include only real ISO-2 destinations.
                  top_countries: (() => {
                    const countryCount = {};
                    (usageData.recent || []).forEach(item => {
                      const country = String(item.destination_country || item.customer_country || '').trim().toUpperCase();
                      if (/^[A-Z]{2}$/.test(country)) {
                        countryCount[country] = (countryCount[country] || 0) + 1;
                      }
                    });
                    return Object.entries(countryCount)
                      .sort(([, a], [, b]) => b - a)
                      .slice(0, 5)
                      .map(([country, calculations]) => ({ country, calculations, import_costs: 0 }));
                  })(),
                  
                  // Usage stats
                  total_calculations: usageData.usage?.used || 0,
                  calculations_used: usageData.usage?.used || 0,
                  
                  // Plan information
                  plan_name: usageData.plan?.name || 'Unknown',
                  plan_limit: usageData.plan?.limit || 0,
                  
                  // Additional metrics with defaults
                  vs_yesterday_delta: null,
                  estimated_incremental_revenue: null,
                  international_orders_percent: 0,
                  top_countries_pie: [], // Keep for compatibility if needed elsewhere
                  store_health: {
                    lifetime_total_calculations: usageData.usage?.used || 0,
                    api_error_rate_today: 0.0
                  },
                  cart_abandonment_reduction: null,
                  latency_p95_today: 0.0,
                  error_rate_today: 0.0,
                  widget_visibility_rate: null,
                  calculation_to_order_attach_rate: null,
                  
                  // Metadata
                  period: 'today',
                  shop_id: shopDomain,
                  generated_at: new Date().toISOString(),
                  data_source: 'usage_api' // Indicate this came from usage API
                };
                
                setAnalyticsData(analyticsData);

                // Also fetch rich period analytics from the new endpoint
                try {
                  // Note: analyticsStartDate may not be loaded yet on first render;
                  // fetchAnalyticsForPeriod will re-run with it once settings are loaded.
                  const analyticsRes = await api.get(`/usage/analytics?shop=${encodeURIComponent(shopDomain)}&days=7`);
                  if (analyticsRes.status === 200 && analyticsRes.data) {
                    setAnalyticsDetail(analyticsRes.data);
                  }
                } catch (analyticsErr) {
                  console.warn('Period analytics fetch failed (non-critical):', analyticsErr.message);
                }
                
              }
            } catch (usageError) {
              // Fallback to original analytics endpoint
              try {
                const analyticsRes = await api.get(`/analytics/summary?shop_id=${shopDomain}&period=today`);
                if (analyticsRes.status === 200 && analyticsRes.data) {
                  setAnalyticsData(analyticsRes.data);

                }
              } catch (fallbackError) {
                console.error('❌ Both usage and analytics endpoints failed:', fallbackError);
              }
            }
          } else {
          }
        } catch (err) {
          console.warn('Analytics loading failed:', err.message);
          errors.analytics = handleApiError(err, 'analytics');
        } finally {
          setDataLoadingState('analytics', false);
        }
      }
      
      // Load additional data for stores
      if (results.stores.length > 0) {
        const origins = {};
        const cartSettings = {};
        
        for (const store of results.stores) {
          try {
            const originRes = await api.get(`/shipping-origin/${store.shop_domain}`);
            origins[store.shop_domain] = originRes.data.shipping_origin_country || null;
          } catch (error) {
            // Keep origin unset. The embedded Setup guide requires the merchant to choose it.
            origins[store.shop_domain] = null;
          }
          
          // Load analytics_start_date (hide-before filter) from store settings
          try {
            const settingsRes = await api.get(`/store-settings/${encodeURIComponent(store.shop_domain)}`);
            if (settingsRes.data && settingsRes.data.analytics_start_date) {
              setAnalyticsStartDate(settingsRes.data.analytics_start_date);
            }
          } catch (_) {}
          
          // Skip cart integration settings - controlled by App Embed only
        }
        
        setShippingOrigins(origins);
        if (resolveInitialTabRef.current) {
          // Do not infer readiness from a saved origin. A merchant may have an
          // origin but still need the HS-code, test-estimate, or storefront steps.
          // Keeping Setup active prevents async data loading from overriding the guide.
          setActiveTab('setup');
          resolveInitialTabRef.current = false;
        }
        // Cart integration settings removed - controlled by App Embed only
      }
      
      // Show summary of any errors
      const errorKeys = Object.keys(errors);
      if (errorKeys.length > 0) {
        setError(`Some data could not be loaded: ${errorKeys.join(', ')}. App will continue with available data.`);
      } else {
        setSuccess('Data loaded successfully');
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      }
      
    } catch (err) {
      const errorMessage = handleApiError(err, 'application data');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Billing navigation is centralized to prevent cross-app subscriptions.
  const handleUpgrade = (shopDomain) => {
    const shop = shopDomain || new URLSearchParams(window.location.search).get('shop') || stores[0]?.shop_domain;
    if (!redirectToShopifyBilling(shop)) {
      setError('Unable to open Shopify billing. Please contact support to confirm your app billing configuration.');
    }
  };

  // Fetch rich analytics data for a given period from the new /usage/analytics endpoint
  const fetchAnalyticsForPeriod = async (days, shopDomain, startDate) => {
    const shop = shopDomain || stores[0]?.shop_domain;
    if (!shop) return;
    setAnalyticsLoading(true);
    try {
      const hideBefore = startDate !== undefined ? startDate : analyticsStartDate;
      let url = `/usage/analytics?shop=${encodeURIComponent(shop)}&days=${days}`;
      if (hideBefore) url += `&analytics_start_date=${encodeURIComponent(hideBefore)}`;
      const res = await api.get(url);
      if (res.status === 200 && res.data) {
        setAnalyticsDetail(res.data);
      }
    } catch (err) {
      console.warn('Analytics detail fetch failed:', err.message);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const updateShippingOrigin = async (shopDomain, countryCode) => {
    try {
      setLoading(true);
      await api.put(`/shipping-origin/${shopDomain}`, {
        shipping_origin_country: countryCode
      });
      
      setShippingOrigins(prev => ({
        ...prev,
        [shopDomain]: countryCode
      }));
      
      // Update the stores array to reflect the change immediately
      setStores(prevStores => 
        prevStores.map(store => 
          store.shop_domain === shopDomain 
            ? { ...store, shipping_origin_country: countryCode }
            : store
        )
      );
      
      setSuccess('Shipping origin updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      
      // Trigger analytics reload to reflect new country data
      await loadData();
      
    } catch (error) {
      const errorMessage = handleApiError(error, 'updating shipping origin');
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Cart integration function removed - controlled by App Embed only

  const initializeEnhancedData = async () => {
    setLoading(true);
    try {
      await api.post('/initialize-enhanced-data');
      setSuccess('Enhanced data with government APIs initialized successfully!');
      await loadData();
    } catch (err) {
      setError('Failed to initialize enhanced data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  return (
    <div className="App">
      {/* Reauth Modal - shows when OAuth token is invalid */}
      <ReauthModal
        show={showReauthModal}
        shopDomain={reauthShop}
        errorDetails={reauthError}
        onReconnect={(url) => {
          // Modal will handle the redirect
        }}
        onDismiss={() => {
          setShowReauthModal(false);
        }}
        dismissible={true}
      />
      
      {/* Plan Selection Onboarding — shown on first install before setup.
           - planCheckComplete gates the render until the plan_selected API check resolves.
           - planSelectionRef.current is a synchronous ref that is set before React state
             commits, preventing any re-render from showing the dashboard in the gap.
           - showPlanSelectionScreen is the React state that drives the render once committed. */}
      {(!planCheckComplete || planSelectionRef.current) && !showPlanSelectionScreen ? (
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',flexDirection:'column',gap:'1rem',background:'#0f172a',color:'#fff'}}>
          <div style={{width:40,height:40,border:'3px solid #6366f1',borderTopColor:'transparent',borderRadius:'50%',animation:'spin 0.8s linear infinite'}} />
          <p style={{fontSize:'0.9rem',opacity:0.7}}>Loading Tax &amp; Duty King…</p>
        </div>
      ) : showPlanSelectionScreen || planSelectionRef.current ? (
        <PlanSelectionOnboarding shopDomain={new URLSearchParams(window.location.search).get('shop') || ''} />
      ) : (
        <>
          <header className="header">
            <h1>Tax & Duty King 👑</h1>

          </header>

      <nav className="nav-tabs">
        <button
          className={activeTab === 'setup' ? 'active' : ''}
          onClick={() => { setActiveTab('setup'); clearMessages(); }}
        >
          🧭 Setup
        </button>
        <button 
          className={activeTab === 'dashboard' ? 'active' : ''}
          onClick={() => { setActiveTab('dashboard'); clearMessages(); }}
        >
          📊 Dashboard
        </button>
        <button 
          className={activeTab === 'billing' ? 'active' : ''}
          onClick={() => { setActiveTab('billing'); clearMessages(); }}
        >
          💳 Billing
        </button>
        <button 
          className={activeTab === 'calculator' ? 'active' : ''}
          onClick={() => { setActiveTab('calculator'); clearMessages(); loadDutyRates(); }}
        >
          🧮 Calculator
        </button>
        <button 
          className={activeTab === 'vat-rates' ? 'active' : ''}
          onClick={() => { setActiveTab('vat-rates'); clearMessages(); }}
        >
          📋 VAT Rates
        </button>
        <button 
          className={activeTab === 'duty-rates' ? 'active' : ''}
          onClick={() => { setActiveTab('duty-rates'); clearMessages(); loadDutyRates(); }}
        >
          📦 Duty Rates
        </button>
        <button 
          className={activeTab === 'currency-rates' ? 'active' : ''}
          onClick={() => { setActiveTab('currency-rates'); clearMessages(); }}
        >
          💱 Currency Rates
        </button>
        <button 
          className={activeTab === 'hs-codes' ? 'active' : ''}
          onClick={() => { setActiveTab('hs-codes'); clearMessages(); }}
        >
          🔖 HS Codes
        </button>
        <button 
          className={activeTab === 'settings' ? 'active' : ''}
          onClick={() => { setActiveTab('settings'); clearMessages(); }}
        >
          ⚙️ Settings
        </button>
      </nav>

      <main className="main-content">
        <TrialStatusNotice
          shop={stores[0]?.shop_domain || new URLSearchParams(window.location.search).get('shop') || ''}
          onOpenBilling={() => { setActiveTab('billing'); clearMessages(); }}
        />
        {error && (
          <div className="alert alert-error">
            ❌ {error}
            <button onClick={() => setError('')}>×</button>
          </div>
        )}
        
        {success && (
          <div className="alert alert-success">
            ✅ {success}
            <button onClick={() => setSuccess('')}>×</button>
          </div>
        )}

        {loading && <div className="loading">Loading data, please wait...</div>}

        {!loading && activeTab === 'setup' && (
          <EmbeddedOnboarding
            apiClient={api}
            stores={stores}
            onUpdateShippingOrigin={updateShippingOrigin}
            onNavigate={(tab) => {
              if (tab === 'settings-ddp' || tab === 'settings-multi-origin') {
                setActiveTab('settings');
                clearMessages();
                const selector = tab === 'settings-ddp' ? '.ddp-section' : '.multi-origin-section';
                window.setTimeout(() => document.querySelector(selector)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 180);
                return;
              }
              setActiveTab(tab);
              clearMessages();
              if (tab === 'calculator' || tab === 'duty-rates') loadDutyRates();
            }}
            onComplete={() => { setActiveTab('dashboard'); clearMessages(); }}
            renderSetupLink={(linkType) => (
              <SetupLink
                linkType={linkType}
                shop={stores[0]?.shop_domain || new URLSearchParams(window.location.search).get('shop') || ''}
                appHandle={process.env.REACT_APP_SHOPIFY_APP_HANDLE || 'vat-duty-king'}
                className="embedded-onboarding-theme-link"
              />
            )}
            renderStorefrontVideo={() => <SetupHelpVideo variant="onboarding" />}
          />
        )}

        {!loading && activeTab === 'dashboard' && (
          <EnhancedDashboard 
            stores={stores} 
            vatRates={vatRates} 
            dutyRates={dutyRates} 
            categories={categories}
            analyticsData={analyticsData}
            analyticsDetail={analyticsDetail}
            analyticsPeriod={analyticsPeriod}
            analyticsStartDate={analyticsStartDate}
            analyticsLoading={analyticsLoading}
            onAnalyticsPeriodChange={(days) => {
              setAnalyticsPeriod(days);
              fetchAnalyticsForPeriod(days, stores[0]?.shop_domain);
            }}
            onStartDateChange={async (newDate) => {
              setAnalyticsStartDate(newDate);
              // Persist to store settings
              const shop = stores[0]?.shop_domain;
              if (shop) {
                try {
                  await api.patch(`/store-settings/${encodeURIComponent(shop)}`, { analytics_start_date: newDate || null });
                } catch (_) {}
              }
              // Re-fetch analytics with new floor
              fetchAnalyticsForPeriod(analyticsPeriod, stores[0]?.shop_domain, newDate);
            }}
            onInitializeData={initializeEnhancedData}
            shippingOrigins={shippingOrigins}
            onOpenSetup={() => setActiveTab('setup')}
          />
        )}
        
        {activeTab === 'calculator' && (
          <Calculator vatRates={vatRates} dutyRates={dutyRates} categories={categories} stores={stores} billingData={billingData} shippingOrigins={shippingOrigins} />
        )}
        
        {activeTab === 'vat-rates' && (
          <VATRatesManager 
            vatRates={vatRates} 
            onReload={loadData}
            backendUrl={BACKEND_URL}
            apiKey={API_KEY}
          />
        )}
        
        {activeTab === 'duty-rates' && (
          <>
            <DutyRateAgentPanel backendUrl={BACKEND_URL} apiKey={API_KEY} />
            <DutyRatesManager 
              dutyRates={dutyRates} 
              categories={categories} 
              onReload={() => loadDutyRates(true)}
              backendUrl={BACKEND_URL}
              apiKey={API_KEY}
              shop={stores[0]?.shop_domain || ''}
            />
          </>
        )}
        
        {activeTab === 'currency-rates' && (
          <CurrencyRatesManager
            backendUrl={BACKEND_URL}
            apiKey={API_KEY}
          />
        )}

        {activeTab === 'hs-codes' && (
          <HSCodesManager
            shopDomain={stores[0]?.shop_domain}
            backendUrl={BACKEND_URL}
          />
        )}
        
        {!loading && activeTab === 'settings' && (
          <>
            <SettingsManager 
              stores={stores} 
              onReload={loadData} 
              shippingOrigins={shippingOrigins}
              onUpdateShippingOrigin={updateShippingOrigin}
              storeStateData={storeState}
              onUpgrade={handleUpgrade}
            />
          </>
        )}
        
        {activeTab === 'billing' && (
          (process.env.REACT_APP_ENABLE_MANAGED_PRICING === 'true') ? (
            <ManagedBilling />
          ) : (
            <SubscriptionManagement stores={stores} onReload={loadData} />
          )
        )}
      </main>
      </>
      )}
    </div>
  );
}

// Currency Conversion and Formatting Utilities
const CurrencyUtils = {
  // Get currency code based on country code
  getCountryCurrency: (countryCode) => {
    const countryToCurrency = {
      'US': 'USD', 'CA': 'CAD', 'GB': 'GBP', 'AU': 'AUD', 'NZ': 'NZD',
      'DE': 'EUR', 'FR': 'EUR', 'IT': 'EUR', 'ES': 'EUR', 'NL': 'EUR', 
      'BE': 'EUR', 'AT': 'EUR', 'IE': 'EUR', 'PT': 'EUR', 'FI': 'EUR',
      'GR': 'EUR', 'LU': 'EUR', 'MT': 'EUR', 'CY': 'EUR', 'SK': 'EUR',
      'SI': 'EUR', 'EE': 'EUR', 'LV': 'EUR', 'LT': 'EUR',
      'CH': 'CHF', 'SE': 'SEK', 'NO': 'NOK', 'DK': 'DKK', 'PL': 'PLN',
      'CZ': 'CZK', 'HU': 'HUF', 'BG': 'BGN', 'RO': 'RON', 'HR': 'HRK',
      'JP': 'JPY', 'CN': 'CNY', 'IN': 'INR', 'KR': 'KRW', 'SG': 'SGD',
      'HK': 'HKD', 'TW': 'TWD', 'TH': 'THB', 'MY': 'MYR', 'ID': 'IDR',
      'PH': 'PHP', 'VN': 'VND', 'BR': 'BRL', 'MX': 'MXN', 'AR': 'ARS',
      'CL': 'CLP', 'CO': 'COP', 'PE': 'PEN', 'AE': 'AED', 'SA': 'SAR',
      'IL': 'ILS', 'ZA': 'ZAR', 'TR': 'TRY', 'RU': 'RUB', 'IS': 'ISK'
    };
    return countryToCurrency[countryCode] || 'USD';
  },

  // Get currency symbol based on currency code
  getCurrencySymbol: (currency) => {
    const symbols = {
      'USD': '$', 'EUR': '€', 'GBP': '£', 'CAD': 'C$', 'AUD': 'A$', 'JPY': '¥',
      'CHF': 'CHF', 'SEK': 'kr', 'NOK': 'kr', 'DKK': 'kr', 'PLN': 'zł',
      'CZK': 'Kč', 'HUF': 'Ft', 'BGN': 'лв', 'RON': 'lei', 'HRK': 'kn',
      'RSD': 'дин', 'ISK': 'kr', 'TRY': '₺', 'RUB': '₽', 'CNY': '¥',
      'INR': '₹', 'KRW': '₩', 'SGD': 'S$', 'HKD': 'HK$', 'TWD': 'NT$',
      'THB': '฿', 'MYR': 'RM', 'IDR': 'Rp', 'PHP': '₱', 'VND': '₫',
      'BRL': 'R$', 'ARS': '$', 'CLP': '$', 'COP': '$', 'PEN': 'S/',
      'MXN': '$', 'AED': 'د.إ', 'SAR': '﷼', 'ILS': '₪', 'ZAR': 'R',
      'NZD': 'NZ$'
    };
    return symbols[currency] || currency + ' ';
  },

  // Convert USD amount to store currency
  convertToStoreCurrency: (usdAmount, storeCurrency) => {
    if (!storeCurrency || storeCurrency.currency === 'USD') {
      return usdAmount;
    }
    return usdAmount * (storeCurrency.exchange_rate || 1);
  },

  // Format currency amount with proper symbol and formatting
  formatCurrency: (usdAmount, storeCurrency, showTooltip = false) => {
    if (!storeCurrency) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(usdAmount || 0);
    }

    const convertedAmount = CurrencyUtils.convertToStoreCurrency(usdAmount, storeCurrency);
    const currency = storeCurrency.currency;
    
    // Special formatting for specific currencies
    const formatOptions = {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: currency === 'JPY' || currency === 'KRW' ? 0 : 2,
      maximumFractionDigits: currency === 'JPY' || currency === 'KRW' ? 0 : 2
    };

    const formattedAmount = new Intl.NumberFormat('en-US', formatOptions).format(convertedAmount);
    
    if (showTooltip && storeCurrency.currency !== 'USD') {
      return `${formattedAmount} (${currency})`;
    }
    
    return formattedAmount;
  },

  // Get currency info text for tooltips
  getCurrencyTooltip: (storeCurrency) => {
    if (!storeCurrency || storeCurrency.currency === 'USD') {
      return '';
    }
    return `Auto-converted from USD using current exchange rate (1 USD = ${storeCurrency.exchange_rate.toFixed(4)} ${storeCurrency.currency})`;
  },

  // Format currency based on shipping origin country
  formatCurrencyByCountry: (amount, countryCode) => {
    const currency = CurrencyUtils.getCountryCurrency(countryCode);
    
    // For dashboard display, we'll show the amount in the local currency format
    // Note: For now we assume 1:1 conversion for display purposes
    // In a real implementation, you'd want to fetch exchange rates
    const formatOptions = {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: currency === 'JPY' || currency === 'KRW' ? 0 : 2,
      maximumFractionDigits: currency === 'JPY' || currency === 'KRW' ? 0 : 2
    };

    return new Intl.NumberFormat('en-US', formatOptions).format(amount || 0);
  }
};

// Safe Component Wrapper for fallback support
const SafeComponent = ({ fallback: FallbackComponent, children, fallbackProps, ...props }) => {
  try {
    if (typeof children === 'function') {
      return children(props);
    }
    return children;
  } catch (error) {
    console.error('Enhanced component failed, using fallback:', error);
    return <FallbackComponent {...fallbackProps} />;
  }
};

// Enhanced Dashboard Analytics Component - Overhauled with period selector, trend chart, real country data
const DashboardAnalytics = ({ analyticsData = {}, analyticsDetail = null, analyticsPeriod = 7, analyticsStartDate = '', analyticsLoading = false, onPeriodChange, onStartDateChange, shippingOrigin = 'US' }) => {
  const [savingStartDate, setSavingStartDate] = React.useState(false);
  const [startDateSaved, setStartDateSaved] = React.useState(false);

  const handleStartDateChange = async (newDate) => {
    setSavingStartDate(true);
    setStartDateSaved(false);
    try {
      if (onStartDateChange) await onStartDateChange(newDate);
      setStartDateSaved(true);
      setTimeout(() => setStartDateSaved(false), 2500);
    } finally {
      setSavingStartDate(false);
    }
  };
  if (!FEATURE_FLAGS.enhanced_analytics) return null;

  // ── Determine data source ──────────────────────────────────────────────────
  // Prefer analyticsDetail (period endpoint) when available; fall back to today's data
  const hasPeriodData = analyticsDetail && analyticsDetail.period_totals;

  const periodLabels = { 1: 'Today', 7: 'Last 7 Days', 30: 'Last 30 Days', 90: 'Last 90 Days' };

  // ── KPI values ─────────────────────────────────────────────────────────────
  const kpiCalculations = hasPeriodData
    ? analyticsDetail.period_totals.calculations
    : (analyticsData.today_calculations || 0);

  // Cart value: use raw `amount` field from period totals — no hacks needed
  const kpiCartValue = hasPeriodData
    ? analyticsDetail.period_totals.cart_value
    : (analyticsData.add_to_cart_value_today || 0);

  const kpiImportCosts = hasPeriodData
    ? analyticsDetail.period_totals.import_costs
    : 0;

  const kpiAvgImportCost = hasPeriodData
    ? analyticsDetail.period_totals.avg_import_cost
    : (analyticsData.average_import_cost || 0);

  // ── Top countries: use real destination_country from period endpoint ───────
  // Both the period endpoint and the fallback can supply structured rows.
  // Never wrap an existing country object as the country value: React cannot
  // render an object as a child, and that crashed the entire dashboard.
  const topCountries = (hasPeriodData
    ? (analyticsDetail.top_countries || [])
    : (analyticsData.top_countries || [])
  ).map(entry => {
    const row = typeof entry === 'string' ? { country: entry } : (entry || {});
    return {
      country: typeof row.country === 'string' ? row.country : '',
      calculations: Number(row.calculations ?? 1) || 0,
      import_costs: Number(row.import_costs ?? 0) || 0
    };
  }).filter(row => /^[A-Z]{2}$/.test(row.country.toUpperCase()));

  // Historical events may not have stored a destination country. Currency is
  // useful context, but is deliberately displayed separately from geography.
  const topCurrencies = hasPeriodData
    ? (analyticsDetail.top_currencies || [])
    : Object.entries((analyticsData.recent_activity || []).reduce((acc, item) => {
        const currency = item.currency || 'USD';
        acc[currency] = (acc[currency] || 0) + 1;
        return acc;
      }, {})).map(([currency, calculations]) => ({ currency, calculations }));

  // ── Daily trend data ───────────────────────────────────────────────────────
  const dailyTrend = hasPeriodData ? analyticsDetail.daily_trend : [];

  // ── Simple inline bar chart (no external library needed) ──────────────────
  const maxCalcs = dailyTrend.length > 0 ? Math.max(...dailyTrend.map(d => d.calculations), 1) : 1;

  // Use the store's own currency returned by the analytics endpoint (e.g. GBP for UK stores)
  const storeCurrency = (hasPeriodData && analyticsDetail.store_currency)
    ? analyticsDetail.store_currency
    : (analyticsDetail && analyticsDetail.period_totals && analyticsDetail.period_totals.currency)
      ? analyticsDetail.period_totals.currency
      : 'USD';

  const formatCurrency = (amount) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: storeCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    } catch (e) {
      // Fallback if currency code is unrecognised
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    }
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return '';
    const d = new Date(isoDate + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const destinationDataQuality = hasPeriodData
    ? analyticsDetail.destination_data_quality
    : null;

  const isKnownCountry = (countryCode) => /^[A-Z]{2}$/.test(String(countryCode || '').toUpperCase());

  const getCountryName = (countryCode) => {
    if (!isKnownCountry(countryCode)) return 'Destination not captured';
    try {
      return new Intl.DisplayNames(['en'], { type: 'region' }).of(countryCode);
    } catch (e) {
      return countryCode;
    }
  };

  const formatActivityCurrency = (amount, currency) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || storeCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    } catch (e) {
      return formatCurrency(amount);
    }
  };

  const formatRelativeTime = (timestamp) => {
    const value = new Date(timestamp).getTime();
    if (!Number.isFinite(value)) return 'Recently';
    const minutes = Math.max(0, Math.floor((Date.now() - value) / 60000));
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  // Reference-data polls and incomplete events can have no cart or import
  // value. They do not help a merchant understand customer activity, so keep
  // the timeline focused on meaningful import estimates.
  const meaningfulRecentActivity = (analyticsData.recent_activity || []).filter((activity) => (
    Number(activity.cart_value || 0) > 0 || Number(activity.total_taxes || 0) > 0
  ));

  return (
    <div className="analytics-section" style={{ position: 'relative' }}>
      {/* Branded loading overlay */}
      {analyticsLoading && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 10,
          background: 'rgba(255,255,255,0.82)',
          backdropFilter: 'blur(2px)',
          borderRadius: '12px',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: '14px'
        }}>
          <div style={{
            width: '44px', height: '44px',
            border: '4px solid #e8eaf6',
            borderTop: '4px solid #5c6ac4',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 600, color: '#5c6ac4', fontSize: '15px', letterSpacing: '0.01em' }}>Loading analytics…</div>
            <div style={{ color: '#8c9196', fontSize: '12px', marginTop: '4px' }}>Fetching and converting data</div>
          </div>
        </div>
      )}
      {/* Header with period selector + hide-before-date filter */}
      <div className="analytics-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <h3 style={{ margin: 0 }}>📊 Analytics Dashboard</h3>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
          {/* Period selector */}
          <div className="period-selector" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {[1, 7, 30, 90].map(d => (
              <button
                key={d}
                disabled={analyticsLoading}
                onClick={() => onPeriodChange && onPeriodChange(d)}
                style={{
                  padding: '4px 12px',
                  borderRadius: '16px',
                  border: analyticsPeriod === d ? '2px solid #5c6ac4' : '1px solid #c4cdd5',
                  background: analyticsPeriod === d ? '#5c6ac4' : '#fff',
                  color: analyticsPeriod === d ? '#fff' : '#454f5b',
                  fontWeight: analyticsPeriod === d ? 600 : 400,
                  cursor: analyticsLoading ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  opacity: analyticsLoading && analyticsPeriod !== d ? 0.55 : 1,
                  display: 'flex', alignItems: 'center', gap: '5px'
                }}
              >
                {analyticsLoading && analyticsPeriod === d && (
                  <span style={{
                    display: 'inline-block', width: '10px', height: '10px',
                    border: '2px solid rgba(255,255,255,0.4)',
                    borderTop: '2px solid #fff',
                    borderRadius: '50%',
                    animation: 'spin 0.7s linear infinite',
                    flexShrink: 0
                  }} />
                )}
                {d === 1 ? 'Today' : `${d}d`}
              </button>
            ))}
          </div>
          {/* Hide-before-date filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#637381' }}>
            <span title="Hide test/trial data recorded before this date. Your data is never deleted.">🗓 Show data from:</span>
            <input
              type="date"
              disabled={analyticsLoading || savingStartDate}
              value={analyticsStartDate || ''}
              onChange={e => handleStartDateChange(e.target.value)}
              style={{
                padding: '3px 8px',
                borderRadius: '6px',
                border: '1px solid #c4cdd5',
                fontSize: '13px',
                color: '#212b36',
                background: analyticsStartDate ? '#f4f6f8' : '#fff',
                cursor: (analyticsLoading || savingStartDate) ? 'not-allowed' : 'pointer',
                opacity: (analyticsLoading || savingStartDate) ? 0.6 : 1
              }}
            />
            {analyticsStartDate && (
              <button
                disabled={analyticsLoading || savingStartDate}
                onClick={() => handleStartDateChange('')}
                title="Clear filter — show all data"
                style={{ background: 'none', border: 'none', cursor: (analyticsLoading || savingStartDate) ? 'not-allowed' : 'pointer', color: '#637381', fontSize: '16px', lineHeight: 1, padding: '0 2px', opacity: (analyticsLoading || savingStartDate) ? 0.5 : 1 }}
              >×</button>
            )}
            {savingStartDate && <span style={{ color: '#5c6ac4', fontSize: '12px' }}>Saving…</span>}
            {startDateSaved && <span style={{ color: '#108043', fontSize: '12px' }}>✓ Saved</span>}
            {analyticsStartDate && (
              <span style={{ color: '#bf0711', fontSize: '11px', background: '#fbeae5', padding: '2px 6px', borderRadius: '4px' }}>
                Hiding data before {analyticsStartDate}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="metrics-grid">
        {/* Calculations */}
        <div className="metric-card primary">
          <div className="metric-header">
            <span className="metric-icon">📊</span>
            <span className="metric-title">Calculations ({periodLabels[analyticsPeriod] || `${analyticsPeriod}d`})</span>
          </div>
          <div className="metric-value">{kpiCalculations.toLocaleString()}</div>
          <div className="metric-subtitle">Total import cost lookups</div>
        </div>

        {/* Cart Value — uses raw amount field, no /200 hack */}
        <div className="metric-card cart-value">
          <div className="metric-header">
            <span className="metric-icon">🛒</span>
            <span className="metric-title">Cart Value ({periodLabels[analyticsPeriod] || `${analyticsPeriod}d`})</span>
          </div>
          <div className="metric-value">{formatCurrency(kpiCartValue)}</div>
          <div className="metric-subtitle">Total cart value when calculator used ({storeCurrency})</div>
        </div>

        {/* Import Costs */}
        <div className="metric-card">
          <div className="metric-header">
            <span className="metric-icon">💰</span>
            <span className="metric-title">Import Costs ({periodLabels[analyticsPeriod] || `${analyticsPeriod}d`})</span>
          </div>
          <div className="metric-value">{formatCurrency(kpiImportCosts)}</div>
          <div className="metric-subtitle">Total taxes + duties shown to customers ({storeCurrency})</div>
        </div>

        {/* Average Import Cost */}
        <div className="metric-card average">
          <div className="metric-header">
            <span className="metric-icon">📦</span>
            <span className="metric-title">Avg Import Cost</span>
          </div>
          <div className="metric-value">{formatCurrency(kpiAvgImportCost)}</div>
          <div className="metric-subtitle">
            {kpiAvgImportCost === 0 && kpiCalculations > 0
              ? 'Mostly domestic orders'
              : 'Per calculation'}
          </div>
        </div>
      </div>

      {/* Daily Trend Chart View */}
      {dailyTrend.length > 0 && (
        <div className="trend-chart-section" style={{ marginTop: '20px' }}>
          <h4 style={{ marginBottom: '12px', color: '#454f5b' }}>Daily Calculations Trend</h4>
          <div className="trend-chart" style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '80px', padding: '0 4px' }}>
            {dailyTrend.map((day, idx) => {
              const barHeight = maxCalcs > 0 ? Math.max((day.calculations / maxCalcs) * 72, day.calculations > 0 ? 4 : 0) : 0;
              return (
                <div
                  key={idx}
                  title={`${formatDate(day.date)}: ${day.calculations} calculations`}
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: `${barHeight}px`,
                      background: day.calculations > 0 ? '#5c6ac4' : '#e9ecef',
                      borderRadius: '3px 3px 0 0',
                      minHeight: '2px',
                      transition: 'height 0.3s ease'
                    }}
                  />
                  {dailyTrend.length <= 14 && (
                    <span style={{ fontSize: '9px', color: '#8c9196', whiteSpace: 'nowrap', transform: 'rotate(-45deg)', transformOrigin: 'top left', marginTop: '4px', display: 'block', height: '20px' }}>
                      {formatDate(day.date)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          {dailyTrend.length > 14 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '11px', color: '#8c9196' }}>
              <span>{formatDate(dailyTrend[0]?.date)}</span>
              <span>{formatDate(dailyTrend[dailyTrend.length - 1]?.date)}</span>
            </div>
          )}
        </div>
      )}

      {/* Top destinations — rankings contain only captured ISO-2 destinations. */}
      {(topCountries.length > 0 || destinationDataQuality) && (
        <div className="metric-card countries destination-insight-card" style={{ marginTop: '16px' }}>
          <div className="metric-header">
            <span className="metric-icon">🌍</span>
            <span className="metric-title">Top Destination Countries ({periodLabels[analyticsPeriod] || `${analyticsPeriod}d`})</span>
            {destinationDataQuality && (
              <span className="analytics-quality-badge">
                {destinationDataQuality.coverage_percent}% destination coverage
              </span>
            )}
          </div>
          {topCountries.length > 0 ? (
            <div className="countries-list-real">
              <div className="countries-display">
                {topCountries.slice(0, 5).map((item, idx) => {
                  const countryCode = typeof item === 'string' ? item : item.country;
                  const calcs = typeof item === 'object' ? item.calculations : 1;
                  const maxCalcsCountry = topCountries[0]?.calculations || 1;
                  const barWidth = Math.max((calcs / maxCalcsCountry) * 100, 4);
                  return (
                    <div key={countryCode || idx} className="country-item country-insight-row">
                      <div className="country-label">
                        <span className="country-flag">{getCountryFlag(countryCode)}</span>
                        <span>
                          <strong>{getCountryName(countryCode)}</strong>
                          <small>{countryCode}</small>
                        </span>
                      </div>
                      <div className="country-progress-track">
                        <div className="country-progress-fill" style={{ width: `${barWidth}%` }} />
                      </div>
                      <span className="country-calculation-count">{calcs} {calcs === 1 ? 'calculation' : 'calculations'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="destination-empty-state historical-destination-summary">
              <strong>Destination data is unavailable for these historical calculations.</strong>
              <span>New calculations will populate real destination markets automatically. In the meantime, the confirmed currency activity below keeps this panel useful without treating a currency as a country.</span>
              {topCurrencies.length > 0 && (
                <div className="historical-currency-summary">
                  <span className="historical-summary-label">Customer currency activity</span>
                  <div className="historical-currency-chips">
                    {topCurrencies.slice(0, 4).map((item) => (
                      <span key={item.currency} className="historical-currency-chip">
                        <strong>{item.currency}</strong>
                        <small>{item.calculations} {item.calculations === 1 ? 'calculation' : 'calculations'}</small>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Recent Activity — show only calculations with merchant-meaningful values. */}
      <div className="metric-card recent-activity" style={{ marginTop: '16px' }}>
        <div className="metric-header">
          <span className="metric-icon">🕐</span>
          <span className="metric-title">Recent Import Estimates</span>
          <span className="analytics-panel-hint">Customer-facing calculations</span>
        </div>
        {meaningfulRecentActivity.length > 0 ? (
          <div className="activity-list activity-timeline">
            {meaningfulRecentActivity.slice(0, 4).map((activity, idx) => {
              const hasDestination = isKnownCountry(activity.customer_country);
              return (
                <div key={activity.id || activity.calc_id || idx} className="activity-item activity-timeline-item">
                  <div className="activity-destination">
                    <span className="activity-country-flag">{hasDestination ? getCountryFlag(activity.customer_country) : activity.currency}</span>
                    <div>
                      <strong>{hasDestination ? getCountryName(activity.customer_country) : 'Import estimate calculated'}</strong>
                      <small>{hasDestination ? `Importing to ${activity.customer_country}` : `${activity.currency || 'Store'} calculation — historical destination not available`}</small>
                    </div>
                  </div>
                  <div className="activity-values">
                    <div>
                      <span>Cart value</span>
                      <strong>{formatActivityCurrency(activity.cart_value, activity.currency)}</strong>
                    </div>
                    <div>
                      <span>Import estimate</span>
                      <strong>{formatActivityCurrency(activity.total_taxes, activity.currency)}</strong>
                    </div>
                  </div>
                  <time className="activity-time" dateTime={activity.timestamp} title={new Date(activity.timestamp).toLocaleString()}>
                    {formatRelativeTime(activity.timestamp)}
                  </time>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="activity-empty-state">
            <strong>No customer import estimates are available in this period.</strong>
            <span>Background or incomplete calculation events are excluded so this timeline remains useful.</span>
          </div>
        )}
      </div>
    </div>
  );
};



// ValueMetrics component removed - inaccurate data for Shopify compliance

// StoreHealthPanel component removed - functionality moved to metric cards

// Enhanced Dashboard Component with fallback support
const StorefrontActivationCard = ({ onOpenSetup }) => {
  const [activationState, setActivationState] = useState({ state: 'loading', product: null, appEmbed: null });

  useEffect(() => {
    let mounted = true;

    const loadActivations = async () => {
      try {
        if (!window.shopify?.app?.extensions) {
          if (mounted) setActivationState({ state: 'unavailable', product: null, appEmbed: null });
          return;
        }

        const extensions = await window.shopify.app.extensions();
        const themeActivations = extensions
          .filter((extension) => extension.type === 'theme_app_extension')
          .flatMap((extension) => extension.activations || []);

        const findStatus = (handle) => themeActivations.find((activation) => activation.handle === handle)?.status || 'available';
        if (mounted) {
          setActivationState({
            state: 'ready',
            product: findStatus('product-page-widget'),
            appEmbed: findStatus('app-embed'),
          });
        }
      } catch (error) {
        console.warn('Unable to read Shopify extension activations:', error);
        if (mounted) setActivationState({ state: 'unavailable', product: null, appEmbed: null });
      }
    };

    loadActivations();
    return () => { mounted = false; };
  }, []);

  const labelFor = (status) => status === 'active' ? 'Active on published theme' : status === 'unavailable' ? 'Unavailable' : 'Not active';

  return (
    <section className="storefront-activation-card" aria-labelledby="storefront-activation-heading">
      <div className="storefront-activation-card__header">
        <div>
          <p className="storefront-activation-card__eyebrow">STORE FRONT STATUS</p>
          <h3 id="storefront-activation-heading">Check where shoppers can see estimates</h3>
          <p>Shopify reports this status from your published theme. Test changes in a duplicate theme before making them live.</p>
        </div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onOpenSetup}>Open Setup</button>
      </div>

      {activationState.state === 'loading' && <p className="storefront-activation-card__loading">Checking your published theme…</p>}
      {activationState.state === 'unavailable' && <p className="storefront-activation-card__loading">Open Tax &amp; Duty King in Shopify Admin to confirm theme activation status.</p>}
      {activationState.state === 'ready' && (
        <div className="storefront-activation-card__items">
          <div className={`storefront-activation-item storefront-activation-item--${activationState.product}`}>
            <span className="storefront-activation-item__marker" aria-hidden="true">{activationState.product === 'active' ? '✓' : '○'}</span>
            <div><strong>Product-page estimate</strong><span>{labelFor(activationState.product)}</span></div>
          </div>
          <div className={`storefront-activation-item storefront-activation-item--${activationState.appEmbed}`}>
            <span className="storefront-activation-item__marker" aria-hidden="true">{activationState.appEmbed === 'active' ? '✓' : '○'}</span>
            <div><strong>Cart &amp; cart-drawer App Embed</strong><span>{labelFor(activationState.appEmbed)}</span></div>
          </div>
        </div>
      )}
    </section>
  );
};

// Shopify-native dashboard composition: usage is paired with the published-theme activation evidence merchants need.
const EnhancedDashboard = ({ stores = [], vatRates = [], dutyRates = [], categories = [], analyticsData = {}, analyticsDetail = null, analyticsPeriod = 7, analyticsStartDate = '', analyticsLoading = false, onAnalyticsPeriodChange, onStartDateChange, onInitializeData, shippingOrigins = {}, onOpenSetup }) => {
  return (
    <div className="enhanced-dashboard">
      {/* Keep existing dashboard as fallback */}
      <SafeComponent
        fallback={Dashboard}
        fallbackProps={{ stores, vatRates, dutyRates, categories, onInitializeData }}
      >
        {() => (
          <div className="dashboard">
            <div className="dashboard-header">
              <h2>📊 Dashboard Overview</h2>
              <button className="btn btn-primary" onClick={onInitializeData}>
                🚀 Initialize Enhanced Data
              </button>
            </div>
            
            {/* 1. Analytics Section - Key metrics like revenue, average import cost, etc. */}
            <DashboardAnalytics 
              analyticsData={analyticsData}
              analyticsDetail={analyticsDetail}
              analyticsPeriod={analyticsPeriod}
              analyticsStartDate={analyticsStartDate}
              analyticsLoading={analyticsLoading}
              onPeriodChange={onAnalyticsPeriodChange}
              onStartDateChange={onStartDateChange}
              shippingOrigin={(() => {
                const currentStore = stores.find(store => store.shop_domain === analyticsData.shop_domain) || stores[0];
                return currentStore ? (shippingOrigins[currentStore.shop_domain] || '') : '';
              })()} 
            />

            <StorefrontActivationCard onOpenSetup={onOpenSetup} />
            
            {/* 2. Monthly Usage Card - Track calculator usage and limits */}
            <MonthlyUsageCard stores={stores} analyticsData={analyticsData} />
            
            {/* ValueMetrics section removed for Shopify compliance */}
            


            {/* API Status section removed */}

            {/* 7. API Status / System Health - Connection status, error logs, etc. */}
            <div className="api-status">
              <h3>🔌 API Integration Status</h3>
              <div className="api-status-grid">
                <div className="api-status-item active">
                  <span className="api-name">Government APIs</span>
                  <span className="api-status-badge">✅ Active</span>
                </div>
                <div className="api-status-item active">
                  <span className="api-name">Exchange Rate API</span>
                  <span className="api-status-badge">✅ Active</span>
                </div>
                <div className="api-status-item pending">
                  <span className="api-name">Premium APIs</span>
                  <span className="api-status-badge">⏳ Tier-based</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </SafeComponent>
    </div>
  );
};

// Dashboard Component (Original - kept as fallback)
const Dashboard = ({ stores, vatRates, dutyRates, categories, onInitializeData }) => {
  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>📊 Dashboard Overview</h2>
        <button className="btn btn-primary" onClick={onInitializeData}>
          🚀 Initialize Enhanced Data
        </button>
      </div>
      
      {/* Simplified fallback dashboard - dashboard-sections removed */}

      <div className="api-status">
        <h3>🔌 API Integration Status</h3>
        <div className="api-status-grid">
          <div className="api-status-item active">
            <span className="api-name">Government APIs</span>
            <span className="api-status-badge">✅ Active</span>
          </div>
          <div className="api-status-item active">
            <span className="api-name">Exchange Rate API</span>
            <span className="api-status-badge">✅ Active</span>
          </div>
          <div className="api-status-item pending">
            <span className="api-name">Premium APIs</span>
            <span className="api-status-badge">⏳ Tier-based</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Pricing Tiers Component
const PricingTiers = ({ pricingTiers }) => {
  return (
    <div className="pricing-tiers">
      <div className="pricing-header">
        <h2>💰 Subscription Pricing</h2>
        <p>Choose the plan that fits your store's needs</p>
      </div>
      
      <div className="pricing-grid">
        {pricingTiers.map((tier, index) => (
          <div key={index} className={`pricing-card ${tier.is_popular ? 'popular' : ''}`}>
            {tier.is_popular && <div className="popular-badge">Most Popular</div>}
            
            <div className="pricing-header">
              <h3>{tier.name}</h3>
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">{tier.price}</span>
                <span className="period">/month</span>
              </div>
            </div>
            
            <div className="pricing-features">
              <div className="calculation-limit">
                <strong>{tier.calculations_limit === 999999 ? 'Unlimited' : tier.calculations_limit.toLocaleString()}</strong> calculations/month
              </div>
              <ul>
                {tier.features.map((feature, i) => (
                  <li key={i}>✅ {feature}</li>
                ))}
              </ul>
            </div>
            
            <div className="pricing-footer">
              <button className={`btn ${tier.is_popular ? 'btn-primary' : 'btn-secondary'}`}>
                {index === 0 ? 'Start 14-Day Trial' : 'Upgrade Plan'}
              </button>
              <div className="profit-margin">80% profit margin</div>
            </div>
          </div>
        ))}
      </div>

      <div className="pricing-features-comparison">
        <h3>🔍 Feature Comparison</h3>
        <div className="feature-grid">
          <div className="feature-item">
            <h4>Government API Integration</h4>
            <p>Real-time data from USITC, UK Gov, Canada Revenue Agency</p>
          </div>
          <div className="feature-item">
            <h4>HS Code Classification</h4>
            <p>Accurate product categorization for precise duty calculations</p>
          </div>
          <div className="feature-item">
            <h4>Processing Fees</h4>
            <p>Includes customs handling and processing fees by country</p>
          </div>
          <div className="feature-item">
            <h4>Real-time Currency</h4>
            <p>Live exchange rates for accurate international pricing</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Calculator Component
const Calculator = ({ vatRates, dutyRates, categories, stores, billingData, shippingOrigins = {} }) => {
  const InfoDot = ({ text }) => (
    <span
      title={text}
      aria-label={text}
      tabIndex={0}
      style={{
        display:'inline-flex', alignItems:'center', justifyContent:'center',
        width:'14px', height:'14px', marginLeft:'4px', border:'1px solid #c7cdd3',
        borderRadius:'50%', color:'#637381', fontSize:'9px', fontWeight:700,
        lineHeight:1, cursor:'help', verticalAlign:'1px'
      }}
    >i</span>
  );
  // ── Store / billing context ───────────────────────────────────────────────
  const storeShopDomain = stores && stores.length > 0 ? stores[0]?.shop_domain : '';
  const storeOriginCountry = (
    (storeShopDomain && shippingOrigins[storeShopDomain]) ||
    (stores && stores.length > 0 ? stores[0]?.shipping_origin_country : null) ||
    ''
  );
  const storeBilling = billingData && storeShopDomain ? billingData[storeShopDomain] : null;
  const effectiveStatus = storeBilling?.effective_status || 'inactive';
  const isAccessAllowed = storeBilling?.gating?.accessAllowed !== false;

  // ── Currency helpers ──────────────────────────────────────────────────────
  const getCurrencyByCountry = (cc) => {
    const m = { US:'USD',GB:'GBP',DE:'EUR',FR:'EUR',IT:'EUR',ES:'EUR',NL:'EUR',BE:'EUR',AT:'EUR',IE:'EUR',PT:'EUR',FI:'EUR',GR:'EUR',CA:'CAD',AU:'AUD',NZ:'NZD',JP:'JPY',KR:'KRW',CH:'CHF',SE:'SEK',DK:'DKK',NO:'NOK',PL:'PLN',CZ:'CZK',HU:'HUF',RO:'RON',BG:'BGN',HR:'HRK',IN:'INR',BR:'BRL',MX:'MXN',ZA:'ZAR',SG:'SGD',HK:'HKD',MY:'MYR',TH:'THB',ID:'IDR',PH:'PHP',VN:'VND',AE:'AED',SA:'SAR',TR:'TRY',IL:'ILS',EG:'EGP',AR:'ARS',CL:'CLP',CO:'COP',PE:'PEN' };
    return m[cc] || 'USD';
  };
  const getCurrencySymbol = (currency) => {
    const s = { USD:'$',GBP:'£',EUR:'€',CAD:'CA$',AUD:'A$',NZD:'NZ$',JPY:'¥',CHF:'CHF ',SEK:'kr',DKK:'kr',NOK:'kr',PLN:'zł',CZK:'Kč',HUF:'Ft',RON:'lei',BGN:'лв',HRK:'kn',INR:'₹',BRL:'R$',MXN:'MX$',ZAR:'R',SGD:'S$',HKD:'HK$',MYR:'RM',THB:'฿',IDR:'Rp',PHP:'₱',VND:'₫',AED:'د.إ',SAR:'﷼',TRY:'₺',ILS:'₪',EGP:'E£',ARS:'$',CLP:'$',COP:'$',PEN:'S/' };
    return s[currency] || currency + ' ';
  };
  const fmtAmt = (amount, currency) => {
    const code = String(currency || 'USD').toUpperCase();
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: code,
        minimumFractionDigits: (code === 'JPY' || code === 'KRW') ? 0 : 2,
        maximumFractionDigits: (code === 'JPY' || code === 'KRW') ? 0 : 2
      }).format(Number(amount || 0));
    } catch (e) {
      const sym = getCurrencySymbol(code);
      const value = (code === 'JPY' || code === 'KRW')
        ? Math.round(Number(amount || 0)).toLocaleString('en-US')
        : Number(amount || 0).toFixed(2);
      return sym + value;
    }
  };
  const defaultCurrency = getCurrencyByCountry(storeOriginCountry);

  // ── State ─────────────────────────────────────────────────────────────────
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [productSearching, setProductSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);   // {id, title, vendor}
  const [variants, setVariants] = useState([]);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [variantsError, setVariantsError] = useState('');
  const [manualPrice, setManualPrice] = useState('');
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [hsCode, setHsCode] = useState('');
  const [hsDescription, setHsDescription] = useState('');
  const [hsSource, setHsSource] = useState('');          // 'tdk' | 'manual' | ''
  const [resolvedOrigin, setResolvedOrigin] = useState(storeOriginCountry);
  const [vendorOriginMap, setVendorOriginMap] = useState({});
  const [customerCountry, setCustomerCountry] = useState('');
  const [usState, setUsState] = useState('');
  const [usPostalCode, setUsPostalCode] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = React.useRef(null);
  const dropdownRef = React.useRef(null);

  // ── Load multi-origin config once ─────────────────────────────────────────
  useEffect(() => {
    if (!storeShopDomain) return;
    const apiKey = process.env.REACT_APP_API_KEY || '';
    fetch(`${BACKEND_URL}/api/multi-origin/config?shop=${encodeURIComponent(storeShopDomain)}`, {
      headers: { 'x-api-key': apiKey, 'Content-Type': 'application/json' }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.multi_origin_config?.vendor_origin_map) {
          setVendorOriginMap(data.multi_origin_config.vendor_origin_map);
        }
      })
      .catch(() => {});
  }, [storeShopDomain]);

  // ── Update resolvedOrigin when store changes ───────────────────────────────
  useEffect(() => {
    if (!selectedProduct) {
      setResolvedOrigin(storeOriginCountry);
    }
  }, [storeOriginCountry, selectedProduct]);

  // ── Close dropdown on outside click ───────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Debounced product search ───────────────────────────────────────────────
  useEffect(() => {
    if (!productSearch.trim() || productSearch.length < 2) {
      setProductResults([]);
      setShowDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      if (!storeShopDomain) return;
      setProductSearching(true);
      try {
        const apiKey = process.env.REACT_APP_API_KEY || '';
        const params = new URLSearchParams({ shop_domain: storeShopDomain, search: productSearch, limit: 10, page: 1 });
        const res = await fetch(`${BACKEND_URL}/api/hs-products?${params}`, {
          headers: { 'x-api-key': apiKey, 'Content-Type': 'application/json' }
        });
        if (res.ok) {
          const data = await res.json();
          setProductResults(data.products || []);
          setShowDropdown(true);
        }
      } catch (e) { /* ignore */ }
      finally { setProductSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch, storeShopDomain]);

  // ── Select a product ──────────────────────────────────────────────────────
  const handleSelectProduct = async (product) => {
    setSelectedProduct(product);
    setProductSearch(product.title);
    setShowDropdown(false);
    setVariants([]);
    setSelectedVariantId('');
    setHsCode('');
    setHsDescription('');
    setHsSource('');
    setResult(null);
    setError('');

    // Resolve vendor origin
    const vendorKey = Object.keys(vendorOriginMap).find(
      k => k.toLowerCase() === (product.vendor || '').toLowerCase()
    );
    setResolvedOrigin(vendorKey ? vendorOriginMap[vendorKey] : storeOriginCountry);

    const apiKey = process.env.REACT_APP_API_KEY || '';
    const headers = { 'x-api-key': apiKey, 'Content-Type': 'application/json' };

    // Fetch HS code from TDK classifications
    try {
      const hsRes = await fetch(
        `${BACKEND_URL}/api/hs-classifications/${product.id}?shop_domain=${encodeURIComponent(storeShopDomain)}`,
        { headers }
      );
      if (hsRes.ok) {
        const hsData = await hsRes.json();
        if (hsData.hs_code) {
          setHsCode(hsData.hs_code);
          setHsDescription(hsData.hs_description || '');
          setHsSource('tdk');
        }
      }
    } catch (e) { /* ignore */ }

    // Fetch variants from Shopify via backend
    setVariantsLoading(true);
    setVariantsError('');
    setManualPrice('');
    try {
      const vRes = await fetch(
        `${BACKEND_URL}/api/products/${product.id}/variants?shop=${encodeURIComponent(storeShopDomain)}`,
        { headers }
      );
      if (vRes.ok) {
        const vData = await vRes.json();
        const vList = vData.variants || [];
        setVariants(vList);
        if (vList.length === 1) setSelectedVariantId(vList[0].id);
      } else {
        let errDetail = `HTTP ${vRes.status}`;
        try { const j = await vRes.json(); errDetail = j.detail || errDetail; } catch(_){}
        setVariantsError(errDetail);
      }
    } catch (e) {
      setVariantsError(e.message || 'Network error');
    } finally { setVariantsLoading(false); }
  };

  // ── Clear product ─────────────────────────────────────────────────────────
  const handleClearProduct = () => {
    setSelectedProduct(null);
    setProductSearch('');
    setVariants([]);
    setSelectedVariantId('');
    setVariantsError('');
    setManualPrice('');
    setHsCode('');
    setHsDescription('');
    setHsSource('');
    setResolvedOrigin(storeOriginCountry);
    setResult(null);
    setError('');
  };

  // ── Derived values ────────────────────────────────────────────────────────
  const selectedVariant = variants.find(v => v.id === selectedVariantId) || null;
  const productPrice = selectedVariant ? parseFloat(selectedVariant.price) : (manualPrice ? parseFloat(manualPrice) : 0);
  const currency = getCurrencyByCountry(resolvedOrigin);

  // Derive product_category from hs_code (mirrors backend _hs_to_category)
  const hsToCategory = (code) => {
    if (!code) return 'general';
    const heading = code.replace('.', '').substring(0, 4);
    const n = parseInt(heading, 10);
    if (n >= 6101 && n <= 6217) return 'clothing';
    if (n >= 6401 && n <= 6405) return 'footwear';
    if (n >= 8471 && n <= 8473) return 'electronics';
    if (n >= 9401 && n <= 9403) return 'furniture';
    if (n >= 6301 && n <= 6310) return 'textiles';
    if (n >= 8711 && n <= 8714) return 'bicycles';
    if (n >= 9501 && n <= 9508) return 'toys';
    if (n >= 3301 && n <= 3307) return 'cosmetics';
    if (n >= 101 && n <= 511) return 'animals';
    if (n >= 601 && n <= 714) return 'plants';
    if (n >= 1001 && n <= 2403) return 'food';
    return 'general';
  };

  // ── Calculate ─────────────────────────────────────────────────────────────
  const handleCalculate = async () => {
    if (!selectedProduct) { setError('Please search and select a product'); return; }
    if (!selectedVariantId && variants.length > 1) { setError('Please select a variant'); return; }
    if (!customerCountry) { setError('Please select a customer country'); return; }
    if (!productPrice || productPrice <= 0) {
      if (variantsError) { setError('Please enter a price manually — variants could not be loaded'); }
      else { setError('Selected variant has no price'); }
      return;
    }

    if (!isAccessAllowed) {
      const msg = storeBilling?.gating?.message || 'Calculator access is restricted. Please upgrade your plan.';
      if (effectiveStatus === 'trial_expired') { setError('🚫 Trial expired. ' + msg); }
      else { setError('🚫 ' + msg); }
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);
    try {
      const payload = {
        product_price: productPrice,
        product_category: hsToCategory(hsCode),
        customer_country: customerCountry,
        currency,
        quantity,
        shop_domain: storeShopDomain,
        shipping_origin: resolvedOrigin,
        hs_code: hsCode || undefined,
        vendor: selectedProduct.vendor || undefined,
        product_id: selectedProduct.id,
        track_analytics: false,
        calculate_ddp_costs: true,
      };
      const res = await api.post('/calculate', payload);
      const calculation = res.data;

      if (customerCountry === 'US') {
        try {
          const taxRes = await api.post('/us-sales-tax/estimate', {
            shop_domain: storeShopDomain,
            ...(usState ? { state: usState } : {}),
            ...(usPostalCode ? { postal_code: usPostalCode } : {}),
            currency: calculation.currency || currency,
            items: [{
              product_price: productPrice,
              product_category: hsToCategory(hsCode),
              quantity,
              vendor: selectedProduct.vendor || undefined,
              product_id: selectedProduct.id,
              variant_id: selectedVariantId || undefined,
              hs_code: hsCode || undefined,
              origin_country: resolvedOrigin || undefined,
              requires_shipping: true,
            }],
          });
          calculation.us_sales_tax = taxRes.data;
        } catch (_) {
          calculation.us_sales_tax = {
            status: 'unavailable',
            message: 'Sales tax will be confirmed at checkout.',
          };
        }
      }

      setResult(calculation);
    } catch (err) {
      setError('Calculation failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  // ── Sorted country list ───────────────────────────────────────────────────
  const sortedCountries = [...vatRates].sort((a, b) => a.country_name.localeCompare(b.country_name));

  // ── Result rendering helpers ──────────────────────────────────────────────
  const isDDPActive = (d) =>
    d?.country_ddp_applies || d?.all_items_ddp || d?.ddp_enabled || d?.ddp;
  const isIOSS = result?.ioss?.applicable && !result?.ioss?.mixed;
  const isMixedIOSS = result?.ioss?.applicable && result?.ioss?.mixed;
  const isDeMinimis = result?.de_minimis_applied || (result && result.total_taxes === 0 && !result.is_domestic && !isIOSS && !isDDPActive(result));

  const renderResultBadge = () => {
    if (!result) return null;
    const mode = result.union?.mode;
    const badges = { IOSS: { label: '🇪🇺 IOSS Mode', color: '#2e7d32', bg: '#e8f5e9' }, IMPORT: { label: '📦 Import Mode', color: '#1565c0', bg: '#e3f2fd' }, DOMESTIC: { label: '🏠 Domestic', color: '#6d4c41', bg: '#efebe9' } };
    const b = badges[mode];
    if (!b) return null;
    return <span style={{ display:'inline-block', padding:'3px 10px', borderRadius:'12px', fontSize:'12px', fontWeight:600, color: b.color, background: b.bg, marginBottom:'10px' }}>{b.label}</span>;
  };

  const renderMessages = () => {
    if (!result?.messages?.length) return null;
    return result.messages.map((msg, i) => (
      <div key={i} style={{ padding:'8px 12px', background:'#e3f2fd', border:'1px solid #2196f3', borderRadius:'6px', color:'#1565c0', fontSize:'13px', marginBottom:'8px' }}>
        ℹ️ {msg}
      </div>
    ));
  };

  const renderResult = () => {
    if (!result) return null;
    const r = result;
    const cur = r.currency || currency;

    // Domestic
    if (r.is_domestic) {
      return (
        <div>
          {renderResultBadge()}
          <div style={{ padding:'12px', background:'#e8f5e9', border:'1px solid #4caf50', borderRadius:'6px', textAlign:'center', color:'#2e7d32', marginBottom:'10px' }}>
            <strong>✅ {r.breakdown?.message || 'No import charges for domestic orders'}</strong>
          </div>
          {renderMessages()}
          <div className="result-item total"><label>Product Total:</label><span>{fmtAmt(r.subtotal, cur)}</span></div>
        </div>
      );
    }

    // IOSS without DDP keeps the compact success state, but still shows
    // the temporary EU customs duty when one is calculated.
    if (isIOSS && !isDDPActive(r)) {
      const msg = (r.messages && r.messages[0]) ||
        'VAT collected at checkout under IOSS for eligible consignments up to €150. Any applicable EU temporary customs duty is shown separately.';
      return (
        <div>
          {renderResultBadge()}
          <div style={{ padding:'12px', background:'#e8f5e9', border:'1px solid #4caf50', borderRadius:'6px', textAlign:'center', color:'#2e7d32', marginBottom:'10px' }}>
            <strong>✅ {msg}</strong>
          </div>
          {renderBreakdown(r, cur, { suppressArrivalTip: true })}
        </div>
      );
    }

    // DDP shows the same landed-cost calculation as DDU, then confirms that
    // the merchant has incorporated those costs into the selling price.
    if (isDDPActive(r)) {
      const msg = (r.messages || []).find(m =>
        /DDP|Tax\s*&\s*Duties Included|included in the price/i.test(m)
      ) || (customerCountry === 'US'
        ? 'Estimated taxes, customs duty and federal customs-processing charges shown above are included in the price. Final checkout tax and carrier/broker charges may vary.'
        : 'Tax & Duties Included in the price shown above. No additional import charges should be due on delivery.');
      const iossMsg = isIOSS
        ? (r.messages || []).find(m => /IOSS|VAT collected at checkout/i.test(m))
        : null;
      return (
        <div>
          {renderResultBadge()}
          {iossMsg && (
            <div style={{ padding:'5px 8px', background:'#f8faf9', border:'1px solid #d9e2dc', borderRadius:'6px', color:'#4b5563', fontSize:'11px', marginBottom:'6px', display:'flex', alignItems:'center', gap:'4px' }}>
              <span>✓ IOSS treatment applies</span><InfoDot text={iossMsg} />
            </div>
          )}
          {renderBreakdown(r, cur, { suppressArrivalTip: true })}
          <div style={{ marginTop:'8px', padding:'6px 8px', background:'#f8faf9', border:'1px solid #d9e2dc', borderRadius:'6px', color:'#374151', fontSize:'11px', display:'flex', alignItems:'center', gap:'4px' }}>
            <strong style={{fontWeight:600}}>✓ Taxes & duties included</strong>
            <InfoDot text={msg} />
          </div>
        </div>
      );
    }

    // New Zealand low-value import: no border GST/duty, but the 2026
    // Customs + MPI goods levy still applies. Do not render this as generic
    // "duty-free / zero import costs" because that would hide the levy and
    // incorrectly repeat the legacy NZD 400 threshold.
    const isNzLowValue = customerCountry === 'NZ' && r.breakdown?.nz_low_value_import;
    if (isNzLowValue) {
      const levy = Number(r.processing_fees || 0);
      return (
        <div>
          {renderResultBadge()}
          {renderMessages()}
          <div className="result-item"><label>Subtotal:</label><span>{fmtAmt(r.subtotal, cur)}</span></div>
          <div className="result-item">
            <label>Customs + MPI goods levy:</label>
            <span>{fmtAmt(levy, cur)}</span>
          </div>
          <div className="result-item total">
            <label>Additional Import Costs:</label>
            <span>{fmtAmt(levy, cur)}</span>
          </div>
          <div style={{ marginTop:'7px', fontSize:'10.5px', color:'#637381', display:'flex', alignItems:'center', gap:'2px' }}>
            Estimate <InfoDot text="For New Zealand consignments valued at NZ$1,000 or less, Customs generally does not collect border GST or duty (excluding alcohol/tobacco). An overseas supplier may collect 15% GST at point of sale. The levy shown uses the 2026 air/mail Customs + MPI estimate." />
          </div>
        </div>
      );
    }

    // De Minimis
    if (isDeMinimis) {
      const threshold = r.de_minimis_threshold;
      const deMinCur = r.de_minimis_currency || cur;
      const thresholdText = threshold && threshold > 0 ? ` Orders under ${deMinCur} ${Number(threshold).toFixed(0)} are imported without duty or VAT.` : '';
      return (
        <div>
          {renderResultBadge()}
          <div style={{ padding:'12px', background:'#f0fdf4', border:'1px solid #22c55e', borderRadius:'6px', textAlign:'center', color:'#15803d', marginBottom:'10px' }}>
            <strong>🎉 Duty-free!</strong> This order to {r.country_name} is below the de minimis threshold.{thresholdText}
          </div>
          {renderMessages()}
          <div className="result-item"><label>Subtotal:</label><span>{fmtAmt(r.subtotal, cur)}</span></div>
          <div className="result-item total"><label>Additional Import Costs:</label><span>{fmtAmt(0, cur)}</span></div>
        </div>
      );
    }

    // UK border VAT / TCA messages
    const hasUKMsg = r.messages && r.messages.some(m => m.includes('UK import VAT') || m.includes('Trade Agreement') || m.includes('£135'));
    if (hasUKMsg) {
      return (
        <div>
          {renderResultBadge()}
          <div style={{ padding:'12px', background:'#e3f2fd', border:'1px solid #2196f3', borderRadius:'6px', textAlign:'center', color:'#1565c0', marginBottom:'10px' }}>
            <strong>🇬🇧 {r.messages.join(' ')}</strong>
          </div>
          {r.import_vat_mode === 'border' && renderBreakdown(r, cur)}
        </div>
      );
    }

    // Normal import
    return (
      <div>
        {renderResultBadge()}
        {renderMessages()}
        {renderBreakdown(r, cur)}
      </div>
    );
  };

  const renderBreakdown = (r, cur, options = {}) => {
    const duty = r.duty_amount || 0;
    const vat = r.vat_amount || 0;
    const fees = r.processing_fees || 0;
    const euFee = r.eu_processing_fee || 0;
    const usTariff = r.us_additional_tariff_amount || r.us_tariff_amount || 0;
    const isUs = customerCountry === 'US';
    const usSalesTax = isUs ? r.us_sales_tax : null;
    const usSalesTaxAmount = ['calculated', 'estimated'].includes(usSalesTax?.status)
      ? Number(usSalesTax?.sales_tax_amount || 0)
      : 0;
    const usSalesTaxRate = Number(usSalesTax?.effective_rate || 0);
    const usTaxLabel = usSalesTax?.calculation_source === 'national_average'
      ? 'Estimated U.S. Sales Tax (U.S. average)'
      : usSalesTax?.calculation_source === 'state_average'
        ? 'Estimated U.S. Sales Tax (State average)'
        : 'Estimated U.S. Sales Tax';
    const total = (r.total_taxes || 0) + fees + euFee + usTariff + (isUs ? usSalesTaxAmount : 0);
    const taxName = isUs
      ? 'Estimated U.S. Sales Tax'
      : (customerCountry === 'NZ'
          ? 'GST'
          : (customerCountry === 'CH'
              ? 'Import VAT'
              : (r.breakdown?.tax_name || r.breakdown?.local_tax_name || 'VAT')));
    const usTaxDetail = usSalesTax?.calculation_source === 'national_average'
      ? 'Uses the U.S. nationwide average until a State is selected. Checkout tax is final.'
      : usSalesTax?.calculation_source === 'state_average'
        ? 'Uses the State rate plus an average local rate. ZIP can refine the local estimate.'
        : (usSalesTax?.message || 'Estimated using the merchant’s Shopify tax settings. Final checkout tax may vary.');
    const usTariffBreakdown = r.us_tariff_breakdown || {};
    const tariffParts = [];
    if (usTariffBreakdown.section_301 > 0) tariffParts.push('S.301');
    if (usTariffBreakdown.section_232 > 0) tariffParts.push('S.232');
    if (usTariffBreakdown.global_tariff > 0) tariffParts.push('Global');

    return (
      <div>
        <div className="result-item">
          <label>{r.breakdown?.duty_estimated ? 'Estimated Customs Duty' : 'Customs Duty'} ({r.duty_rate || r.breakdown?.duty_rate || 0}%):</label>
          <span>{fmtAmt(duty, cur)}</span>
        </div>
        <div className="result-item">
          <label>{taxName}{isUs && usSalesTaxRate > 0 ? ` (${usSalesTaxRate.toFixed(usSalesTaxRate % 1 ? 3 : 0)}%)` : (!isUs ? ` (${r.vat_rate || 0}%)` : '')}:{isUs && <InfoDot text={usTaxDetail} />}</label>
          <span>{fmtAmt(isUs ? usSalesTaxAmount : vat, cur)}</span>
        </div>
        <div className="result-item"><label>{isUs ? 'U.S. Customs Processing' : 'Processing'}:{isUs && <InfoDot text="Estimated federal CBP customs-processing fee. Private carrier or broker fees can differ." />}</label><span>{fmtAmt(fees, cur)}</span></div>
        {euFee > 0 && <div className="result-item"><label>🇪🇺 EU Temporary Customs Duty:</label><span>{fmtAmt(euFee, cur)}</span></div>}
        {usTariff > 0 && (
          <div className="result-item">
            <label>🇺🇸 US Additional Tariff{tariffParts.length > 0 ? ` (${tariffParts.join('+')})` : ''}:</label>
            <span>{fmtAmt(usTariff, cur)}</span>
          </div>
        )}
        <div className="result-item total"><label>{isUs ? (isDDPActive(r) ? 'Included Taxes & Duties' : 'Estimated Taxes & Duties') : 'Approximate Fees'}:</label><span>{fmtAmt(total, cur)}</span></div>
        {r.tca_applied && (
          <div style={{ marginTop:'8px', padding:'8px 12px', background:'#e3f2fd', border:'1px solid #2196f3', borderRadius:'6px', fontSize:'12px', color:'#1565c0' }}>
            ✅ Preferential duty rate applied under Trade & Cooperation Agreement (TCA)
          </div>
        )}
        {r.ddp_items_excluded && r.ddp_items_count > 0 && (
          <div style={{ marginTop:'8px', padding:'8px', background:'#fff9e6', border:'1px solid #ffc107', borderRadius:'6px', fontSize:'12px', color:'#856404', textAlign:'center' }}>
            📦 {r.ddp_items_count === 1 ? '1 item already includes' : `${r.ddp_items_count} items already include`} tax & duty
          </div>
        )}
        {isMixedIOSS && r.messages && r.messages.length > 0 && (
          <div style={{ marginTop:'8px', padding:'8px', background:'#e8f5e9', border:'1px solid #4caf50', borderRadius:'6px', fontSize:'12px', color:'#2e7d32', textAlign:'center' }}>
            ✅ {r.messages[0]}
          </div>
        )}
        {!options.suppressArrivalTip && (
          <div style={{ marginTop:'7px', fontSize:'10.5px', color:'#637381', display:'flex', alignItems:'center', gap:'2px' }}>
            Estimate <InfoDot text="Final tax, tariff classification, carrier or broker fees and checkout charges may vary from this estimate." />
          </div>
        )}
        <div style={{ marginTop:'9px', paddingTop:'8px', borderTop:'1px solid #e5e7eb', fontSize:'10.5px', color:'#6b7280', display:'flex', alignItems:'center', gap:'4px' }}>
          <span>{resolvedOrigin} → {r.country_name}{hsCode ? ` · HS ${hsCode}` : ''}</span>
          <InfoDot text={[
            r.breakdown?.exchange_rate ? `Exchange rate: ${r.breakdown.exchange_rate}` : '',
            r.breakdown?.de_minimis_threshold ? `De minimis: ${r.breakdown.local_currency} ${r.breakdown.de_minimis_threshold}` : '',
            r.ioss?.threshold_basis_eur ? `IOSS threshold basis: ${r.ioss.threshold_basis_eur}` : '',
            `Data source: ${r.breakdown?.data_source || 'Government API'}`,
            hsDescription || ''
          ].filter(Boolean).join(' · ')} />
        </div>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="calculator">
      <h2>🧮 VAT & Duty Calculator</h2>
      <a
        href="https://www.taxdutyking.com"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Tax & Duty King website"
        style={{
          display:'inline-flex', alignItems:'center', gap:'4px', margin:'-4px 0 4px',
          color:'#637381', fontSize:'11px', textDecoration:'none'
        }}
      >
        <span aria-hidden="true" style={{color:'#e6b800', fontSize:'13px'}}>♛</span>
        <span>Tax &amp; Duty King</span>
      </a>
      <p className="calculator-subtitle">Product-level import cost estimator for merchant use</p>

      <div className="calculator-form">
        {/* Origin indicator */}
        <div style={{ marginBottom:'12px', padding:'8px 12px', background:'#f4f6f8', borderRadius:'6px', fontSize:'13px', color:'#454f5b', display:'flex', alignItems:'center', gap:'6px' }}>
          <span>📦</span>
          <span>Shipping from: <strong>{resolvedOrigin}</strong></span>
          {resolvedOrigin !== storeOriginCountry && selectedProduct && (
            <span style={{ color:'#637381', fontSize:'12px' }}>(vendor origin: {selectedProduct.vendor})</span>
          )}
        </div>

        {/* Product search */}
        <div className="form-group" style={{ position:'relative' }}>
          <label>Product</label>
          <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
            <input
              ref={searchRef}
              type="text"
              value={productSearch}
              onChange={e => { setProductSearch(e.target.value); if (selectedProduct) handleClearProduct(); }}
              onFocus={() => productResults.length > 0 && setShowDropdown(true)}
              placeholder="Search products by name, vendor or type…"
              style={{ flex:1 }}
            />
            {selectedProduct && (
              <button onClick={handleClearProduct} style={{ padding:'4px 8px', background:'#f4f6f8', border:'1px solid #c4cdd5', borderRadius:'4px', cursor:'pointer', fontSize:'12px', whiteSpace:'nowrap' }}>✕ Clear</button>
            )}
          </div>
          {productSearching && <div style={{ fontSize:'12px', color:'#637381', marginTop:'4px' }}>Searching…</div>}
          {showDropdown && productResults.length > 0 && (
            <div ref={dropdownRef} style={{ position:'absolute', zIndex:1000, top:'100%', left:0, right:0, background:'#fff', border:'1px solid #c4cdd5', borderRadius:'6px', boxShadow:'0 4px 12px rgba(0,0,0,0.12)', maxHeight:'240px', overflowY:'auto', marginTop:'2px' }}>
              {productResults.map(p => (
                <div
                  key={p.id}
                  onClick={() => handleSelectProduct(p)}
                  style={{ padding:'8px 12px', cursor:'pointer', borderBottom:'1px solid #f4f6f8', fontSize:'13px' }}
                  onMouseEnter={e => e.currentTarget.style.background='#f4f6f8'}
                  onMouseLeave={e => e.currentTarget.style.background='#fff'}
                >
                  <div style={{ fontWeight:600, color:'#212b36' }}>{p.title}</div>
                  <div style={{ fontSize:'11px', color:'#637381' }}>{p.vendor}{p.product_type ? ` · ${p.product_type}` : ''}</div>
                </div>
              ))}
            </div>
          )}
          {showDropdown && !productSearching && productResults.length === 0 && productSearch.length >= 2 && (
            <div style={{ position:'absolute', zIndex:1000, top:'100%', left:0, right:0, background:'#fff', border:'1px solid #c4cdd5', borderRadius:'6px', padding:'10px 12px', fontSize:'13px', color:'#637381', marginTop:'2px' }}>
              No products found. Make sure your catalogue is synced in the HS Codes tab.
            </div>
          )}
        </div>

        {/* Variant selector */}
        {selectedProduct && (
          <div className="form-group">
            <label>Variant</label>
            {variantsLoading ? (
              <div style={{ fontSize:'13px', color:'#637381', padding:'8px 0' }}>Loading variants…</div>
            ) : variantsError ? (
              <div>
                <div style={{ fontSize:'13px', color:'#bf0711', padding:'4px 0 6px' }}>⚠️ Could not load variants: {variantsError}</div>
                <div style={{ fontSize:'12px', color:'#637381', marginBottom:'4px' }}>Enter price manually to continue:</div>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder={`Price in ${currency}`}
                  value={manualPrice}
                  onChange={e => setManualPrice(e.target.value)}
                  style={{ width:'100%', padding:'8px 12px', border:'1px solid #c4cdd5', borderRadius:'6px', fontSize:'14px' }}
                />
              </div>
            ) : variants.length === 0 ? (
              <div style={{ fontSize:'13px', color:'#637381', padding:'8px 0' }}>No variants found for this product</div>
            ) : variants.length === 1 ? (
              <div style={{ padding:'8px 12px', background:'#f4f6f8', borderRadius:'6px', fontSize:'13px', color:'#454f5b' }}>
                {variants[0].title === 'Default Title' ? 'Single variant' : variants[0].title} — <strong>{getCurrencySymbol(currency)}{variants[0].price}</strong>
              </div>
            ) : (
              <select value={selectedVariantId} onChange={e => setSelectedVariantId(e.target.value)}>
                <option value="">Select variant…</option>
                {variants.map(v => (
                  <option key={v.id} value={v.id}>{v.title} — {getCurrencySymbol(currency)}{v.price}{v.sku ? ` (${v.sku})` : ''}</option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* HS Code (auto-populated, editable) */}
        {selectedProduct && (
          <div className="form-group">
            <label>
              HS Code
              {hsSource === 'tdk' && <span style={{ marginLeft:'6px', fontSize:'11px', color:'#2e7d32', background:'#e8f5e9', padding:'1px 6px', borderRadius:'10px' }}>✅ Auto-populated from TDK</span>}
              {hsSource === 'manual' && <span style={{ marginLeft:'6px', fontSize:'11px', color:'#856404', background:'#fff3cd', padding:'1px 6px', borderRadius:'10px' }}>✏️ Manual</span>}
              {!hsSource && !hsCode && <span style={{ marginLeft:'6px', fontSize:'11px', color:'#bf0711', background:'#fef3f2', padding:'1px 6px', borderRadius:'10px' }}>⚠️ No HS code — classify in HS Codes tab</span>}
            </label>
            <input
              type="text"
              value={hsCode}
              onChange={e => { setHsCode(e.target.value); setHsSource('manual'); }}
              placeholder="e.g. 8712.00 (optional — improves accuracy)"
            />
            {hsDescription && <div style={{ fontSize:'11px', color:'#637381', marginTop:'3px' }}>📋 {hsDescription}</div>}
          </div>
        )}

        {/* Customer Country */}
        <div className="form-group">
          <label>Customer Country</label>
          <select value={customerCountry} onChange={e => {
            const nextCountry = e.target.value;
            setCustomerCountry(nextCountry);
            if (nextCountry !== 'US') {
              setUsState('');
              setUsPostalCode('');
            }
            setResult(null);
          }}>
            <option value="">Select country</option>
            {sortedCountries.map(rate => (
              <option key={rate.id} value={rate.country_code}>{rate.country_name} ({rate.country_code})</option>
            ))}
          </select>
        </div>

        {customerCountry === 'US' && (
          <div className="form-group">
            <label>
              U.S. delivery location <span style={{fontWeight:400, color:'#637381'}}>(optional)</span>
              <InfoDot text="Without a State, Tax & Duty King uses the labelled U.S. nationwide sales-tax average. State and ZIP refine the estimate." />
            </label>
            <div style={{display:'flex', gap:'8px'}}>
              <input
                type="text"
                value={usState}
                onChange={e => { setUsState(e.target.value.toUpperCase().slice(0, 2)); setResult(null); }}
                placeholder="State, e.g. CA"
                maxLength={2}
                style={{width:'38%'}}
              />
              <input
                type="text"
                value={usPostalCode}
                onChange={e => { setUsPostalCode(e.target.value.replace(/[^0-9-]/g, '').slice(0, 10)); setResult(null); }}
                placeholder="ZIP (optional)"
                style={{width:'62%'}}
              />
            </div>
          </div>
        )}

        {/* Quantity */}
        <div className="form-group">
          <label>Quantity</label>
          <input type="number" min="1" value={quantity} onChange={e => setQuantity(parseInt(e.target.value) || 1)} />
        </div>

        <button className="polaris-button" onClick={handleCalculate} disabled={loading || !selectedProduct || !customerCountry}>
          {loading ? 'Calculating…' : '🧮 Calculate VAT & Duty'}
        </button>
      </div>

      {error && <div className="alert alert-error">❌ {error}</div>}

      {result && (
        <div className="calculation-result">
          <h3 style={{fontSize:'14px', marginBottom:'8px', color:'#374151'}}>Calculation result</h3>
          {renderResult()}
        </div>
      )}
    </div>
  );
};


// Pagination Component
const Pagination = ({ currentPage, totalItems, itemsPerPage, onPageChange }) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  if (totalPages <= 1) return null;
  
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    pages.push(i);
  }
  
  const handlePageChange = (newPage) => {
    if (typeof onPageChange === 'function' && newPage >= 1 && newPage <= totalPages) {
      onPageChange(newPage);
    }
  };
  
  return (
    <div className="pagination">
      <button 
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="pagination-btn"
      >
        ← Previous
      </button>
      
      {pages.map(page => (
        <button
          key={page}
          onClick={() => handlePageChange(page)}
          className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
        >
          {page}
        </button>
      ))}
      
      <button 
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="pagination-btn"
      >
        Next →
      </button>
    </div>
  );
};

// In-App Banner Component
const UsageBanner = ({ subscription, onDismiss, onUpgrade }) => {
  if (!subscription || (!subscription.at_warning && !subscription.at_limit && !subscription.grace_active)) {
    return null;
  }

  const getBannerType = () => {
    if (subscription.grace_active) return 'critical';
    if (subscription.at_limit) return 'danger';
    if (subscription.at_warning) return 'warning';
    return 'info';
  };

  const getBannerMessage = () => {
    const { calculations_used, calculations_limit, usage_percentage, plan_handle } = subscription;
    
    if (subscription.grace_active) {
      return {
        title: '🚨 Grace Period Active',
        message: `You've exceeded your ${plan_handle} plan limit (${calculations_used}/${calculations_limit} calculations). Service may be interrupted soon.`
      };
    } else if (subscription.at_limit) {
      return {
        title: '⛔ Usage Limit Reached',
        message: `You've reached your monthly limit of ${calculations_limit} calculations on your ${plan_handle} plan.`
      };
    } else if (subscription.at_warning) {
      return {
        title: '⚠️ Approaching Usage Limit',
        message: `You've used ${usage_percentage.toFixed(1)}% of your monthly calculations (${calculations_used}/${calculations_limit}).`
      };
    }
    return { title: '', message: '' };
  };

  const bannerType = getBannerType();
  const { title, message } = getBannerMessage();

  return (
    <div className={`usage-banner usage-banner-${bannerType}`}>
      <div className="usage-banner-content">
        <div className="usage-banner-main">
          <h4>{title}</h4>
          <p>{message}</p>
        </div>
        <div className="usage-banner-actions">
          {onUpgrade && (
            <button className="banner-btn banner-btn-primary" onClick={onUpgrade}>
              ⚡ Upgrade Plan
            </button>
          )}
          <button className="banner-btn banner-btn-secondary" onClick={onDismiss}>
            ✕ Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

// Enhanced Monthly Usage Card Component with new unified API
const MonthlyUsageCard = ({ stores, analyticsData = {} }) => {
  const [usageData, setUsageData] = useState({});
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (stores.length > 0) {
      loadUsageData();
    }
  }, [stores]);

  const loadUsageData = async () => {
    setLoading(true);
    try {
      const usageMap = {};
      
      for (const store of stores) {
        try {
          // Use new hardened usage API
          const response = await api.get(`/usage/current?shop=${store.shop_domain}`);
          
          
          // ⚠️ CHECK FOR REAUTH
          if (response.data?.reauth?.needs_reauth === true) {
            console.warn("🔐 Store needs reauthorization:", store.shop_domain);
            // Trigger reauth modal in parent App component
            window.dispatchEvent(new CustomEvent('needsReauth', { 
              detail: { 
                shop: store.shop_domain,
                errorDetails: response.data.reauth.error_details 
              }
            }));
          }
          
          const billing = mapUsageResponseToBilling(response.data);

          const planHandle = billing.plan_handle || store.plan_handle || 'starter';
          const planLimit = billing.calculations_limit || store.limits?.[planHandle] || 750;
          const used = billing.calculations_used || 0;
          const percentUsed = planLimit > 0 ? Math.min(100, Math.round((used / planLimit) * 100)) : 0;
          
          // Use server-provided threshold flags instead of calculating client-side
          const atWarning = billing.at_warning || false;  // From server gating.atWarning
          const atLimit = billing.at_limit || false;       // From server gating.isOverLimit

          // Convert billing data to usage format for this component with safe fallbacks
          usageMap[store.shop_domain] = {
            used,
            limit: planLimit,
            plan: billing.plan_name || store.plan_name || "Starter",
            paused: billing.paused || false,
            percentUsed,
            atWarning,
            atLimit,
            graceActive: billing.gating?.isInGracePeriod || false,
            period: response.data.period || new Date().toISOString().slice(0, 7),
            shouldPromptUpgrade: billing.gating?.shouldPromptUpgrade || false,
            isOverLimit: billing.gating?.isOverLimit || false,
            gating: billing.gating || { isOverLimit: false, shouldPromptUpgrade: false, atWarning: false, atSoft: false },
            accessAllowed: billing.gating?.accessAllowed !== undefined ? billing.gating.accessAllowed : true,
            ui_today: billing.ui_today || { calculations: 0, revenue: 0, avg_cost: 0, day_start_utc: null },
            trial: response.data.trial || null,  // Add trial information
            effective_status: billing.effective_status || 'inactive'  // CRITICAL: Preserve effective_status for gating
          };
        } catch (err) {
          console.error(`Failed to load usage for ${store.shop_domain}:`, err);
          // Graceful fallback with minimal defaults - but log the failure
          usageMap[store.shop_domain] = {
            used: null,
            limit: null,
            plan: null,
            paused: false,
            percentUsed: 0,
            atWarning: false,
            atLimit: false,
            graceActive: false,
            period: null,
            shouldPromptUpgrade: false,
            isOverLimit: false
          };
        }
      }
      
      setUsageData(usageMap);
    } catch (err) {
      console.error('Failed to load usage data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Billing navigation functions (same as ManagedBilling component)
  const getShop = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('shop') || stores[0]?.shop_domain;
  };

  const getStoreHandle = () => {
    const shop = getShop();
    return shop ? shop.replace('.myshopify.com', '') : null;
  };

  const redirectToHostedPricing = () => {
    const shop = getShop();
    if (!redirectToShopifyBilling(shop)) {
      console.error('Unable to open Shopify billing. Verify the app handle configured in Vercel.');
      return;
    }
    setRedirecting(true);
  };

  const pauseUsage = async (shopDomain) => {
    try {
      await api.post('/usage/pause', { shop_domain: shopDomain });
      // Reload data to reflect paused state
      await loadUsageData();
    } catch (err) {
      console.error(`Failed to pause usage for ${shopDomain}:`, err);
    }
  };

  const resumeUsage = async (shopDomain) => {
    try {
      await api.post('/usage/resume', { shop_domain: shopDomain });
      // Reload data to reflect resumed state
      await loadUsageData();
    } catch (err) {
      console.error(`Failed to resume usage for ${shopDomain}:`, err);
    }
  };

  const createSubscription = async (shopDomain, planHandle) => {
    try {
      const response = await api.post('/subscription/create', {
        shop_domain: shopDomain,
        plan_handle: planHandle,
        billing_period: 'monthly'
      });
      
      if (response.data.success) {
        // Redirect to Shopify confirmation URL
        window.open(response.data.confirmation_url, '_blank');
        setTimeout(() => {
          loadUsageData();
        }, 2000);
      } else {
        // Handle structured error responses
        if (response.data.error === "shop_not_connected") {
          alert(`🚀 Shopify App Store Required\n\n${response.data.message}\n\nNext Steps:\n${response.data.details.required_steps.join('\n')}\n\nSelected Plan: ${response.data.details.plan_handle} (${response.data.details.billing_period})`);
        } else {
          alert(`⚠️ Development Mode\n\n${response.data.message}\n\nPlan: ${response.data.details?.plan_handle || planHandle}\nBilling: ${response.data.details?.billing_period || 'monthly'}`);
        }
      }
    } catch (err) {
      console.error('Failed to create subscription:', err);
      
      // Handle HTTP errors
      if (err.response?.status === 400) {
        alert('🚀 Shopify App Store Connection Required\n\nThis app needs to be:\n1. Submitted to Shopify App Store\n2. Installed from the App Store\n3. Connected via OAuth\n\nThen subscription creation will work!\n\nCurrent Status: Development/Testing Mode');
      } else if (err.response?.status === 404) {
        alert('⚠️ Shop Not Found\n\nThis shop needs to install the app from the Shopify App Store first.');
      } else if (err.response?.status === 422) {
        alert('⚠️ App Configuration Required\n\nThis app needs to be properly configured with Shopify Partner Dashboard.');
      } else {
        alert('❌ Subscription Creation Failed\n\nThis is expected until the app is listed on Shopify App Store.\n\nPlease try again after App Store submission.');
      }
    }
  };

  const getUpgradeOptions = (currentPlan) => {
    const plans = ['trial', 'starter', 'growth', 'enterprise'];
    const planNames = { trial: 'Trial', starter: 'Starter', growth: 'Growth', enterprise: 'Enterprise' };
    const currentIndex = plans.findIndex(p => currentPlan.toLowerCase().includes(p));
    return plans.slice(Math.max(0, currentIndex + 1));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusColor = (usage) => {
    if (usage.gating?.accessAllowed === false) return '#dc2626';
    if (usage.paused) return '#6b7280'; // Gray for paused
    if (usage.atLimit) return '#dc2626'; // Red for at limit
    if (usage.atWarning) return '#f59e0b'; // Yellow for warning
    return '#10b981'; // Green for normal
  };

  const getStatusText = (usage) => {
    if (usage.gating?.accessAllowed === false) {
      if (usage.effective_status === 'trial_expired') return 'Trial Expired';
      return 'Upgrade Required';
    }
    if (usage.paused) return 'Paused';
    if (usage.atLimit) return 'Limit Reached';
    if (usage.atWarning) return 'Near Limit';
    return 'Active';
  };

  const formatUsageMessage = (usage) => {
    const { used, limit, percentUsed, paused, atWarning, atLimit, graceActive } = usage;

    if (usage.gating?.accessAllowed === false) {
      return usage.gating?.message || 'Plan inactive. Please upgrade to continue using the app.';
    }

    if (paused) {
      return `Usage is paused until the 1st. You used ${used}/${limit} calculations this month. Click Resume to continue (counts toward this month).`;
    } else if (atLimit && !graceActive) {
      return `You've reached your monthly limit (${used}/${limit} calculations). Upgrade for more capacity or Pause usage for the rest of this month. Resets on the 1st at 00:00 (Europe/London).`;
    } else if (graceActive) {
      return `You've exceeded your limit but are in the grace period (${used}/${limit} calculations, ${percentUsed.toFixed(1)}%). Upgrade or pause to avoid service interruption.`;
    } else if (atWarning) {
      return `You've used ${used}/${limit} calculations this month (${percentUsed.toFixed(1)}%). Consider upgrading to avoid interruptions.`;
    } else {
      return `You've used ${used}/${limit} calculations this month (${percentUsed.toFixed(1)}%). Resets on the 1st at 00:00 (Europe/London).`;
    }
  };

  if (loading) {
    return (
      <div className="usage-card">
        <div className="usage-header">
          <h3>📊 Monthly Calculations Used</h3>
        </div>
        <div className="usage-loading">Loading usage data...</div>
      </div>
    );
  }

  return (
    <div className="usage-card">
      <div className="usage-header">
        <h3>📊 Monthly Calculations Used</h3>
        <p className="usage-subtitle">Track your calculator usage and plan limits</p>
        <div className="usage-info-text">
          <small>Resets on the 1st at 00:00 (Europe/London). Grace buffer: 5%.</small>
        </div>
      </div>

      <div className="usage-calculation-explainer">
        <div className="usage-calculation-explainer__heading"><span>ℹ️</span><div><b>When a calculation is used</b><p>A calculation is generated when Tax &amp; Duty King needs a fresh landed-cost estimate for a shopper’s route or product selection.</p></div></div>
        <div className="usage-calculation-explainer__surfaces">
          <div className="is-high"><b>Product page — highest exposure</b><span>Product pages receive far more visits than carts. A displayed estimate can therefore use substantially more calculations, especially when shoppers change country or quantity.</span></div>
          <div><b>Cart drawer — lower exposure</b><span>Calculations are requested only after a shopper has added an item and opens the quick cart.</span></div>
          <div><b>Cart page — lower exposure</b><span>Calculations are requested for the smaller group of shoppers who reach the full cart.</span></div>
        </div>
        <p className="usage-calculation-explainer__tip"><b>Planning tip:</b> Product-page estimates build confidence early in the purchase journey, but monitor their usage closely. Cart drawer and cart-page placement normally expose the calculator to fewer shopper visits.</p>
      </div>

      <div className="usage-stores">
        {stores.map(store => {
          const usage = usageData[store.shop_domain];
          
          if (!usage) {
            return (
              <div key={store.shop_domain} className="usage-store">
                <h4>{store.shop_domain}</h4>
                <div className="usage-no-data">Loading usage data...</div>
              </div>
            );
          }

          const upgradeOptions = getUpgradeOptions(usage.plan);
          const statusColor = getStatusColor(usage);
          const statusText = getStatusText(usage);

          return (
            <div key={store.shop_domain} className="usage-store">
              <div className="usage-store-header">
                <h4>{store.shop_domain}</h4>
                <div className="usage-status-badges">
                  <span 
                    className="usage-status-badge"
                    style={{ backgroundColor: statusColor, color: 'white' }}
                  >
                    {statusText}
                  </span>
                  {usage.graceActive && (
                    <span className="grace-badge">Grace Period</span>
                  )}
                </div>
              </div>

              <div className="usage-stats">
                <div className="usage-numbers">
                  <span className="usage-current">{usage.used}</span>
                  <span className="usage-separator"> / </span>
                  <span className="usage-limit">{usage.limit}</span>
                  <span className="usage-label"> calculations</span>
                </div>
                
                <div className="usage-percentage">
                  {usage.percentUsed.toFixed(1)}% used
                </div>
              </div>

              {/* Trial Countdown Display - Only show for trial_active status */}
              {usage.effective_status === 'trial_active' && usage.trial && (
                <div className="trial-countdown">
                  <div className="trial-status">
                    <div className="trial-active">
                      <span className="trial-icon">🎁</span>
                      <span className="trial-text">
                        Trial: {usage.trial.days_remaining === 0 
                          ? `${usage.trial.hours_remaining}h left` 
                          : `${usage.trial.days_remaining}d ${usage.trial.hours_remaining}h left`}
                      </span>
                      <div className="trial-urgency">
                        {usage.trial.days_remaining <= 1 && (
                          <span className="urgent">⚡ Upgrade now to avoid interruption!</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="trial-actions">
                    <button 
                      className="btn btn-primary btn-sm trial-upgrade-btn"
                      onClick={redirectToHostedPricing}
                      disabled={redirecting}
                    >
                      {redirecting ? (
                        <>
                          <div className="button-spinner"></div>
                          Redirecting...
                        </>
                      ) : (
                        '🏪 Upgrade Plan'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Trial expired banner */}
              {usage.effective_status === 'trial_expired' && (
                <div className="trial-expired">
                  <div className="trial-expired-message">
                    <span className="trial-icon">⏰</span>
                    <div className="trial-text">
                      <strong>Trial ended</strong>
                      <span>{usage.gating?.message || 'Upgrade to continue running calculations.'}</span>
                    </div>
                  </div>
                  <div className="trial-actions">
                    <button
                      className="btn btn-primary btn-sm trial-upgrade-btn"
                      onClick={redirectToHostedPricing}
                      disabled={redirecting}
                    >
                      {redirecting ? (
                        <>
                          <div className="button-spinner"></div>
                          Redirecting...
                        </>
                      ) : (
                        '🏪 Upgrade Plan'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Store frozen/paused banner */}
              {usage.storeFrozen && (
                <div className="store-frozen-banner" style={{
                  background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                  border: '1px solid #f87171',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '24px' }}>🔒</span>
                  <div style={{ flex: 1 }}>
                    <strong style={{ color: '#b91c1c', display: 'block', marginBottom: '4px' }}>
                      Shopify Store Inactive
                    </strong>
                    <span style={{ color: '#dc2626', fontSize: '14px' }}>
                      Your Shopify store subscription has expired or is paused. 
                      Please reactivate your Shopify subscription to continue using Tax & Duty King.
                    </span>
                  </div>
                  <a 
                    href="https://admin.shopify.com/store/settings/billing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-danger btn-sm"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    🏪 Reactivate Store
                  </a>
                </div>
              )}

              {/* App subscription expired banner */}
              {usage.subscriptionExpired && !usage.storeFrozen && (
                <div className="subscription-expired-banner" style={{
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '24px' }}>⚠️</span>
                  <div style={{ flex: 1 }}>
                    <strong style={{ color: '#92400e', display: 'block', marginBottom: '4px' }}>
                      App Subscription Expired
                    </strong>
                    <span style={{ color: '#b45309', fontSize: '14px' }}>
                      Your Tax & Duty King subscription has expired. 
                      Please renew to continue running calculations for your customers.
                    </span>
                  </div>
                  <button
                    className="btn btn-warning btn-sm"
                    onClick={redirectToHostedPricing}
                    disabled={redirecting}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {redirecting ? 'Redirecting...' : '🔄 Renew Subscription'}
                  </button>
                </div>
              )}

              <div className="usage-progress">
                <div className="usage-bar">
                  <div 
                    className="usage-fill"
                    style={{ 
                      width: `${Math.min(usage.percentUsed, 100)}%`,
                      backgroundColor: statusColor
                    }}
                  ></div>
                  {usage.graceActive && (
                    <div 
                      className="grace-indicator"
                      style={{ 
                        left: '100%',
                        width: `${Math.max(0, usage.percentUsed - 100)}%`,
                        backgroundColor: '#f59e0b'
                      }}
                    ></div>
                  )}
                </div>
              </div>

              {/* Usage Message */}
              <div className="usage-message">
                <p>{formatUsageMessage(usage)}</p>
              </div>

              {/* Action Buttons */}
              <div className="usage-actions">
                {usage.gating?.accessAllowed === false ? (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={redirectToHostedPricing}
                    disabled={redirecting}
                  >
                    {redirecting ? (
                      <>
                        <div className="button-spinner"></div>
                        Redirecting...
                      </>
                    ) : (
                      'Upgrade to Continue'
                    )}
                  </button>
                ) : usage.paused ? (
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => resumeUsage(store.shop_domain)}
                  >
                    ▶️ Resume Usage
                  </button>
                ) : (
                  <>

                    {(usage.atWarning || usage.atLimit || usage.graceActive) && upgradeOptions.length > 0 && (
                      <div className="upgrade-options-quick">
                        <h6>Upgrade Options:</h6>
                        <div className="upgrade-buttons">
                          {upgradeOptions.map(planHandle => {
                            const planConfig = {
                              'growth': { name: 'Growth', price: 49.99, limit: 2500 },
                              'enterprise': { name: 'Enterprise', price: 149.99, limit: 10000 }
                            };
                            
                            const plan = planConfig[planHandle];
                            if (!plan) return null;
                            
                            return (
                              <button
                                key={planHandle}
                                className="btn btn-primary btn-sm"
                                onClick={() => {
                                  // Use exact same function as ManagedBilling component
                                  try {
                                    redirectToShopifyBilling();
                                  } catch (error) {
                                    console.error('❌ Billing redirect failed:', error);
                                    // Show error message to user
                                    alert('Unable to redirect to billing. Please try again or contact support.');
                                  }
                                }}
                              >
                                {plan.name} - {formatCurrency(plan.price)}/month
                                <br />
                                <small>{plan.limit.toLocaleString()} calculations</small>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Period Information */}
              <div className="usage-period-info">
                <small>
                  Period: {usage.period} | Plan: {usage.plan}
                  {usage.resetsAt && ` | Next reset: ${new Date(usage.resetsAt).toLocaleDateString()}`}
                </small>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Feature Gating Component for Plan-based Restrictions
const FeatureGate = ({ requiredPlan, currentPlan, children, fallback = null }) => {
  const planHierarchy = ['starter', 'growth', 'enterprise'];
  
  if (!currentPlan) {
    return fallback; // No plan info, show fallback
  }
  
  const currentPlanIndex = planHierarchy.indexOf(currentPlan);
  const requiredPlanIndex = planHierarchy.indexOf(requiredPlan);
  
  // Check if current plan meets or exceeds required plan..
  const hasAccess = currentPlanIndex >= requiredPlanIndex;
  
  return hasAccess ? children : fallback;
};

// Upgrade Prompt Component
const UpgradePrompt = ({ requiredPlan, feature, className = "" }) => {
  const planNames = {
    'trial': 'Trial',
    'starter': 'Starter',
    'growth': 'Growth', 
    'enterprise': 'Enterprise'
  };
  
  return (
    <div className={`upgrade-prompt ${className}`}>
      <div className="upgrade-content">
        <h4>🔒 {planNames[requiredPlan]} Plan Required</h4>
        <p>Upgrade to {planNames[requiredPlan]} plan to access {feature}.</p>
        <button className="btn btn-primary btn-sm">
          ⚡ Upgrade Now
        </button>
      </div>
    </div>
  );
};

// Plan-aware Data Display Components
const PlanAwareDataStats = ({ currentPlan, dataCount, dataType }) => {
  const planLimit = currentPlan?.limits?.[currentPlan?.plan_handle] ?? 750;

  return (
    <div className="plan-aware-stats">
      <div className="data-count">
        <span className="count">{dataCount}</span>
        <span className="limit">/ {planLimit}</span>
        <span className="type">{dataType}</span>
      </div>
      {currentPlan?.plan_handle === 'starter' && (
        <div className="plan-limitation">
          <span className="limitation-text">
            {dataType === 'countries' ? 'Major markets only' : 'Basic categories only'}
          </span>
        </div>
      )}
    </div>
  );
};

// Comprehensive Subscription Management Component

// ── VAT Rates Manager — with search, pagination, country selector ──────────────
const VATRatesManager = ({ vatRates, loading = false, onReload, backendUrl, apiKey }) => {
  const PAGE_SIZE = 25;

  const [search, setSearch] = React.useState('');
  const [regionFilter, setRegionFilter] = React.useState('all');
  const [page, setPage] = React.useState(1);
  const [vatHistory, setVatHistory] = React.useState([]);
  const [historyExpanded, setHistoryExpanded] = React.useState(false);
  const [agentRunning, setAgentRunning] = React.useState(false);
  const [agentResult, setAgentResult] = React.useState(null);
  const [agentError, setAgentError] = React.useState(null);
  const [vatStatus, setVatStatus] = React.useState(null);

  const headers = { 'Content-Type': 'application/json', 'x-api-key': apiKey };

  React.useEffect(() => {
    if (backendUrl && apiKey) fetchVatStatus();
  }, []); // eslint-disable-line

  // Region groupings
  const REGIONS = {
    all: 'All Countries',
    eu: 'European Union',
    apac: 'Asia-Pacific',
    americas: 'Americas',
    mena: 'Middle East & Africa',
    other: 'Other',
  };
  const EU_CODES = ['AT','BE','BG','CY','CZ','DE','DK','EE','ES','FI','FR','GB','GR','HR','HU','IE','IT','LT','LU','LV','MT','NL','PL','PT','RO','SE','SI','SK','NO','IS','CH'];
  const APAC_CODES = ['AU','NZ','JP','KR','SG','HK','TW','TH','MY','IN','ID','PH','VN','CN'];
  const AMERICAS_CODES = ['US','CA','MX','BR','AR','CL','CO','PE'];
  const MENA_CODES = ['AE','SA','QA','KW','IL','EG','ZA','TR'];

  const getRegion = (code) => {
    if (EU_CODES.includes(code)) return 'eu';
    if (APAC_CODES.includes(code)) return 'apac';
    if (AMERICAS_CODES.includes(code)) return 'americas';
    if (MENA_CODES.includes(code)) return 'mena';
    return 'other';
  };

  // Filter + search
  const filtered = React.useMemo(() => {
    let list = vatRates || [];
    if (regionFilter !== 'all') {
      list = list.filter(r => getRegion(r.country_code) === regionFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(r =>
        (r.country_name || '').toLowerCase().includes(q) ||
        (r.country_code || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [vatRates, search, regionFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Reset page when filter/search changes
  React.useEffect(() => { setPage(1); }, [search, regionFilter]);

  const fetchVatStatus = async () => {
    if (!backendUrl || !apiKey) return;
    try {
      const res = await fetch(`${backendUrl}/api/duty-rate-agent/status`, { headers });
      if (res.ok) {
        const data = await res.json();
        setVatStatus(data);
      }
    } catch (e) { /* non-critical */ }
  };

  const fetchVatHistory = async () => {
    if (!backendUrl || !apiKey) return;
    try {
      const res = await fetch(`${backendUrl}/api/duty-rate-agent/history?limit=50&record_type=vat_rate_change`, { headers });
      if (res.ok) {
        const data = await res.json();
        setVatHistory(data.history || []);
      }
    } catch (e) { /* non-critical */ }
  };

  const runVatAudit = async () => {
    if (!backendUrl || !apiKey) return;
    setAgentRunning(true);
    setAgentResult(null);
    setAgentError(null);
    try {
      const res = await fetch(`${backendUrl}/api/duty-rate-agent/run-vat`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      if (res.ok) {
        setAgentResult(data);
        if (onReload) onReload();
        fetchVatHistory();
        fetchVatStatus();
      } else {
        setAgentError(data.detail || 'VAT audit failed');
      }
    } catch (e) {
      setAgentError('Failed to run VAT audit: ' + e.message);
    } finally {
      setAgentRunning(false);
    }
  };

  const toggleHistory = () => {
    if (!historyExpanded) fetchVatHistory();
    setHistoryExpanded(!historyExpanded);
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="vat-rates-manager">
        <div className="section-header">
          <h2>📋 Global VAT Rates</h2>
          <LoadingSpinner text="Loading VAT rates..." />
        </div>
      </div>
    );
  }

  if (!vatRates || vatRates.length === 0) {
    return (
      <div className="vat-rates-manager">
        <div className="section-header">
          <h2>📋 Global VAT Rates</h2>
          <EmptyState
            title="No VAT Rates Available"
            description="VAT rates data could not be loaded. This may be due to a connection issue or server maintenance."
            icon="📋"
            action={
              onReload && (
                <button onClick={onReload} className="btn btn-primary">
                  🔄 Reload VAT Rates
                </button>
              )
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="vat-rates-manager">

      {/* ── AI VAT Agent Panel ─────────────────────────────────────────────── */}
      {backendUrl && apiKey && (
        <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', borderRadius: '12px', padding: '20px', marginBottom: '24px', border: '1px solid #0f3460', color: '#e0e0e0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div>
              <h3 style={{ margin: 0, color: '#fff', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                🤖 AI VAT Rate Agent
                <span style={{ background: '#00b894', color: '#fff', fontSize: '11px', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>ACTIVE</span>
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#a0aec0' }}>
                Automatically monitors EU VAT rates via euvatrates.com and non-EU rates via AI research every week.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={toggleHistory}
                style={{ background: '#2d3748', color: '#e0e0e0', border: 'none', borderRadius: '6px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px' }}
              >
                {historyExpanded ? '▲ Hide History' : '📋 View Changes'}
              </button>
              <button
                onClick={runVatAudit}
                disabled={agentRunning}
                style={{ background: agentRunning ? '#4a5568' : '#3182ce', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: agentRunning ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600 }}
              >
                {agentRunning ? '⏳ Running...' : '▶ Run Now'}
              </button>
            </div>
          </div>

          {/* Stats row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: agentError || agentResult ? '16px' : 0 }}>
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '12px' }}>
              <div style={{ fontSize: '11px', color: '#718096', marginBottom: '4px' }}>SCHEDULE</div>
              <div style={{ fontSize: '13px', color: '#e0e0e0' }}>Every Monday 06:00 UTC</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '12px' }}>
              <div style={{ fontSize: '11px', color: '#718096', marginBottom: '4px' }}>LAST RUN</div>
              <div style={{ fontSize: '13px', color: '#e0e0e0' }}>
                {vatStatus?.last_vat_run?.started_at
                  ? new Date(vatStatus.last_vat_run.started_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                  : 'Never'}
              </div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '12px' }}>
              <div style={{ fontSize: '11px', color: '#718096', marginBottom: '4px' }}>TOTAL VAT CHANGES</div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#63b3ed' }}>
                {vatStatus?.total_vat_changes_all_time ?? '—'}
              </div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '12px' }}>
              <div style={{ fontSize: '11px', color: '#718096', marginBottom: '4px' }}>SOURCES</div>
              <div style={{ fontSize: '12px', color: '#e0e0e0', lineHeight: '1.5' }}>EU/GB: euvatrates.com<br/>Non-EU: GPT-4.1-mini</div>
            </div>
          </div>

          {agentError && (
            <div style={{ background: 'rgba(245,101,101,0.15)', border: '1px solid #fc8181', borderRadius: '6px', padding: '10px 14px', marginTop: '12px', fontSize: '13px', color: '#fc8181' }}>
              ❌ {agentError}
            </div>
          )}

          {agentResult && (
            <div style={{ background: 'rgba(72,187,120,0.1)', border: '1px solid #68d391', borderRadius: '6px', padding: '12px 16px', marginTop: '12px', fontSize: '13px', color: '#68d391' }}>
              <strong>✅ VAT audit complete:</strong> {agentResult.updated} rates updated, {agentResult.unchanged} unchanged
              {agentResult.errors > 0 && <span style={{ color: '#fc8181' }}>, {agentResult.errors} errors</span>}
              <span style={{ color: '#a0aec0', marginLeft: '8px' }}>({(agentResult.duration_seconds || 0).toFixed(1)}s)</span>
            </div>
          )}

          {historyExpanded && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#a0aec0', marginBottom: '8px' }}>Recent VAT Rate Changes</div>
              {vatHistory.length === 0 ? (
                <div style={{ color: '#718096', fontSize: '13px', textAlign: 'center', padding: '20px' }}>No VAT rate changes recorded yet. Run the agent to check for updates.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #2d3748' }}>
                      {['Date', 'Country', 'Old Rate', 'New Rate', 'Change', 'Source'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '6px 10px', color: '#718096', fontWeight: 500 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {vatHistory.map((rec, i) => {
                      const delta = (rec.new_rate - rec.old_rate).toFixed(1);
                      const isUp = rec.new_rate > rec.old_rate;
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <td style={{ padding: '8px 10px', color: '#a0aec0' }}>{formatDate(rec.detected_at)}</td>
                          <td style={{ padding: '8px 10px' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {getCountryFlag(rec.country_code)}
                              <span style={{ background: '#2d3748', padding: '2px 6px', borderRadius: '4px', fontFamily: 'monospace' }}>{rec.country_code}</span>
                              <span style={{ color: '#a0aec0' }}>{rec.country_name}</span>
                            </span>
                          </td>
                          <td style={{ padding: '8px 10px', color: '#a0aec0' }}>{rec.old_rate}%</td>
                          <td style={{ padding: '8px 10px', color: '#e0e0e0', fontWeight: 600 }}>{rec.new_rate}%</td>
                          <td style={{ padding: '8px 10px', color: isUp ? '#fc8181' : '#68d391', fontWeight: 700 }}>{isUp ? '▲' : '▼'} {Math.abs(delta)}%</td>
                          <td style={{ padding: '8px 10px', color: '#718096', fontSize: '11px' }}>{rec.source?.replace(/_/g, ' ')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="section-header">
        <h2>📋 Global VAT Rates</h2>
        <p>Comprehensive tax rates for {vatRates.length} countries — auto-updated weekly</p>
        <div className="stats">
          <span className="stat">🌍 {vatRates.length} Countries</span>
          <span className="stat">💱 Multiple Currencies</span>
          <span className="stat">🏛️ Government Sources</span>
          {onReload && (
            <button onClick={onReload} className="btn btn-secondary btn-sm">
              🔄 Refresh
            </button>
          )}
        </div>
      </div>

      {/* ── Search + Region Filter ─────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 220px', minWidth: '180px' }}>
          <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#718096', pointerEvents: 'none' }}>🔍</span>
          <input
            type="text"
            placeholder="Search country name or code..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px 8px 32px', borderRadius: '6px',
              border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box',
              background: '#fff', color: '#2d3748',
            }}
          />
        </div>
        <select
          value={regionFilter}
          onChange={e => setRegionFilter(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0',
            fontSize: '14px', background: '#fff', color: '#2d3748', cursor: 'pointer',
            minWidth: '180px',
          }}
        >
          {Object.entries(REGIONS).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
        <span style={{ fontSize: '13px', color: '#718096', whiteSpace: 'nowrap' }}>
          {filtered.length} result{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Country</th>
              <th>Code</th>
              <th>VAT Rate</th>
              <th>De Minimis</th>
              <th>Currency</th>
              <th>Data Source</th>
              <th>Last Updated</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '32px', color: '#718096' }}>
                  No countries match your search.
                </td>
              </tr>
            ) : paginated.map((rate, index) => (
              <tr key={rate.id || index}>
                <td>
                  <div className="country-cell">
                    <span className="country-flag">{getCountryFlag(rate.country_code)}</span>
                    <span>{rate.country_name}</span>
                  </div>
                </td>
                <td><span className="country-code">{rate.country_code}</span></td>
                <td>
                  <span className="vat-rate" style={{ fontWeight: 700 }}>{rate.vat_rate}%</span>
                  {rate.previous_rate != null && rate.previous_rate !== rate.vat_rate && (
                    <span style={{ fontSize: '11px', color: rate.vat_rate > rate.previous_rate ? '#e53e3e' : '#38a169', marginLeft: '6px' }}>
                      {rate.vat_rate > rate.previous_rate ? '▲' : '▼'} was {rate.previous_rate}%
                    </span>
                  )}
                </td>
                <td>
                  {rate.de_minimis_threshold
                    ? `${rate.currency || ''} ${rate.de_minimis_threshold}`.trim()
                    : <span style={{ color: '#a0aec0' }}>—</span>}
                </td>
                <td><span className="currency">{rate.currency || '—'}</span></td>
                <td>
                  <span className="source-badge">{rate.source || '—'}</span>
                </td>
                <td style={{ fontSize: '12px', color: '#718096' }}>
                  {rate.last_verified_at
                    ? formatDate(rate.last_verified_at)
                    : <span style={{ color: '#cbd5e0' }}>Not verified</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setPage(1)}
            disabled={currentPage === 1}
            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #e2e8f0', background: currentPage === 1 ? '#f7fafc' : '#fff', cursor: currentPage === 1 ? 'default' : 'pointer', color: '#4a5568', fontSize: '13px' }}
          >«</button>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #e2e8f0', background: currentPage === 1 ? '#f7fafc' : '#fff', cursor: currentPage === 1 ? 'default' : 'pointer', color: '#4a5568', fontSize: '13px' }}
          >‹ Prev</button>

          {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
            // Show pages around current
            let p;
            if (totalPages <= 7) {
              p = i + 1;
            } else if (currentPage <= 4) {
              p = i + 1;
            } else if (currentPage >= totalPages - 3) {
              p = totalPages - 6 + i;
            } else {
              p = currentPage - 3 + i;
            }
            return (
              <button
                key={p}
                onClick={() => setPage(p)}
                style={{
                  padding: '6px 12px', borderRadius: '4px', border: '1px solid #e2e8f0',
                  background: p === currentPage ? '#3182ce' : '#fff',
                  color: p === currentPage ? '#fff' : '#4a5568',
                  cursor: 'pointer', fontSize: '13px', fontWeight: p === currentPage ? 700 : 400,
                }}
              >{p}</button>
            );
          })}

          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #e2e8f0', background: currentPage === totalPages ? '#f7fafc' : '#fff', cursor: currentPage === totalPages ? 'default' : 'pointer', color: '#4a5568', fontSize: '13px' }}
          >Next ›</button>
          <button
            onClick={() => setPage(totalPages)}
            disabled={currentPage === totalPages}
            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #e2e8f0', background: currentPage === totalPages ? '#f7fafc' : '#fff', cursor: currentPage === totalPages ? 'default' : 'pointer', color: '#4a5568', fontSize: '13px' }}
          >»</button>

          <span style={{ fontSize: '13px', color: '#718096', marginLeft: '8px' }}>
            Page {currentPage} of {totalPages} ({filtered.length} countries)
          </span>
        </div>
      )}
    </div>
  );
};

// Helper function for country flags
const getCountryFlag = (countryCode) => {
  const flags = {
    'US': '🇺🇸', 'CA': '🇨🇦', 'MX': '🇲🇽', 'GB': '🇬🇧', 'DE': '🇩🇪', 'FR': '🇫🇷',
    'IT': '🇮🇹', 'ES': '🇪🇸', 'NL': '🇳🇱', 'BE': '🇧🇪', 'AT': '🇦🇹', 'PL': '🇵🇱',
    'PT': '🇵🇹', 'IE': '🇮🇪', 'GR': '🇬🇷', 'CZ': '🇨🇿', 'HU': '🇭🇺', 'RO': '🇷🇴',
    'SK': '🇸🇰', 'HR': '🇭🇷', 'SI': '🇸🇮', 'BG': '🇧🇬', 'LT': '🇱🇹', 'LV': '🇱🇻',
    'EE': '🇪🇪', 'LU': '🇱🇺', 'MT': '🇲🇹', 'CY': '🇨🇾', 'SE': '🇸🇪', 'NO': '🇳🇴',
    'DK': '🇩🇰', 'FI': '🇫🇮', 'IS': '🇮🇸', 'AU': '🇦🇺', 'NZ': '🇳🇿', 'JP': '🇯🇵',
    'KR': '🇰🇷', 'SG': '🇸🇬', 'HK': '🇭🇰', 'TW': '🇹🇼', 'TH': '🇹🇭', 'MY': '🇲🇾',
    'IN': '🇮🇳', 'BR': '🇧🇷', 'AR': '🇦🇷', 'CL': '🇨🇱', 'CO': '🇨🇴', 'PE': '🇵🇪',
    'AE': '🇦🇪', 'SA': '🇸🇦', 'IL': '🇮🇱', 'QA': '🇶🇦', 'KW': '🇰🇼', 'CH': '🇨🇭',
    'TR': '🇹🇷', 'RU': '🇷🇺', 'ZA': '🇿🇦'
  };
  return flags[countryCode] || '🌍';
};

// ── Duty Rate Agent Panel ────────────────────────────────────────────────────
const DutyRateAgentPanel = ({ backendUrl, apiKey }) => {
  const [status, setStatus] = React.useState(null);
  const [history, setHistory] = React.useState([]);
  const [running, setRunning] = React.useState(false);
  const [runResult, setRunResult] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [expanded, setExpanded] = React.useState(false);

  const headers = { 'Content-Type': 'application/json', 'x-api-key': apiKey };

  React.useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/duty-rate-agent/status`, { headers });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (e) {
      // Status fetch is non-critical
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/duty-rate-agent/history?limit=20`, { headers });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.history || []);
      }
    } catch (e) {
      setError('Failed to load rate change history');
    }
  };

  const runAudit = async () => {
    setRunning(true);
    setRunResult(null);
    setError(null);
    try {
      const res = await fetch(`${backendUrl}/api/duty-rate-agent/run`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      if (res.ok) {
        setRunResult(data);
        fetchStatus();
        fetchHistory();
      } else {
        setError(data.detail || 'Audit run failed');
      }
    } catch (e) {
      setError('Failed to run audit: ' + e.message);
    } finally {
      setRunning(false);
    }
  };

  const toggleHistory = () => {
    if (!expanded) fetchHistory();
    setExpanded(!expanded);
  };

  return (
    <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', borderRadius: '12px', padding: '20px', marginBottom: '24px', border: '1px solid #0f3460', color: '#e0e0e0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div>
          <h3 style={{ margin: 0, color: '#fff', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            🤖 AI Duty Rate Agent
            <span style={{ background: '#00b894', color: '#fff', fontSize: '11px', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>ACTIVE</span>
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#a0aec0' }}>
            Automatically monitors and updates duty rates from official government tariff APIs every week.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={toggleHistory}
            style={{ background: '#2d3748', color: '#e0e0e0', border: 'none', borderRadius: '6px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px' }}
          >
            {expanded ? '▲ Hide History' : '📋 View Changes'}
          </button>
          <button
            onClick={runAudit}
            disabled={running}
            style={{ background: running ? '#4a5568' : '#3182ce', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: running ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600 }}
          >
            {running ? '⏳ Running...' : '▶ Run Now'}
          </button>
        </div>
      </div>

      {/* Status row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: error || runResult ? '16px' : 0 }}>
        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '12px' }}>
          <div style={{ fontSize: '11px', color: '#718096', marginBottom: '4px' }}>SCHEDULE</div>
          <div style={{ fontSize: '13px', color: '#e0e0e0' }}>Every Monday 06:00 UTC</div>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '12px' }}>
          <div style={{ fontSize: '11px', color: '#718096', marginBottom: '4px' }}>LAST RUN</div>
          <div style={{ fontSize: '13px', color: '#e0e0e0' }}>
            {status?.last_run?.started_at
              ? new Date(status.last_run.started_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
              : 'Never'}
          </div>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '12px' }}>
          <div style={{ fontSize: '11px', color: '#718096', marginBottom: '4px' }}>TOTAL RATE CHANGES</div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#63b3ed' }}>
            {status?.total_changes_all_time ?? '—'}
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: 'rgba(245,101,101,0.15)', border: '1px solid #fc8181', borderRadius: '6px', padding: '10px 14px', marginTop: '12px', fontSize: '13px', color: '#fc8181' }}>
          ❌ {error}
        </div>
      )}

      {/* Run result */}
      {runResult && (
        <div style={{ background: 'rgba(72,187,120,0.1)', border: '1px solid #68d391', borderRadius: '6px', padding: '12px 16px', marginTop: '12px', fontSize: '13px', color: '#68d391' }}>
          <strong>✅ Audit complete:</strong> {runResult.updated} rates updated, {runResult.unchanged} unchanged
          {runResult.errors > 0 && <span style={{ color: '#fc8181' }}>, {runResult.errors} errors</span>}
          <span style={{ color: '#a0aec0', marginLeft: '8px' }}>({(runResult.duration_seconds || 0).toFixed(1)}s)</span>
        </div>
      )}

      {/* Change history */}
      {expanded && (
        <div style={{ marginTop: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#a0aec0', marginBottom: '8px' }}>Recent Rate Changes</div>
          {history.length === 0 ? (
            <div style={{ color: '#718096', fontSize: '13px', textAlign: 'center', padding: '20px' }}>No rate changes recorded yet. Run the agent to check for updates.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #2d3748' }}>
                  {['Date', 'Category', 'Country', 'Old Rate', 'New Rate', 'Change', 'Source'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 10px', color: '#718096', fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((rec, i) => {
                  const delta = (rec.new_rate - rec.old_rate).toFixed(1);
                  const isUp = rec.new_rate > rec.old_rate;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <td style={{ padding: '8px 10px', color: '#a0aec0' }}>{rec.detected_at ? new Date(rec.detected_at).toLocaleDateString('en-GB') : '—'}</td>
                      <td style={{ padding: '8px 10px', color: '#e0e0e0' }}>{rec.category}</td>
                      <td style={{ padding: '8px 10px' }}><span style={{ background: '#2d3748', padding: '2px 6px', borderRadius: '4px', fontFamily: 'monospace' }}>{rec.country_code}</span></td>
                      <td style={{ padding: '8px 10px', color: '#a0aec0' }}>{rec.old_rate}%</td>
                      <td style={{ padding: '8px 10px', color: '#e0e0e0', fontWeight: 600 }}>{rec.new_rate}%</td>
                      <td style={{ padding: '8px 10px', color: isUp ? '#fc8181' : '#68d391', fontWeight: 700 }}>{isUp ? '▲' : '▼'} {Math.abs(delta)}%</td>
                      <td style={{ padding: '8px 10px', color: '#718096', fontSize: '11px' }}>{rec.source?.replace(/_/g, ' ')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================
// Currency Rates Manager Component
// ============================================================

// Currency group definitions for the selector dropdown
const CURRENCY_GROUPS = {
  all:    'All Currencies',
  major:  'Major (USD, EUR, GBP, JPY, CHF)',
  europe: 'European',
  apac:   'Asia-Pacific',
  americas: 'Americas',
  mena:   'Middle East & Africa',
  other:  'Other',
};

const CURRENCY_GROUP_MAP = {
  major:    ['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'NZD'],
  europe:   ['EUR', 'GBP', 'CHF', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'RON', 'BGN', 'HRK', 'ISK', 'RSD', 'MKD', 'ALL', 'BAM', 'MDL', 'UAH', 'GEL', 'AMD', 'AZN', 'BYN'],
  apac:     ['JPY', 'CNY', 'HKD', 'SGD', 'KRW', 'TWD', 'AUD', 'NZD', 'INR', 'IDR', 'MYR', 'PHP', 'THB', 'VND', 'BDT', 'PKR', 'LKR', 'NPR', 'MMK', 'KHR', 'LAK', 'MNT', 'FJD', 'PGK'],
  americas: ['USD', 'CAD', 'MXN', 'BRL', 'ARS', 'CLP', 'COP', 'PEN', 'UYU', 'BOB', 'PYG', 'VES', 'GTQ', 'HNL', 'NIO', 'CRC', 'PAB', 'DOP', 'CUP', 'JMD', 'TTD', 'BBD', 'BSD', 'BZD', 'GYD', 'SRD'],
  mena:     ['SAR', 'AED', 'QAR', 'KWD', 'BHD', 'OMR', 'JOD', 'ILS', 'EGP', 'MAD', 'TND', 'DZD', 'LYD', 'ZAR', 'NGN', 'KES', 'GHS', 'TZS', 'UGX', 'ETB', 'XOF', 'XAF', 'MZN', 'AOA', 'ZMW'],
};

const CURRENCY_NAMES = {
  USD:'US Dollar', EUR:'Euro', GBP:'British Pound', JPY:'Japanese Yen', CHF:'Swiss Franc',
  CAD:'Canadian Dollar', AUD:'Australian Dollar', NZD:'New Zealand Dollar', CNY:'Chinese Yuan',
  HKD:'Hong Kong Dollar', SGD:'Singapore Dollar', KRW:'South Korean Won', TWD:'New Taiwan Dollar',
  INR:'Indian Rupee', IDR:'Indonesian Rupiah', MYR:'Malaysian Ringgit', PHP:'Philippine Peso',
  THB:'Thai Baht', VND:'Vietnamese Dong', BDT:'Bangladeshi Taka', PKR:'Pakistani Rupee',
  SEK:'Swedish Krona', NOK:'Norwegian Krone', DKK:'Danish Krone', PLN:'Polish Zloty',
  CZK:'Czech Koruna', HUF:'Hungarian Forint', RON:'Romanian Leu', MXN:'Mexican Peso',
  BRL:'Brazilian Real', ARS:'Argentine Peso', CLP:'Chilean Peso', COP:'Colombian Peso',
  PEN:'Peruvian Sol', SAR:'Saudi Riyal', AED:'UAE Dirham', QAR:'Qatari Riyal',
  KWD:'Kuwaiti Dinar', BHD:'Bahraini Dinar', OMR:'Omani Rial', ILS:'Israeli Shekel',
  EGP:'Egyptian Pound', MAD:'Moroccan Dirham', ZAR:'South African Rand', NGN:'Nigerian Naira',
  KES:'Kenyan Shilling', TRY:'Turkish Lira', RUB:'Russian Ruble', UAH:'Ukrainian Hryvnia',
};

const formatRate = (rate) => {
  if (rate == null) return '—';
  if (rate >= 100) return rate.toFixed(2);
  if (rate >= 1) return rate.toFixed(4);
  return rate.toFixed(6);
};

const CurrencyRatesManager = ({ backendUrl, apiKey }) => {
  const PAGE_SIZE = 25;

  const [fxData, setFxData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [groupFilter, setGroupFilter] = React.useState('all');
  const [page, setPage] = React.useState(1);

  // Agent panel state
  const [agentRunning, setAgentRunning] = React.useState(false);
  const [agentResult, setAgentResult] = React.useState(null);
  const [agentError, setAgentError] = React.useState(null);
  const [showHistory, setShowHistory] = React.useState(false);
  const [history, setHistory] = React.useState([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);

  const fetchRates = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/duty-rate-agent/currency-rates`, {
        headers: { 'x-api-key': apiKey },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setFxData(data);
    } catch (err) {
      console.error('Currency rates fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, apiKey]);

  React.useEffect(() => { fetchRates(); }, [fetchRates]);

  const runAgent = async () => {
    setAgentRunning(true);
    setAgentResult(null);
    setAgentError(null);
    try {
      const res = await fetch(`${backendUrl}/api/duty-rate-agent/run-currency`, {
        method: 'POST',
        headers: { 'x-api-key': apiKey },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setAgentResult(data);
      await fetchRates(); // Refresh table after run
    } catch (err) {
      setAgentError(err.message);
    } finally {
      setAgentRunning(false);
    }
  };

  const loadHistory = async () => {
    if (showHistory) { setShowHistory(false); return; }
    setHistoryLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/duty-rate-agent/currency-history?limit=50`, {
        headers: { 'x-api-key': apiKey },
      });
      const data = await res.json();
      setHistory(data.history || []);
      setShowHistory(true);
    } catch (err) {
      console.error('Currency history error:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Build flat list of {code, rate} from fxData.rates
  const allRates = React.useMemo(() => {
    if (!fxData || !fxData.rates) return [];
    return Object.entries(fxData.rates)
      .map(([code, rate]) => ({ code, rate, name: CURRENCY_NAMES[code] || code }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [fxData]);

  const filtered = React.useMemo(() => {
    let list = allRates;
    if (groupFilter !== 'all') {
      const codes = CURRENCY_GROUP_MAP[groupFilter] || [];
      list = list.filter(r => codes.includes(r.code));
    }
    if (search.trim()) {
      const q = search.trim().toUpperCase();
      list = list.filter(r => r.code.includes(q) || r.name.toUpperCase().includes(q));
    }
    return list;
  }, [allRates, groupFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  React.useEffect(() => { setPage(1); }, [search, groupFilter]);

  const updatedAt = fxData?.updated_at ? new Date(fxData.updated_at) : null;
  const ageHours = updatedAt ? Math.round((Date.now() - updatedAt.getTime()) / 3600000) : null;
  const isStale = ageHours !== null && ageHours > 48;

  return (
    <div className="duty-rates-manager">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="section-header">
        <h2>💱 Currency Exchange Rates</h2>
        <p>
          {allRates.length} currencies tracked · Base: USD ·{' '}
          {updatedAt
            ? <span style={{ color: isStale ? '#e53e3e' : '#38a169' }}>
                Last updated {updatedAt.toLocaleString()}{isStale ? ' ⚠️ stale' : ''}
              </span>
            : 'Not yet synced'}
        </p>
        <div className="stats">
          <span className="stat">📡 open.er-api.com</span>
          <span className="stat">🔄 Daily Auto-Sync</span>
          <span className="stat">📊 {fxData?.rates_count || allRates.length} Currencies</span>
        </div>
      </div>

      {/* ── AI Agent Panel ─────────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
        borderRadius: '12px', padding: '20px', marginBottom: '24px', color: '#fff',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h3 style={{ margin: '0 0 6px', fontSize: '16px', color: '#90cdf4' }}>🤖 AI Currency Agent</h3>
            <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#a0aec0' }}>
              Fetches live rates from <strong style={{ color: '#90cdf4' }}>open.er-api.com</strong> (free, no API key).
              Logs any movement ≥ 0.5% to change history.
            </p>
            <p style={{ margin: 0, fontSize: '12px', color: '#718096' }}>
              Schedule: <strong style={{ color: '#e2e8f0' }}>Daily at 07:00 UTC</strong>
              {fxData?.next_update_utc && (
                <span> · Next API update: {fxData.next_update_utc}</span>
              )}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={runAgent}
              disabled={agentRunning}
              style={{
                padding: '10px 20px', borderRadius: '8px', border: 'none',
                background: agentRunning ? '#4a5568' : '#3182ce',
                color: '#fff', cursor: agentRunning ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: '14px',
              }}
            >
              {agentRunning ? '⏳ Syncing...' : '🔄 Sync Now'}
            </button>
            <button
              onClick={loadHistory}
              disabled={historyLoading}
              style={{
                padding: '10px 20px', borderRadius: '8px', border: '1px solid #4a5568',
                background: 'transparent', color: '#e2e8f0',
                cursor: historyLoading ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: '14px',
              }}
            >
              {historyLoading ? '⏳ Loading...' : showHistory ? '▲ Hide Changes' : '📋 View Changes'}
            </button>
          </div>
        </div>

        {/* Result / error banners */}
        {agentResult && (
          <div style={{ marginTop: '12px', padding: '10px 14px', borderRadius: '8px', background: '#276749', fontSize: '13px' }}>
            ✅ Sync complete — <strong>{agentResult.updated || 0}</strong> rates stored,{' '}
            <strong>{agentResult.significant_changes || 0}</strong> significant movements logged.
          </div>
        )}
        {agentError && (
          <div style={{ marginTop: '12px', padding: '10px 14px', borderRadius: '8px', background: '#742a2a', fontSize: '13px' }}>
            ❌ {agentError}
          </div>
        )}

        {/* Change history table */}
        {showHistory && (
          <div style={{ marginTop: '16px' }}>
            <h4 style={{ margin: '0 0 10px', fontSize: '14px', color: '#90cdf4' }}>Recent FX Movements (≥ 0.5%)</h4>
            {history.length === 0 ? (
              <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>No significant movements recorded yet.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #4a5568' }}>
                      {['Currency', 'Old Rate', 'New Rate', 'Change %', 'Detected'].map(h => (
                        <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: '#a0aec0', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {history.slice(0, 20).map((h, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #2d3748' }}>
                        <td style={{ padding: '6px 10px', fontWeight: 700 }}>{h.currency_code}</td>
                        <td style={{ padding: '6px 10px', color: '#a0aec0' }}>{formatRate(h.old_rate)}</td>
                        <td style={{ padding: '6px 10px' }}>{formatRate(h.new_rate)}</td>
                        <td style={{ padding: '6px 10px', color: h.change_pct > 0 ? '#fc8181' : '#68d391', fontWeight: 600 }}>
                          {h.change_pct > 0 ? '▲' : '▼'} {Math.abs(h.change_pct).toFixed(3)}%
                        </td>
                        <td style={{ padding: '6px 10px', color: '#718096' }}>
                          {h.detected_at ? new Date(h.detected_at).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Search + Group Filter ──────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 220px', minWidth: '180px' }}>
          <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#718096', pointerEvents: 'none' }}>🔍</span>
          <input
            type="text"
            placeholder="Search currency code or name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px 8px 32px', borderRadius: '6px',
              border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box',
              background: '#fff', color: '#2d3748',
            }}
          />
        </div>
        <select
          value={groupFilter}
          onChange={e => setGroupFilter(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0',
            fontSize: '14px', background: '#fff', color: '#2d3748', cursor: 'pointer',
            minWidth: '200px',
          }}
        >
          {Object.entries(CURRENCY_GROUPS).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
        <span style={{ fontSize: '13px', color: '#718096', whiteSpace: 'nowrap' }}>
          {filtered.length} result{filtered.length !== 1 ? 's' : ''}
        </span>
        {(search || groupFilter !== 'all') && (
          <button
            onClick={() => { setSearch(''); setGroupFilter('all'); }}
            style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', color: '#718096', cursor: 'pointer', fontSize: '13px' }}
          >× Clear</button>
        )}
      </div>

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>Loading exchange rates...</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Currency Name</th>
                <th>Rate (vs USD)</th>
                <th>Inverse (USD per 1 unit)</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '32px', color: '#718096' }}>
                    No currencies match your search.
                  </td>
                </tr>
              ) : paginated.map((r) => (
                <tr key={r.code}>
                  <td><code className="hs-code" style={{ fontSize: '14px', fontWeight: 700 }}>{r.code}</code></td>
                  <td>{r.name}</td>
                  <td>
                    <span style={{ fontWeight: 600, color: '#2d3748' }}>{formatRate(r.rate)}</span>
                    <span style={{ fontSize: '11px', color: '#a0aec0', marginLeft: '4px' }}>{r.code}/USD</span>
                  </td>
                  <td style={{ color: '#718096', fontSize: '13px' }}>
                    {r.rate > 0 ? formatRate(1 / r.rate) : '—'}
                    <span style={{ fontSize: '11px', color: '#a0aec0', marginLeft: '4px' }}>USD/{r.code}</span>
                  </td>
                  <td>
                    <span className="source-badge">{fxData?.provider || 'open.er-api.com'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ────────────────────────────────────────────────────── */}
      {!loading && totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
          <button onClick={() => setPage(1)} disabled={currentPage === 1}
            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #e2e8f0', background: currentPage === 1 ? '#f7fafc' : '#fff', cursor: currentPage === 1 ? 'default' : 'pointer', color: '#4a5568', fontSize: '13px' }}>«</button>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
            style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #e2e8f0', background: currentPage === 1 ? '#f7fafc' : '#fff', cursor: currentPage === 1 ? 'default' : 'pointer', color: '#4a5568', fontSize: '13px' }}>‹ Prev</button>
          {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
            let p;
            if (totalPages <= 7) p = i + 1;
            else if (currentPage <= 4) p = i + 1;
            else if (currentPage >= totalPages - 3) p = totalPages - 6 + i;
            else p = currentPage - 3 + i;
            return (
              <button key={p} onClick={() => setPage(p)}
                style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #e2e8f0', background: p === currentPage ? '#3182ce' : '#fff', color: p === currentPage ? '#fff' : '#4a5568', cursor: 'pointer', fontSize: '13px', fontWeight: p === currentPage ? 700 : 400 }}>{p}</button>
            );
          })}
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
            style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #e2e8f0', background: currentPage === totalPages ? '#f7fafc' : '#fff', cursor: currentPage === totalPages ? 'default' : 'pointer', color: '#4a5568', fontSize: '13px' }}>Next ›</button>
          <button onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}
            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #e2e8f0', background: currentPage === totalPages ? '#f7fafc' : '#fff', cursor: currentPage === totalPages ? 'default' : 'pointer', color: '#4a5568', fontSize: '13px' }}>»</button>
          <span style={{ fontSize: '13px', color: '#718096', marginLeft: '8px' }}>
            Page {currentPage} of {totalPages} ({filtered.length} currencies)
          </span>
        </div>
      )}
    </div>
  );
};

// Enhanced Duty Rates Manager Component
const DutyRatesManager = ({ dutyRates, categories, loading = false, onReload, backendUrl, apiKey, shop }) => {
  // ── View mode: 'hs' (HS-code grouped) | 'global' (legacy flat list) ──────
  const [viewMode, setViewMode] = React.useState('hs');

  // ── HS-code grouped view state ──────────────────────────────────────────
  const [hsData, setHsData] = React.useState(null);  // null = not yet loaded
  const [hsLoading, setHsLoading] = React.useState(false);
  const [hsError, setHsError] = React.useState(null);
  const [expandedHs, setExpandedHs] = React.useState({});
  const [hsSearch, setHsSearch] = React.useState('');
  const [hsCountryFilter, setHsCountryFilter] = React.useState('all');

  // Load HS summary when HS view is first shown
  React.useEffect(() => {
    if (viewMode === 'hs' && hsData === null && shop && backendUrl && apiKey) {
      loadHsSummary();
    }
  }, [viewMode, shop]);

  const loadHsSummary = async () => {
    setHsLoading(true);
    setHsError(null);
    try {
      const resp = await fetch(`${backendUrl}/api/duty-rates/hs-summary?shop=${encodeURIComponent(shop)}`, {
        headers: { 'x-api-key': apiKey },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setHsData(Array.isArray(data) ? data : []);
    } catch (e) {
      setHsError(e.message);
    } finally {
      setHsLoading(false);
    }
  };

  const toggleExpand = (hs) => setExpandedHs(prev => ({ ...prev, [hs]: !prev[hs] }));

  const allHsCountries = React.useMemo(() => {
    if (!hsData) return [];
    const codes = new Set();
    hsData.forEach(h => {
      (h.target_countries || []).forEach(code => code && codes.add(code));
      (h.rates || []).forEach(rate => rate.country_code && codes.add(rate.country_code));
    });
    return [...codes].sort();
  }, [hsData]);

  const filteredHs = React.useMemo(() => {
    if (!hsData) return [];
    let list = hsData;
    if (hsSearch.trim()) {
      const q = hsSearch.trim().toLowerCase();
      list = list.filter(h =>
        h.hs_code.toLowerCase().includes(q) ||
        (h.description || '').toLowerCase().includes(q) ||
        (h.category || '').toLowerCase().includes(q)
      );
    }
    if (hsCountryFilter !== 'all') {
      list = list.filter(h =>
        (h.target_countries || []).includes(hsCountryFilter) ||
        (h.rates || []).some(r => r.country_code === hsCountryFilter)
      );
    }
    return list;
  }, [hsData, hsSearch, hsCountryFilter]);

  const getSourceStyle = (source) => {
    if (!source) return { background: '#f7fafc', color: '#718096', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '2px 6px', fontSize: '11px' };
    if (source.includes('uk_trade') || source.includes('usitc') || source.includes('official')) return { background: '#ebf8ff', color: '#2b6cb0', border: '1px solid #bee3f8', borderRadius: '4px', padding: '2px 6px', fontSize: '11px' };
    if (source.includes('ai') || source.includes('llm')) return { background: '#f0fff4', color: '#276749', border: '1px solid #c6f6d5', borderRadius: '4px', padding: '2px 6px', fontSize: '11px' };
    return { background: '#f7fafc', color: '#718096', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '2px 6px', fontSize: '11px' };
  };

  // ── Legacy global view state ─────────────────────────────────────────────
  const PAGE_SIZE = 25;
  const [search, setSearch] = React.useState('');
  const [countryFilter, setCountryFilter] = React.useState('all');
  const [categoryFilter, setCategoryFilter] = React.useState('all');
  const [page, setPage] = React.useState(1);

  const uniqueCountries = React.useMemo(() => {
    if (!dutyRates) return [];
    return [...new Set(dutyRates.map(r => r.country_code).filter(Boolean))].sort();
  }, [dutyRates]);

  const uniqueCategories = React.useMemo(() => {
    if (!dutyRates) return [];
    return [...new Set(dutyRates.map(r => r.category).filter(Boolean))].sort();
  }, [dutyRates]);

  const filtered = React.useMemo(() => {
    let list = dutyRates || [];
    if (countryFilter !== 'all') list = list.filter(r => r.country_code === countryFilter);
    if (categoryFilter !== 'all') list = list.filter(r => r.category === categoryFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(r =>
        (r.country_code || '').toLowerCase().includes(q) ||
        (r.category || '').toLowerCase().includes(q) ||
        (r.hs_code || '').toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [dutyRates, search, countryFilter, categoryFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  React.useEffect(() => { setPage(1); }, [search, countryFilter, categoryFilter]);

  return (
    <div className="duty-rates-manager">
      {/* ── Header ───────────────────────────────────────────────────── */}
      <div className="section-header">
        <h2>📦 Duty Rates</h2>
        <p>Import duty rates maintained by the AI agent — updated weekly from official tariff sources</p>
        <div className="stats">
          <span className="stat">🎯 HS-Code Precision</span>
          <span className="stat">🏛️ Official Sources</span>
          <span className="stat">🔄 AI-Maintained</span>
          {onReload && (
            <button onClick={onReload} className="btn btn-secondary btn-sm">🔄 Refresh</button>
          )}
        </div>
      </div>

      {/* ── View toggle ──────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        <button
          onClick={() => setViewMode('hs')}
          style={{
            padding: '7px 18px', borderRadius: '6px', fontSize: '13px', fontWeight: viewMode === 'hs' ? 700 : 400,
            background: viewMode === 'hs' ? '#3182ce' : '#fff',
            color: viewMode === 'hs' ? '#fff' : '#4a5568',
            border: '1px solid ' + (viewMode === 'hs' ? '#3182ce' : '#e2e8f0'),
            cursor: 'pointer',
          }}
        >🏷️ By HS Code</button>
        <button
          onClick={() => setViewMode('global')}
          style={{
            padding: '7px 18px', borderRadius: '6px', fontSize: '13px', fontWeight: viewMode === 'global' ? 700 : 400,
            background: viewMode === 'global' ? '#3182ce' : '#fff',
            color: viewMode === 'global' ? '#fff' : '#4a5568',
            border: '1px solid ' + (viewMode === 'global' ? '#3182ce' : '#e2e8f0'),
            cursor: 'pointer',
          }}
        >🌍 Global Rate Library</button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
           HS-CODE GROUPED VIEW
      ═══════════════════════════════════════════════════════════════ */}
      {viewMode === 'hs' && (
        <div>
          {/* Filters */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: '1 1 220px', minWidth: '180px' }}>
              <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#718096', pointerEvents: 'none' }}>🔍</span>
              <input
                type="text" placeholder="Search HS code, description..."
                value={hsSearch} onChange={e => setHsSearch(e.target.value)}
                style={{ width: '100%', padding: '8px 12px 8px 32px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
            <select value={hsCountryFilter} onChange={e => setHsCountryFilter(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '14px', minWidth: '140px' }}>
              <option value="all">🌍 All Countries</option>
              {allHsCountries.map(code => <option key={code} value={code}>{getCountryFlag(code)} {code}</option>)}
            </select>
            {(hsSearch || hsCountryFilter !== 'all') && (
              <button onClick={() => { setHsSearch(''); setHsCountryFilter('all'); }}
                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', color: '#718096', cursor: 'pointer', fontSize: '13px' }}>× Clear</button>
            )}
            <button onClick={() => { setHsData(null); loadHsSummary(); }}
              style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', color: '#4a5568', cursor: 'pointer', fontSize: '13px' }}>🔄 Refresh</button>
          </div>

          {/* Loading */}
          {hsLoading && <LoadingSpinner text="Loading HS code rates..." />}

          {/* Error */}
          {hsError && !hsLoading && (
            <div style={{ background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: '8px', padding: '16px', marginBottom: '16px', color: '#c53030' }}>
              ⚠️ Could not load HS code rates: {hsError}
              <button onClick={loadHsSummary} style={{ marginLeft: '12px', padding: '4px 10px', borderRadius: '4px', border: '1px solid #c53030', background: '#fff', color: '#c53030', cursor: 'pointer', fontSize: '12px' }}>Retry</button>
            </div>
          )}

          {/* Empty state — no HS codes assigned yet */}
          {!hsLoading && !hsError && hsData && hsData.length === 0 && (
            <div style={{ background: '#fffbeb', border: '1px solid #f6e05e', borderRadius: '8px', padding: '24px', textAlign: 'center', color: '#744210' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>🏷️</div>
              <strong>No HS codes assigned yet</strong>
              <p style={{ margin: '8px 0 0', fontSize: '14px' }}>Assign HS codes to your products in the <strong>HS Codes</strong> tab to see per-destination duty rates here.</p>
            </div>
          )}

          {/* HS code cards */}
          {!hsLoading && !hsError && filteredHs.length > 0 && (
            <div>
              <div style={{ fontSize: '13px', color: '#718096', marginBottom: '12px' }}>
                {filteredHs.length} HS code{filteredHs.length !== 1 ? 's' : ''} in use
              </div>
              {filteredHs.map(hs => {
                const isOpen = !!expandedHs[hs.hs_code];
                const visibleRates = hsCountryFilter !== 'all'
                  ? (hs.rates || []).filter(r => r.country_code === hsCountryFilter)
                  : (hs.rates || []);
                return (
                  <div key={hs.hs_code} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '10px', overflow: 'hidden' }}>
                    {/* Card header — click to expand */}
                    <div
                      onClick={() => toggleExpand(hs.hs_code)}
                      style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: '#f7fafc', cursor: 'pointer', userSelect: 'none' }}
                    >
                      <span style={{ fontSize: '16px' }}>{isOpen ? '▾' : '▸'}</span>
                      <code style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '15px', color: '#2d3748', background: '#e2e8f0', padding: '2px 8px', borderRadius: '4px' }}>{hs.hs_code}</code>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, color: '#2d3748', fontSize: '14px' }}>{hs.description || 'No description'}</div>
                        {hs.category && <div style={{ fontSize: '12px', color: '#718096' }}>{hs.category}</div>}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                        <span style={{ fontSize: '12px', color: '#718096' }}>
                          {hs.covered_count ?? (hs.rates || []).length}/{hs.target_count || '—'} exact destinations
                        </span>
                        {hs.has_pending && <span style={{ background: '#fffbeb', color: '#744210', border: '1px solid #f6e05e', borderRadius: '4px', padding: '2px 6px', fontSize: '11px' }}>⏳ Updating</span>}
                        {hs.population_status === 'failed' && <span style={{ background: '#fff5f5', color: '#c53030', border: '1px solid #fed7d7', borderRadius: '4px', padding: '2px 6px', fontSize: '11px' }}>Needs retry</span>}
                      </div>
                    </div>
                    {/* Expanded rates table */}
                    {isOpen && (
                      <div style={{ padding: '0 16px 12px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                              <th style={{ textAlign: 'left', padding: '6px 8px', color: '#718096', fontWeight: 600 }}>Destination</th>
                              <th style={{ textAlign: 'right', padding: '6px 8px', color: '#718096', fontWeight: 600 }}>Duty Rate</th>
                              <th style={{ textAlign: 'right', padding: '6px 8px', color: '#718096', fontWeight: 600 }}>Source</th>
                              <th style={{ textAlign: 'right', padding: '6px 8px', color: '#718096', fontWeight: 600 }}>Updated</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibleRates.length === 0 ? (
                              <tr><td colSpan={4} style={{ padding: '12px 8px', color: '#718096', textAlign: 'center' }}>
                                {hs.has_pending
                                  ? 'Rates are being populated for this HS code. Refresh shortly to see completed destinations.'
                                  : hs.population_status === 'failed'
                                    ? 'Rate population needs another retry. The app will retry automatically.'
                                    : 'No rates for selected country.'}
                              </td></tr>
                            ) : visibleRates.map((r, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid #f0f4f8' }}>
                                <td style={{ padding: '7px 8px' }}>
                                  <span style={{ marginRight: '6px' }}>{getCountryFlag(r.country_code)}</span>
                                  <strong>{r.country_code}</strong>
                                  {r.country_name && <span style={{ color: '#718096', marginLeft: '6px', fontSize: '12px' }}>{r.country_name}</span>}
                                </td>
                                <td style={{ padding: '7px 8px', textAlign: 'right' }}>
                                  {r.duty_rate != null
                                    ? <strong style={{ color: '#2d3748' }}>{r.duty_rate}%</strong>
                                    : <span style={{ color: '#e53e3e', fontSize: '12px' }}>⏳ Pending</span>}
                                </td>
                                <td style={{ padding: '7px 8px', textAlign: 'right' }}>
                                  <span style={getSourceStyle(r.source)}>{r.source || '—'}</span>
                                  {r.rate_scope === 'chapter_fallback' && <span style={{ marginLeft: '5px', color: '#718096', fontSize: '11px' }}>chapter fallback</span>}
                                </td>
                                <td style={{ padding: '7px 8px', textAlign: 'right', color: '#718096', fontSize: '12px' }}>
                                  {r.last_verified_at ? new Date(r.last_verified_at).toLocaleDateString() : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
           GLOBAL RATE LIBRARY VIEW (legacy flat list)
      ═══════════════════════════════════════════════════════════════ */}
      {viewMode === 'global' && (
        <div>

      {/* ── Search + Filters ─────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: '1 1 220px', minWidth: '180px' }}>
          <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#718096', pointerEvents: 'none' }}>🔍</span>
          <input
            type="text"
            placeholder="Search country, category, HS code..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px 8px 32px', borderRadius: '6px',
              border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box',
              background: '#fff', color: '#2d3748',
            }}
          />
        </div>
        {/* Country filter */}
        <select
          value={countryFilter}
          onChange={e => setCountryFilter(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0',
            fontSize: '14px', background: '#fff', color: '#2d3748', cursor: 'pointer',
            minWidth: '140px',
          }}
        >
          <option value="all">🌍 All Countries</option>
          {uniqueCountries.map(code => (
            <option key={code} value={code}>{getCountryFlag(code)} {code}</option>
          ))}
        </select>
        {/* Category filter */}
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0',
            fontSize: '14px', background: '#fff', color: '#2d3748', cursor: 'pointer',
            minWidth: '160px',
          }}
        >
          <option value="all">📦 All Categories</option>
          {uniqueCategories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        {/* Result count */}
        <span style={{ fontSize: '13px', color: '#718096', whiteSpace: 'nowrap' }}>
          {filtered.length} result{filtered.length !== 1 ? 's' : ''}
        </span>
        {/* Clear filters */}
        {(search || countryFilter !== 'all' || categoryFilter !== 'all') && (
          <button
            onClick={() => { setSearch(''); setCountryFilter('all'); setCategoryFilter('all'); }}
            style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', color: '#718096', cursor: 'pointer', fontSize: '13px' }}
          >× Clear</button>
        )}
      </div>

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Country</th>
              <th>Category</th>
              <th>HS Code</th>
              <th>Duty Rate</th>
              <th>Description</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '32px', color: '#718096' }}>
                  No duty rates match your search.
                </td>
              </tr>
            ) : paginated.map((rate, index) => (
              <tr key={rate.id || index}>
                <td>
                  <div className="country-cell">
                    <span className="country-flag">{getCountryFlag(rate.country_code)}</span>
                    <span>{rate.country_code}</span>
                  </div>
                </td>
                <td><span className="category-name">{rate.category}</span></td>
                <td><code className="hs-code">{rate.hs_code}</code></td>
                <td><span className="duty-rate">{rate.duty_rate}%</span></td>
                <td className="description">{rate.description}</td>
                <td>
                  <span className="source-badge">{rate.source}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
          <button onClick={() => setPage(1)} disabled={currentPage === 1}
            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #e2e8f0', background: currentPage === 1 ? '#f7fafc' : '#fff', cursor: currentPage === 1 ? 'default' : 'pointer', color: '#4a5568', fontSize: '13px' }}>«</button>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
            style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #e2e8f0', background: currentPage === 1 ? '#f7fafc' : '#fff', cursor: currentPage === 1 ? 'default' : 'pointer', color: '#4a5568', fontSize: '13px' }}>‹ Prev</button>
          {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
            let p;
            if (totalPages <= 7) p = i + 1;
            else if (currentPage <= 4) p = i + 1;
            else if (currentPage >= totalPages - 3) p = totalPages - 6 + i;
            else p = currentPage - 3 + i;
            return (
              <button key={p} onClick={() => setPage(p)}
                style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #e2e8f0', background: p === currentPage ? '#3182ce' : '#fff', color: p === currentPage ? '#fff' : '#4a5568', cursor: 'pointer', fontSize: '13px', fontWeight: p === currentPage ? 700 : 400 }}>{p}</button>
            );
          })}
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
            style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #e2e8f0', background: currentPage === totalPages ? '#f7fafc' : '#fff', cursor: currentPage === totalPages ? 'default' : 'pointer', color: '#4a5568', fontSize: '13px' }}>Next ›</button>
          <button onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}
            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #e2e8f0', background: currentPage === totalPages ? '#f7fafc' : '#fff', cursor: currentPage === totalPages ? 'default' : 'pointer', color: '#4a5568', fontSize: '13px' }}>»</button>
          <span style={{ fontSize: '13px', color: '#718096', marginLeft: '8px' }}>
            Page {currentPage} of {totalPages} ({filtered.length} rates)
          </span>
        </div>
      )}
        </div>
      )}
    </div>
  );
};


// Enhanced Categories Manager Component
const CategoriesManager = ({ categories, loading = false, onReload }) => {
  const PAGE_SIZE = 25;

  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(1);

  // Filter + search
  const filtered = React.useMemo(() => {
    let list = categories || [];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.hs_code || '').toLowerCase().includes(q) ||
        (c.description || '').toLowerCase().includes(q) ||
        (c.keywords || []).some(k => k.toLowerCase().includes(q))
      );
    }
    return list;
  }, [categories, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  React.useEffect(() => { setPage(1); }, [search]);

  if (loading) {
    return (
      <div className="categories-manager">
        <div className="section-header">
          <h2>📋 Product Categories</h2>
          <LoadingSpinner text="Loading categories..." />
        </div>
      </div>
    );
  }

  if (!categories || categories.length === 0) {
    return (
      <div className="categories-manager">
        <div className="section-header">
          <h2>📋 Product Categories</h2>
          <EmptyState
            title="No Categories Available"
            description="Product categories data could not be loaded. This may be due to a connection issue or server maintenance."
            icon="🏷️"
            action={
              onReload && (
                <button onClick={onReload} className="btn btn-primary">
                  🔄 Reload Categories
                </button>
              )
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="categories-manager">
      <div className="section-header">
        <h2>📋 Product Categories</h2>
        <p>Comprehensive HS code classification for {categories.length} product types</p>
        <div className="stats">
          <span className="stat">📦 {categories.length} Categories</span>
          <span className="stat">🔢 HS Codes</span>
          <span className="stat">🎯 Smart Matching</span>
          {onReload && (
            <button onClick={onReload} className="btn btn-secondary btn-sm">
              🔄 Refresh
            </button>
          )}
        </div>
      </div>

      {/* ── Search ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 260px', minWidth: '200px' }}>
          <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#718096', pointerEvents: 'none' }}>🔍</span>
          <input
            type="text"
            placeholder="Search category name, HS code, keyword..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px 8px 32px', borderRadius: '6px',
              border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box',
              background: '#fff', color: '#2d3748',
            }}
          />
        </div>
        <span style={{ fontSize: '13px', color: '#718096', whiteSpace: 'nowrap' }}>
          {filtered.length} result{filtered.length !== 1 ? 's' : ''}
        </span>
        {search && (
          <button
            onClick={() => setSearch('')}
            style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', color: '#718096', cursor: 'pointer', fontSize: '13px' }}
          >× Clear</button>
        )}
      </div>

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Category</th>
              <th>HS Code</th>
              <th>Description</th>
              <th>Keywords</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', padding: '32px', color: '#718096' }}>
                  No categories match your search.
                </td>
              </tr>
            ) : paginated.map((category, index) => (
              <tr key={category.id || index}>
                <td><strong>{category.name}</strong></td>
                <td><code className="hs-code">{category.hs_code}</code></td>
                <td className="description">{category.description}</td>
                <td className="keywords">
                  {category.keywords?.slice(0, 3).map(keyword => (
                    <span key={keyword} className="keyword-tag">{keyword}</span>
                  ))}
                  {category.keywords?.length > 3 && <span className="keyword-more">+{category.keywords.length - 3} more</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
          <button onClick={() => setPage(1)} disabled={currentPage === 1}
            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #e2e8f0', background: currentPage === 1 ? '#f7fafc' : '#fff', cursor: currentPage === 1 ? 'default' : 'pointer', color: '#4a5568', fontSize: '13px' }}>«</button>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
            style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #e2e8f0', background: currentPage === 1 ? '#f7fafc' : '#fff', cursor: currentPage === 1 ? 'default' : 'pointer', color: '#4a5568', fontSize: '13px' }}>‹ Prev</button>
          {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
            let p;
            if (totalPages <= 7) p = i + 1;
            else if (currentPage <= 4) p = i + 1;
            else if (currentPage >= totalPages - 3) p = totalPages - 6 + i;
            else p = currentPage - 3 + i;
            return (
              <button key={p} onClick={() => setPage(p)}
                style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #e2e8f0', background: p === currentPage ? '#3182ce' : '#fff', color: p === currentPage ? '#fff' : '#4a5568', cursor: 'pointer', fontSize: '13px', fontWeight: p === currentPage ? 700 : 400 }}>{p}</button>
            );
          })}
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
            style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #e2e8f0', background: currentPage === totalPages ? '#f7fafc' : '#fff', cursor: currentPage === totalPages ? 'default' : 'pointer', color: '#4a5568', fontSize: '13px' }}>Next ›</button>
          <button onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}
            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #e2e8f0', background: currentPage === totalPages ? '#f7fafc' : '#fff', cursor: currentPage === totalPages ? 'default' : 'pointer', color: '#4a5568', fontSize: '13px' }}>»</button>
          <span style={{ fontSize: '13px', color: '#718096', marginLeft: '8px' }}>
            Page {currentPage} of {totalPages} ({filtered.length} categories)
          </span>
        </div>
      )}
    </div>
  );
};

// Safe theme setup links: onboarding only presents unpublished themes for first
// proof, preventing an accidental change to a merchant's live storefront.
const SetupLink = ({ linkType, shop, appHandle, className = "setup-link-primary" }) => {
  const [testThemes, setTestThemes] = useState([]);
  const [themeId, setThemeId] = useState('');
  const [themeError, setThemeError] = useState(false);

  useEffect(() => {
    if (!shop) return;
    api.get(`/setup/test-themes?shop=${encodeURIComponent(shop)}`)
      .then(res => {
        const themes = res.data?.themes || [];
        if (themes.length) {
          setTestThemes(themes);
          setThemeId(String(themes[0].theme_id));
        } else {
          setThemeError(true);
        }
      })
      .catch(() => setThemeError(true));
  }, [shop]);

  if (!shop || !appHandle) return null;

  const storeSlug = shop.replace('.myshopify.com', '');
  const adminBase = `https://admin.shopify.com/store/${storeSlug}`;

  const links = {
    embed: {
      url: `${adminBase}/themes/${themeId}/editor?context=apps`,
      label: '🔧 Open duplicate theme: App embeds',
      description: 'Opens the selected unpublished theme’s App embeds section'
    },
    cart: {
      url: `${adminBase}/themes/${themeId}/editor?template=cart&previewPath=%2Fcart`,
      label: '🛒 Open duplicate Cart template',
      description: 'Opens the selected unpublished theme’s Cart template'
    },
    product: {
      url: `${adminBase}/themes/${themeId}/editor?template=product`,
      label: '★ Open duplicate Product template',
      description: 'Opens the selected unpublished theme’s Product template'
    }
  };

  const link = links[linkType];
  if (!link) return null;

  if (!themeId) {
    return (
      <div className="setup-link-group">
        <a href={`${adminBase}/themes`} target="_top" rel="noopener noreferrer" className={className}>Open Themes and duplicate your live theme</a>
        <div className="setup-link-description">No unpublished theme was found. Create a duplicate in Shopify first, then return here to test it safely.</div>
      </div>
    );
  }

  return (
    <div className="setup-link-group">
      <label className="setup-link-theme-label" htmlFor={`tdk-test-theme-${linkType}`}>Test theme (unpublished)</label>
      <select id={`tdk-test-theme-${linkType}`} value={themeId} onChange={(event) => setThemeId(event.target.value)} className="setup-link-theme-select">
        {testThemes.map((theme) => <option key={theme.theme_id} value={theme.theme_id}>{theme.theme_name}</option>)}
      </select>
      <a
        href={link.url}
        target="_top"
        rel="noopener noreferrer"
        className={className}
      >
        {link.label}
      </a>
      <div className="setup-link-description">
        {link.description}
        {themeError && (
          <span style={{ color: '#e53e3e', marginLeft: 6, fontSize: '0.85em' }}>
            (could not load duplicate themes — no live theme link will be shown)
          </span>
        )}
      </div>
    </div>
  );
};

// Storefront setup video: detailed guidance for product page, cart page, and cart drawer placement.
const SetupHelpVideo = ({ variant = 'settings' }) => {
  const iframeRef = useRef(null);

  const handleFullscreen = () => {
    const iframe = iframeRef.current;
    if (iframe) {
      if (iframe.requestFullscreen) {
        iframe.requestFullscreen();
      } else if (iframe.mozRequestFullScreen) { // Firefox
        iframe.mozRequestFullScreen();
      } else if (iframe.webkitRequestFullscreen) { // Chrome, Safari, Opera
        iframe.webkitRequestFullscreen();
      } else if (iframe.msRequestFullscreen) { // IE/Edge
        iframe.msRequestFullscreen();
      }
    }
  };

  return (
    <div className={`setup-help-video setup-help-video--${variant}`}>
      <h4>📹 Storefront setup walkthrough</h4>
      <div
        className="video-container"
        style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}
      >
        <iframe
          ref={iframeRef}
          src="https://www.youtube.com/embed/GRm94g3ANM8?si=MDhvccVgunM62hcp"
          title="Tax and Duty King storefront setup walkthrough"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture, allowfullscreen"
          allowFullScreen
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            borderRadius: "8px",
            backgroundColor: "#f0f0f0"
          }}
        ></iframe>
      </div>
      <p className="video-caption">
        Follow the full walkthrough for product-page, cart-page, and cart-drawer setup before you make any storefront changes live.
      </p>
    </div>
  );
};


// DeepLink Button Component with App Bridge and robust fallbacks
// Dedicated Setup Page Component for First-Time Users
const SetupPage = ({ onComplete }) => {
  const getShop = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('shop') || '';
  };

  return (
    <div className="setup-page">
      <div className="setup-container">
        <div className="setup-header">
          <h1>🚀 Welcome to Tax & Duty King!</h1>
          <p>Complete these 3 simple steps to get your tax calculator running:</p>
        </div>
        
        <div className="setup-content">
          <SettingsManager 
            stores={[]} 
            onReload={() => {}} 
            shippingOrigins={{}}
            onUpdateShippingOrigin={() => {}}
            storeStateData={{}}
          />
        </div>
        
        <div className="setup-footer">
          <button 
            className="btn btn-secondary"
            onClick={onComplete}
          >
            Continue to Dashboard
          </button>
          <p className="setup-help">
            Need help? Message me on Whats App +44 7736796742 or email us directy at contact@aspenconsulting.co.uk
          </p>
        </div>
      </div>
    </div>
  );
};

// Settings Manager: configuration controls and durable storefront-learning resources.
const SettingsManager = ({ stores, onReload, shippingOrigins, onUpdateShippingOrigin, storeStateData, onUpgrade }) => {
  const [setupStatus, setSetupStatus] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [iossSettings, setIossSettings] = useState({
    ioss_panel_visible: false,
    ioss_feature_enabled: false,
    ioss_upgrade_required: false,
    ioss_enabled: false,
    ioss_registered: false,
  });
  const [iossLoading, setIossLoading] = useState(false);

  // Domestic suppression toggle
  const [hideForDomestic, setHideForDomestic] = useState(false);
  const [hideForDomesticLoading, setHideForDomesticLoading] = useState(false);
  
  // Country-level DDP state
  const [ddpSettings, setDdpSettings] = useState({
    ddp_panel_visible: false,
    ddp_feature_enabled: false,
    ddp_upgrade_required: false,
    ddp_enabled: false,
    ddp_countries: [],
    ddp_behavior: 'show_zero_due',
    is_multi_origin: false,
    ddp_fee_collection_entitled: false,
    ddp_fee_collection_enabled: false,
    ddp_fee_collection_configured: false,
    ddp_fee_variant_id: null,
    ddp_fee_variant_gid: null
  });
  const [ddpLoading, setDdpLoading] = useState(false);

  // Get current shop from URL params
  const getShop = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('shop') || '';
  };

  const getStoreHandle = () => {
    const shop = getShop();
    return shop ? shop.replace('.myshopify.com', '') : '';
  };

  // App configuration
    const APP_HANDLE = process.env.REACT_APP_SHOPIFY_APP_HANDLE || 'vat-duty-king';
  const APP_ID = process.env.REACT_APP_SHOPIFY_APP_ID || APP_HANDLE;

  // A few Railway-edge TLS connections intermittently fail before a response is
  // received. Store-setting writes are idempotent (they set an explicit boolean),
  // so safely retry network-only failures rather than making the merchant toggle
  // the setting again. HTTP responses (for example a plan-gate 403) are surfaced
  // immediately and are never retried.
  const patchStoreSettingsWithRetry = async (shop, payload, maxAttempts = 3) => {
    let lastError;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        return await api.patch(`/store-settings/${encodeURIComponent(shop)}`, payload);
      } catch (error) {
        lastError = error;
        const isNetworkError = !error.response;
        if (!isNetworkError || attempt === maxAttempts) throw error;
        const waitMs = 500 * attempt;
        console.warn(`[IOSS] Save attempt ${attempt} failed before a response; retrying in ${waitMs}ms.`, error.message);
        await new Promise(resolve => setTimeout(resolve, waitMs));
      }
    }
    throw lastError;
  };

  // Fetch IOSS settings
  const fetchIossSettings = async () => {
    const shop = getShop();
    if (!shop) return;

    try {
      const response = await api.get(`/store-settings/${encodeURIComponent(shop)}`);
      setIossSettings({
        ioss_panel_visible: response.data.ioss_panel_visible || false,
        ioss_feature_enabled: response.data.ioss_feature_enabled || false,
        ioss_upgrade_required: response.data.ioss_upgrade_required || false,
        ioss_enabled: response.data.ioss_enabled || false,
        ioss_registered: response.data.ioss_registered || false,
      });
      // Also fetch DDP settings from same response
      setDdpSettings({
        ddp_panel_visible: response.data.ddp_panel_visible || false,
        ddp_feature_enabled: response.data.ddp_feature_enabled || false,
        ddp_upgrade_required: response.data.ddp_upgrade_required || false,
        ddp_enabled: response.data.ddp_enabled || false,
        ddp_countries: response.data.ddp_countries || [],
        ddp_behavior: response.data.ddp_behavior || 'show_zero_due',
        is_multi_origin: response.data.is_multi_origin || false,
        ddp_fee_collection_entitled: response.data.ddp_fee_collection_entitled || false,
        ddp_fee_collection_enabled: response.data.ddp_fee_collection_enabled || false,
        ddp_fee_collection_configured: response.data.ddp_fee_collection_configured || false,
        ddp_fee_variant_id: response.data.ddp_fee_variant_id || null,
        ddp_fee_variant_gid: response.data.ddp_fee_variant_gid || null
      });
      // Domestic suppression
      setHideForDomestic(response.data.hide_for_domestic || false);
    } catch (error) {
      console.error('❌ Failed to fetch IOSS settings:', error);
    }
  };

  // Update hide-for-domestic toggle
  const updateHideForDomestic = async (enabled) => {
    const shop = getShop();
    if (!shop) return;
    try {
      setHideForDomesticLoading(true);
      await api.patch(`/store-settings/${encodeURIComponent(shop)}`, {
        hide_for_domestic: enabled
      });
      setHideForDomestic(enabled);
    } catch (error) {
      console.error('❌ Failed to update domestic suppression:', error);
      alert(`Failed to update setting: ${error.response?.data?.detail || error.message}`);
    } finally {
      setHideForDomesticLoading(false);
    }
  };

  // Update DDP enabled toggle
  const updateDdpEnabled = async (enabled) => {
    const shop = getShop();
    if (!shop) return;

    try {
      setDdpLoading(true);
      await api.patch(`/ddp-settings/${encodeURIComponent(shop)}`, {
        ddp_enabled: enabled,
        ...(enabled ? {} : { ddp_fee_collection_enabled: false })
      });
      setDdpSettings(prev => ({
        ...prev,
        ddp_enabled: enabled,
        ddp_fee_collection_enabled: enabled ? prev.ddp_fee_collection_enabled : false
      }));
    } catch (error) {
      console.error('❌ Failed to update DDP:', error);
      alert(`Failed to update DDP settings: ${error.response?.data?.detail || error.message}`);
    } finally {
      setDdpLoading(false);
    }
  };

  // Collect calculated duties/taxes as a separate Shopify line item.
  // Enabling provisions one hidden TDK service variant for this merchant.
  const updateDdpFeeCollectionEnabled = async (enabled) => {
    const shop = getShop();
    if (!shop) return;

    if (enabled && !ddpSettings.ddp_enabled) {
      alert('Enable Country-Level DDP first.');
      return;
    }

    if (enabled && (!ddpSettings.ddp_countries || ddpSettings.ddp_countries.length === 0)) {
      alert('Select at least one DDP destination country before enabling fee collection.');
      return;
    }

    try {
      setDdpLoading(true);
      const response = await api.patch(`/ddp-settings/${encodeURIComponent(shop)}`, {
        ddp_fee_collection_enabled: enabled
      });

      setDdpSettings(prev => ({
        ...prev,
        ddp_fee_collection_enabled: response.data.ddp_fee_collection_enabled ?? enabled,
        ddp_fee_collection_configured:
          enabled ? Boolean(response.data.ddp_fee_variant_id || response.data.ddp_fee_variant_gid) : prev.ddp_fee_collection_configured,
        ddp_fee_variant_id: response.data.ddp_fee_variant_id || prev.ddp_fee_variant_id,
        ddp_fee_variant_gid: response.data.ddp_fee_variant_gid || prev.ddp_fee_variant_gid
      }));
    } catch (error) {
      console.error('❌ Failed to update DDP fee collection:', error);
      alert(`Failed to update DDP fee collection: ${error.response?.data?.detail || error.message}`);
    } finally {
      setDdpLoading(false);
    }
  };

  // Update DDP countries
  const updateDdpCountries = async (countries) => {
    const shop = getShop();
    if (!shop) return;

    try {
      setDdpLoading(true);
      const removingLastCountry = countries.length === 0 && ddpSettings.ddp_fee_collection_enabled;
      await api.patch(`/ddp-settings/${encodeURIComponent(shop)}`, {
        ddp_countries: countries,
        ...(removingLastCountry ? { ddp_fee_collection_enabled: false } : {})
      });
      setDdpSettings(prev => ({
        ...prev,
        ddp_countries: countries,
        ddp_fee_collection_enabled: removingLastCountry ? false : prev.ddp_fee_collection_enabled
      }));
    } catch (error) {
      console.error('❌ Failed to update DDP countries:', error);
      alert(`Failed to update DDP countries: ${error.response?.data?.detail || error.message}`);
    } finally {
      setDdpLoading(false);
    }
  };

  // Toggle a country in DDP list
  const toggleDdpCountry = (countryCode) => {
    const currentCountries = ddpSettings.ddp_countries || [];
    let newCountries;
    
    if (currentCountries.includes(countryCode)) {
      newCountries = currentCountries.filter(c => c !== countryCode);
    } else {
      newCountries = [...currentCountries, countryCode];
    }
    
    updateDdpCountries(newCountries);
  };

  // Update IOSS enabled toggle
  const updateIossEnabled = async (enabled) => {
    const shop = getShop();
    if (!shop) return;

    try {
      setIossLoading(true);
      await patchStoreSettingsWithRetry(shop, {
        ioss_enabled: enabled
      });
      setIossSettings(prev => ({ ...prev, ioss_enabled: enabled }));
    } catch (error) {
      console.error('❌ Failed to update IOSS:', error);
      alert(`Failed to update IOSS settings: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIossLoading(false);
    }
  };

  // Update IOSS registered toggle
  const updateIossRegistered = async (registered) => {
    const shop = getShop();
    if (!shop) return;

    try {
      setIossLoading(true);
      await patchStoreSettingsWithRetry(shop, {
        ioss_registered: registered
      });
      setIossSettings(prev => ({ ...prev, ioss_registered: registered }));
    } catch (error) {
      console.error('❌ Failed to update IOSS registration:', error);
      alert(`Failed to update IOSS registration: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIossLoading(false);
    }
  };

  // Fetch setup status
  const fetchSetupStatus = async () => {
    if (!getShop()) return;

    try {
      const response = await api.get(`/setup/status?shop=${encodeURIComponent(getShop())}`);
      setSetupStatus(response.data);
      
    } catch (error) {
      console.error('❌ Failed to fetch setup status:', error);
      // Fallback status
      setSetupStatus({
        originSet: false,
        embedEnabled: false,
        cartBlockAdded: false,
        completed: false
      });
    }
  };

  // Handle origin country update
  const handleOriginUpdate = async (countryCode) => {
    try {
      
      // Use the existing updateShippingOrigin function
      await onUpdateShippingOrigin(getShop(), countryCode);
      
      // Refresh setup status to update originSet
      await fetchSetupStatus();
      
      // IMPORTANT: Refresh IOSS settings after origin change
      // This ensures IOSS toggle visibility updates based on new origin + plan tier
      await fetchIossSettings();
      
    } catch (error) {
      console.error('❌ Failed to update origin:', error);
      alert(`Failed to update shipping origin: ${error.message || 'Unknown error'}`);
    }
  };

  // Refresh status for development diagnostics and re-fetch IOSS/DDP settings whenever
  // the stores prop updates (stores starts empty; loadData populates it async,
  // so we need a second fetch once the shop domain is known).
  useEffect(() => {
    fetchSetupStatus();
    fetchIossSettings();
  }, [stores]); // eslint-disable-line

 // Country options for shipping origin - 57 supported countries sorted alphabetically by name
const countryOptions = [
  { code: 'AR', name: '🇦🇷 Argentina' },
  { code: 'AU', name: '🇦🇺 Australia' },
  { code: 'AT', name: '🇦🇹 Austria' },
  { code: 'BE', name: '🇧🇪 Belgium' },
  { code: 'BR', name: '🇧🇷 Brazil' },
  { code: 'BG', name: '🇧🇬 Bulgaria' },
  { code: 'CA', name: '🇨🇦 Canada' },
  { code: 'CL', name: '🇨🇱 Chile' },
  { code: 'CN', name: '🇨🇳 China' },
  { code: 'CO', name: '🇨🇴 Colombia' },
  { code: 'HR', name: '🇭🇷 Croatia' },
  { code: 'CY', name: '🇨🇾 Cyprus' },
  { code: 'CZ', name: '🇨🇿 Czech Republic' },
  { code: 'DK', name: '🇩🇰 Denmark' },
  { code: 'EE', name: '🇪🇪 Estonia' },
  { code: 'FI', name: '🇫🇮 Finland' },
  { code: 'FR', name: '🇫🇷 France' },
  { code: 'DE', name: '🇩🇪 Germany' },
  { code: 'GR', name: '🇬🇷 Greece' },
  { code: 'HK', name: '🇭🇰 Hong Kong' },
  { code: 'HU', name: '🇭🇺 Hungary' },
  { code: 'IS', name: '🇮🇸 Iceland' },
  { code: 'IN', name: '🇮🇳 India' },
  { code: 'IE', name: '🇮🇪 Ireland' },
  { code: 'IL', name: '🇮🇱 Israel' },
  { code: 'IT', name: '🇮🇹 Italy' },
  { code: 'JP', name: '🇯🇵 Japan' },
  { code: 'KW', name: '🇰🇼 Kuwait' },
  { code: 'LV', name: '🇱🇻 Latvia' },
  { code: 'LT', name: '🇱🇹 Lithuania' },
  { code: 'LU', name: '🇱🇺 Luxembourg' },
  { code: 'MY', name: '🇲🇾 Malaysia' },
  { code: 'MT', name: '🇲🇹 Malta' },
  { code: 'MX', name: '🇲🇽 Mexico' },
  { code: 'NL', name: '🇳🇱 Netherlands' },
  { code: 'NZ', name: '🇳🇿 New Zealand' },
  { code: 'NO', name: '🇳🇴 Norway' },
  { code: 'PE', name: '🇵🇪 Peru' },
  { code: 'PL', name: '🇵🇱 Poland' },
  { code: 'PT', name: '🇵🇹 Portugal' },
  { code: 'QA', name: '🇶🇦 Qatar' },
  { code: 'RO', name: '🇷🇴 Romania' },
  { code: 'RU', name: '🇷🇺 Russia' },
  { code: 'SA', name: '🇸🇦 Saudi Arabia' },
  { code: 'SG', name: '🇸🇬 Singapore' },
  { code: 'SK', name: '🇸🇰 Slovakia' },
  { code: 'SI', name: '🇸🇮 Slovenia' },
  { code: 'ZA', name: '🇿🇦 South Africa' },
  { code: 'KR', name: '🇰🇷 South Korea' },
  { code: 'ES', name: '🇪🇸 Spain' },
  { code: 'SE', name: '🇸🇪 Sweden' },
  { code: 'CH', name: '🇨🇭 Switzerland' },
  { code: 'TW', name: '🇹🇼 Taiwan' },
  { code: 'TH', name: '🇹🇭 Thailand' },
  { code: 'TR', name: '🇹🇷 Turkey' },
  { code: 'AE', name: '🇦🇪 United Arab Emirates' },
  { code: 'GB', name: '🇬🇧 United Kingdom' },
  { code: 'US', name: '🇺🇸 United States' },
      { code: 'VN', name: '🇻🇳 Vietnam' }
];


  const currentOrigin = stores.length > 0 ? stores[0]?.shipping_origin_country : null;

  return (
    <div className="settings-manager">
      <h2>⚙️ App Settings</h2>
      
      {/* Shipping Origin Configuration */}
      <div className="settings-section">
        <h3>🌍 Shipping Origin Country</h3>
        <p>Set your primary shipping origin for accurate tax and duty calculations.</p>
        
        <div className="origin-selector">
          <select 
            value={currentOrigin || ''}
            onChange={(e) => handleOriginUpdate(e.target.value)}
            className="origin-select"
          >
            <option value="">Select shipping origin country...</option>
            {countryOptions.map(country => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
          
          {currentOrigin && (
            <div className="current-origin">
              Current origin: {countryOptions.find(c => c.code === currentOrigin)?.name || currentOrigin}
            </div>
          )}
        </div>
      </div>

      {/* Domestic Suppression Toggle */}
      <div className="settings-section">
        <h3>🏠 Hide Widget for Domestic Customers</h3>
        <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '1rem' }}>
          When enabled, the TDK import cost widget will not be shown to customers
          shopping from the same country as your shipping origin. Domestic orders
          do not attract import duties, so there is nothing to display.
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={hideForDomestic}
            onChange={(e) => updateHideForDomestic(e.target.checked)}
            disabled={hideForDomesticLoading}
            style={{ width: 18, height: 18, accentColor: '#6366f1', cursor: 'pointer' }}
          />
          <span style={{ color: '#e2e8f0', fontWeight: 500 }}>
            {hideForDomesticLoading
              ? 'Saving…'
              : hideForDomestic
                ? '✅ Hide widget for domestic customers'
                : 'Show widget to all customers (default)'}
          </span>
        </label>
        {hideForDomestic && (
          <div style={{
            marginTop: '0.75rem',
            padding: '0.6rem 0.9rem',
            background: 'rgba(99,102,241,0.1)',
            border: '1px solid rgba(99,102,241,0.3)',
            borderRadius: 6,
            color: '#a5b4fc',
            fontSize: '0.85rem'
          }}>
            ℹ️ Customers browsing from <strong>{stores?.shipping_origin_country || 'your origin country'}</strong> will
            not see the import cost widget.
          </div>
        )}
      </div>

      {/* IOSS Settings — always visible to UK sellers; Growth+ controls are locked below */}
      {iossSettings.ioss_panel_visible && (
        <div className="settings-section ioss-section">
          <h3>🇪🇺 IOSS (EU ≤ €150) — GB sellers only</h3>
          <p className="ioss-helper-text">
            Pre-collect EU VAT for consignments up to €150 when shipping from Great Britain to EU customers.
            Over €150 uses standard import VAT/duty. For more information, visit <a href="https://taxation-customs.ec.europa.eu/ioss_en" target="_blank" rel="noopener noreferrer">EU IOSS guidance</a>.
          </p>

          {!iossSettings.ioss_feature_enabled ? (
            <div className="alert alert-info" style={{borderLeft: '4px solid #007acc', background: '#f0f8ff'}}>
              <strong>🔒 Available on Growth Plan &amp; Above</strong>
              <p style={{margin: '8px 0 12px', color: '#444'}}>
                IOSS is available to UK sellers on the <strong>Growth, Enterprise, and Plus</strong> plans.
                Upgrade to collect EU VAT at checkout for eligible consignments and suppress import-fee estimates up to €150.
              </p>
              <ul style={{fontSize: '13px', color: '#444', marginBottom: '12px', paddingLeft: '18px'}}>
                <li>Show IOSS VAT-at-checkout messaging for qualifying EU orders</li>
                <li>Suppress import VAT, duty, and processing-fee estimates up to €150</li>
                <li>Record your IOSS registration status for the correct EU processing-fee rate</li>
              </ul>
              <button className="btn btn-primary btn-sm" onClick={() => onUpgrade && onUpgrade(getShop())}>
                Upgrade to Growth →
              </button>
            </div>
          ) : (
            <>
              <div className="ioss-toggle">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={iossSettings.ioss_enabled}
                    onChange={(e) => updateIossEnabled(e.target.checked)}
                    disabled={iossLoading}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span className="toggle-label">
                  {iossSettings.ioss_enabled ? '✅ IOSS Enabled' : 'Enable IOSS'}
                </span>
              </div>

              {iossSettings.ioss_enabled && (
                <div className="alert alert-success" style={{marginTop: '1rem'}}>
                  ✅ IOSS is active. VAT will be collected at checkout for eligible EU consignments ≤ €150.
                  The temporary €3 EU customs duty per tariff classification is shown separately where applicable.
                </div>
              )}

              {/* IOSS Registration — VAT scheme status */}
              <div className="ioss-registered-section" style={{marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb'}}>
                <h4 style={{margin: '0 0 0.4rem 0', fontSize: '0.95rem', fontWeight: 600}}>EU IOSS Registration Status</h4>
                <p style={{margin: '0 0 0.75rem 0', fontSize: '0.85rem', color: '#6b7280'}}>
                  Are you registered for the EU Import One-Stop Shop (IOSS) scheme? This controls the VAT/IOSS treatment shown to customers.
                  The temporary EU customs duty is <strong>€3 per tariff classification</strong> for qualifying low-value imports, regardless of IOSS registration.
                </p>
                <div className="ioss-toggle">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={iossSettings.ioss_registered}
                      onChange={(e) => updateIossRegistered(e.target.checked)}
                      disabled={iossLoading}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className="toggle-label">
                    {iossSettings.ioss_registered ? '✅ IOSS Registered' : 'Not IOSS Registered'}
                  </span>
                </div>
                {iossSettings.ioss_registered && (
                  <div className="alert alert-info" style={{marginTop: '0.75rem', fontSize: '0.85rem'}}>
                    ℹ️ IOSS registration is active for VAT treatment. The temporary EU customs duty remains €3 per tariff classification where applicable.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Country-Level DDP Settings — always visible; frozen when multi-origin is active */}
      {(() => {
        const isMultiOrigin = ddpSettings.is_multi_origin;
        const planHandle = (stores.find(s => s.shop_domain === getShop())?.subscription_tier || 'starter').toLowerCase().trim();
        // 'basic' is the Shopify plan handle used during free trials — treat as trial
        const hasDdpAccess = ddpSettings.ddp_feature_enabled || ['growth', 'enterprise', 'plus', 'trial', 'basic'].includes(planHandle);
        return (
          <div
            className="settings-section ddp-section"
            style={isMultiOrigin ? { opacity: 0.45, pointerEvents: 'none', userSelect: 'none', position: 'relative' } : {}}
          >
            <h3>📦 Delivered Duty Paid (DDP) Countries</h3>

            {/* Multi-origin notice banner — shown instead of the upgrade/toggle UI */}
            {isMultiOrigin && (
              <div style={{
                background: '#fff8e1',
                border: '1px solid #f9a825',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '1rem',
                pointerEvents: 'auto',
                opacity: 1,
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px'
              }}>
                <span style={{fontSize: '18px', lineHeight: 1}}>⚠️</span>
                <div>
                  <strong style={{color: '#e65100'}}>Unavailable in Multi-Origin mode</strong>
                  <p style={{margin: '4px 0 0', fontSize: '13px', color: '#555'}}>
                    Country-level DDP only works in Single-Origin mode. Switch to <strong>Single Origin</strong> in the
                    Multi-Origin Settings section above — this panel will become active as soon as the change is saved.
                  </p>
                </div>
              </div>
            )}

            <p className="ddp-helper-text">
              Choose the destination countries where you want to offer Delivered Duty Paid (DDP).
              You can either include duties in your Shopify Market prices, or let TDK collect the calculated charges as a separate
              <strong> Import Duties &amp; Taxes</strong> line item. Do not use both approaches for the same charge.
            </p>
            {!hasDdpAccess ? (
              <div className="alert alert-info" style={{borderLeft: '4px solid #007acc', background: '#f0f8ff'}}>
                <strong>🔒 Available on Growth Plan &amp; Above</strong>
                <p style={{margin: '8px 0 12px', color: '#444'}}>
                  Country-Level DDP is available on the <strong>Growth, Enterprise, and Plus</strong> plans.
                  Upgrade to configure which destination countries have duties and taxes pre-paid in your pricing.
                </p>
                <ul style={{fontSize: '13px', color: '#444', marginBottom: '12px', paddingLeft: '18px'}}>
                  <li>Mark specific countries as Delivered Duty Paid</li>
                  <li>TDK displays "Tax &amp; Duties Included" for those shoppers</li>
                  <li>Suppresses import fee warnings for DDP destinations</li>
                  <li>Works alongside Shopify Markets pricing</li>
                </ul>
                <button className="btn btn-primary btn-sm" onClick={() => setActiveTab('billing')}>
                  Upgrade to Growth →
                </button>
              </div>
            ) : (
              <>
                <div className="ddp-toggle">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={ddpSettings.ddp_enabled}
                      onChange={(e) => updateDdpEnabled(e.target.checked)}
                      disabled={ddpLoading || isMultiOrigin}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className="toggle-label">
                    {ddpSettings.ddp_enabled ? '✅ Country-Level DDP Enabled' : 'Enable Country-Level DDP'}
                  </span>
                </div>

                {ddpSettings.ddp_enabled && (
                  <>
                    <div className="ddp-country-selector" style={{marginTop: '1rem'}}>
                      <label style={{fontWeight: '500', marginBottom: '0.5rem', display: 'block'}}>
                        Select DDP Countries:
                      </label>
                      <p style={{fontSize: '12px', color: '#666', marginBottom: '0.5rem'}}>
                        These are the destinations where your DDP treatment applies. If TDK line-item collection is enabled below,
                        the calculated charges will be collected separately rather than described as included in product prices.
                      </p>
                      <div className="ddp-country-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                        gap: '8px',
                        maxHeight: '300px',
                        overflowY: 'auto',
                        padding: '12px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        background: '#fafafa'
                      }}>
                        {countryOptions.map(country => (
                          <label
                            key={country.code}
                            className="ddp-country-item"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '8px 12px',
                              background: ddpSettings.ddp_countries.includes(country.code) ? '#fff3e0' : '#fff',
                              border: ddpSettings.ddp_countries.includes(country.code) ? '2px solid #ff9800' : '1px solid #ddd',
                              borderRadius: '6px',
                              cursor: isMultiOrigin ? 'not-allowed' : 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={ddpSettings.ddp_countries.includes(country.code)}
                              onChange={() => toggleDdpCountry(country.code)}
                              disabled={ddpLoading || isMultiOrigin}
                              style={{
                                position: 'absolute',
                                opacity: 0,
                                width: 0,
                                height: 0
                              }}
                            />
                            <span style={{
                              fontSize: '13px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}>
                              {ddpSettings.ddp_countries.includes(country.code) && <span>✓</span>}
                              {country.name}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {ddpSettings.ddp_countries.length > 0 && (
                      <div className="alert alert-warning" style={{marginTop: '1rem', background: '#fff3e0', border: '1px solid #ff9800', color: '#e65100'}}>
                        <strong>📦 DDP Active for {ddpSettings.ddp_countries.length} {ddpSettings.ddp_countries.length === 1 ? 'country' : 'countries'}:</strong>
                        <br />
                        {ddpSettings.ddp_countries.map(code => {
                          const country = countryOptions.find(c => c.code === code);
                          return country ? country.name : code;
                        }).join(', ')}
                      </div>
                    )}

                    <div style={{
                      marginTop: '1rem',
                      padding: '16px',
                      border: '1px solid #c7d7e8',
                      borderLeft: '4px solid #007acc',
                      borderRadius: '8px',
                      background: '#f7fbff'
                    }}>
                      <h4 style={{margin: '0 0 8px', fontSize: '1rem'}}>
                        Collect duties &amp; taxes as a separate TDK line item
                      </h4>
                      <p style={{margin: '0 0 10px', fontSize: '13px', color: '#444', lineHeight: 1.55}}>
                        TDK can add an <strong>Import Duties &amp; Taxes</strong> line to the cart using the live calculation,
                        so the shopper pays the product price and import charges separately before purchase. Product prices are not changed.
                      </p>
                      <ul style={{fontSize: '13px', color: '#444', margin: '0 0 12px', paddingLeft: '18px'}}>
                        <li>Recalculates when cart quantities or the destination change</li>
                        <li>Removes the fee automatically for domestic or zero-charge destinations</li>
                        <li>Uses only the DDP countries selected above</li>
                        <li>On supported Shopify Plus checkouts, TDK shows the full VAT/duty breakdown</li>
                      </ul>

                      <div className="alert alert-warning" style={{
                        margin: '0 0 12px',
                        background: '#fff8e1',
                        border: '1px solid #f9a825',
                        color: '#8a4b00'
                      }}>
                        <strong>Important:</strong> If this is enabled, do not also increase Shopify Market prices to include the same
                        duties and taxes, otherwise customers could be charged twice.
                      </div>

                      <div className="ddp-toggle">
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={ddpSettings.ddp_fee_collection_enabled}
                            onChange={(e) => updateDdpFeeCollectionEnabled(e.target.checked)}
                            disabled={
                              ddpLoading ||
                              isMultiOrigin ||
                              !ddpSettings.ddp_enabled ||
                              ddpSettings.ddp_countries.length === 0
                            }
                          />
                          <span className="toggle-slider"></span>
                        </label>
                        <span className="toggle-label">
                          {ddpSettings.ddp_fee_collection_enabled
                            ? '✅ TDK line-item collection enabled'
                            : 'Enable TDK line-item collection'}
                        </span>
                      </div>

                      {!ddpSettings.ddp_fee_collection_enabled && (
                        <p style={{fontSize: '12px', color: '#666', margin: '8px 0 0'}}>
                          Available on <strong>Growth, Enterprise, Plus, and active trial</strong> plans. The first enable creates a
                          dedicated non-shipping, non-taxable TDK service product in Shopify automatically.
                        </p>
                      )}

                      {ddpSettings.ddp_fee_collection_enabled && (
                        <div className="alert alert-success" style={{marginTop: '10px', marginBottom: 0}}>
                          <strong>✅ Charges will be collected separately</strong>
                          <p style={{margin: '6px 0 0', fontSize: '12px'}}>
                            TDK will add and maintain the Import Duties &amp; Taxes line for the selected DDP destinations.
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        );
      })()}

      {/* Multi-Origin Settings */}
      <MultiOriginSettings
        shop={getShop()}
        planName={stores.find(s => s.shop_domain === getShop())?.subscription_tier || 'starter'}
        currentOrigin={currentOrigin}
        countryOptions={countryOptions}
        onUpgrade={onUpgrade}
        onOriginModeSaved={(isMultiOrigin) => {
          setDdpSettings(previous => ({ ...previous, is_multi_origin: isMultiOrigin }));
        }}
      />

      {/* Courier Processing Fee (replaces the equivalent generic processing charge) */}
      <CourierProcessingFeeSettings shop={getShop()} />

      {/* Product Page Widget Feature Card */}
      {(() => {
        const planHandle = (stores.find(s => s.shop_domain === getShop())?.subscription_tier || 'starter').toLowerCase().trim();
        // 'basic' is the Shopify plan handle used during free trials — treat as trial
        const hasProductPageAccess = ['growth', 'enterprise', 'plus', 'trial', 'basic'].includes(planHandle);
        return (
          <div className="settings-section">
            <h3>🏷️ Product Page Import Cost Widget</h3>
            <p style={{color: '#666', marginBottom: '16px', lineHeight: '1.5'}}>
              Display estimated import duties and taxes directly on your product pages — before shoppers add items to their cart.
              Reduces cart abandonment and builds trust with international customers by showing full landed cost upfront.
            </p>
            {!hasProductPageAccess ? (
              <div className="alert alert-info" style={{borderLeft: '4px solid #007acc', background: '#f0f8ff'}}>
                <strong>🔒 Available on Growth Plan &amp; Above</strong>
                <p style={{margin: '8px 0 12px', color: '#444'}}>
                  The product page import cost widget is available on the <strong>Growth, Enterprise, and Plus</strong> plans.
                  Upgrade to display estimated duties and taxes on your product pages before shoppers add to cart.
                </p>
                <ul style={{fontSize: '13px', color: '#444', marginBottom: '12px', paddingLeft: '18px'}}>
                  <li>Show import costs on product pages — before add to cart</li>
                  <li>Auto-detects visitor country via IP geolocation</li>
                  <li>Shopper can change their country in the widget</li>
                  <li>Recalculates when quantity changes</li>
                  <li>Fully consistent with cart and checkout calculations</li>
                </ul>
                <button className="btn btn-primary btn-sm" onClick={() => setActiveTab('billing')}>
                  Upgrade to Growth →
                </button>
              </div>
            ) : (
              <div className="alert alert-success" style={{borderLeft: '4px solid #28a745', background: '#f0fff4'}}>
                <strong>✅ Product Page Widget — Active on your plan</strong>
                <p style={{margin: '8px 0 12px', color: '#444'}}>
                  Add the <strong>"Estimated Import Costs"</strong> block to any product page template using the Shopify Theme Customizer.
                </p>
                <ol style={{fontSize: '13px', color: '#444', marginBottom: '12px', paddingLeft: '18px'}}>
                  <li>Go to <strong>Online Store → Themes → Customise</strong></li>
                  <li>Open any <strong>Product</strong> template</li>
                  <li>Click <strong>Add block</strong> and search for <strong>"Estimated Import Costs"</strong></li>
                  <li>Drag it between the price and the Add to Cart button</li>
                  <li>Save — the widget is live immediately</li>
                </ol>
                <a
                  href={`https://admin.shopify.com/store/${(getShop() || '').replace('.myshopify.com', '')}/themes/current/editor`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary btn-sm"
                >
                  Open Theme Customizer →
                </a>
              </div>
            )}
          </div>
        );
      })()}

      {/* Checkout Extension Feature Card */}
      {(() => {
        const planHandle = (stores.find(s => s.shop_domain === getShop())?.subscription_tier || 'starter').toLowerCase().trim();
        // 'basic' is the Shopify plan handle used during free trials — treat as trial
        const hasCheckoutAccess = ['enterprise', 'plus', 'trial', 'basic'].includes(planHandle);
        return (
          <div className="settings-section">
            <h3>🛒 Checkout Import Cost Extension</h3>
            <p style={{color: '#666', marginBottom: '16px', lineHeight: '1.5'}}>
              Display a full import cost breakdown inside the Shopify checkout — at the payment step, before the customer places their order.
            </p>
            {!hasCheckoutAccess ? (
              <div className="alert alert-info" style={{borderLeft: '4px solid #007acc', background: '#f0f8ff'}}>
                <strong>🔒 Requires Shopify Plus</strong>
                <p style={{margin: '8px 0 8px', color: '#444'}}>
                  Shopify only allows apps to customise the checkout (information, shipping, and payment steps)
                  for merchants on a <strong>Shopify Plus</strong> plan. This is a Shopify platform restriction
                  that applies to all apps — not just Tax &amp; Duty King.
                </p>
                <p style={{margin: '0 0 12px', color: '#444'}}>
                  If you are on Shopify Plus, upgrade your Tax &amp; Duty King plan to <strong>Enterprise or Plus</strong> to activate this feature.
                </p>
                <ul style={{fontSize: '13px', color: '#444', marginBottom: '12px', paddingLeft: '18px'}}>
                  <li>Shows full duty, tax &amp; fee breakdown at payment step</li>
                  <li>Consistent with cart and product page estimates</li>
                  <li>Supports DDP, IOSS, and TCA rules</li>
                  <li>Drag-and-drop placement via the Checkout Editor</li>
                </ul>
                <a
                  href="https://www.shopify.com/plus"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary btn-sm"
                  style={{marginRight: '8px'}}
                >
                  Learn about Shopify Plus →
                </a>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => onUpgrade && onUpgrade(getShop())}
                >
                  Upgrade TDK Plan →
                </button>
              </div>
            ) : (
              <div className="alert alert-success" style={{borderLeft: '4px solid #28a745', background: '#f0fff4'}}>
                <strong>✅ Checkout Extension — Active on your plan</strong>
                <p style={{margin: '8px 0 12px', color: '#444'}}>
                  Add the <strong>"Import Cost Calculator"</strong> block to your checkout using the Shopify Checkout Editor.
                </p>
                <ol style={{fontSize: '13px', color: '#444', marginBottom: '12px', paddingLeft: '18px'}}>
                  <li>Go to <strong>Settings → Checkout</strong> in your Shopify Admin</li>
                  <li>Click <strong>Customise</strong> next to your checkout</li>
                  <li>Click <strong>Add app block</strong> and select <strong>Tax &amp; Duty King</strong></li>
                  <li>Position it in the order summary section</li>
                  <li>Save — the extension is live immediately</li>
                </ol>
                <a
                  href={`https://admin.shopify.com/store/${(getShop() || '').replace('.myshopify.com', '')}/settings/checkout`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary btn-sm"
                >
                  Open Checkout Settings →
                </a>
              </div>
            )}
          </div>
        );
      })()}

      {/* Setup Status Debug (development only) */}
      {process.env.NODE_ENV === 'development' && setupStatus && (
        <div className="settings-section debug-section">
          <h3>🔧 Setup Status (Debug)</h3>
          <pre>{JSON.stringify(setupStatus, null, 2)}</pre>
          <button onClick={fetchSetupStatus} className="btn btn-secondary btn-sm">
            Refresh Status
          </button>
        </div>
      )}
    </div>
  );
};


// Comprehensive Subscription Management Component - Phase 2.2: Advanced Security & App Bridge
const SubscriptionManagement = ({ stores, onReload }) => {
  const [subscriptions, setSubscriptions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [availablePlans, setAvailablePlans] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [upgradeOptions, setUpgradeOptions] = useState([]);
  const [selectedBillingPeriod, setSelectedBillingPeriod] = useState('monthly');
  const [dismissedBanners, setDismissedBanners] = useState(new Set());
  const [billingStates, setBillingStates] = useState({}); // Track billing states per store
  
  // New state for Phase 2
  const [billingSuccess, setBillingSuccess] = useState(null);
  const [billingError, setBillingError] = useState(null);
  const [testMode, setTestMode] = useState(false);

  // Check URL params for billing return status (cosmetic only - always re-verify with API)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    // Extract correlation ID for tracking
    const correlationId = params.get('correlation_id');
    
    if (params.get('billing_success') === 'true') {
      const successData = {
        plan: params.get('plan') || 'Unknown',
        period: params.get('period') || 'monthly',
        test_mode: params.get('test_mode') === 'true',
        correlation_id: correlationId
      };
      
      
      // Show cosmetic success banner but re-verify with API
      setBillingSuccess(successData);
      setTestMode(params.get('test_mode') === 'true');
      
      // Clean URL parameters immediately
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      
      // Force reload subscription data to get true state
      if (stores.length > 0) {
        setTimeout(() => {
          loadSubscriptionData();
        }, 500);
      }
    }
    
    if (params.get('billing_error') === 'true') {
      const errorData = {
        message: params.get('error_message') || 'billing_failed',
        correlation_id: correlationId
      };
      
      console.error(`❌ Billing return error - Correlation ID: ${correlationId}`, errorData);
      
      setBillingError(errorData.message);
      
      // Clean URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [stores]);

  // Helper functions for enhanced usage cards
  const getStatusClass = (subscription) => {
    if (!subscription) return 'status-none';
    if (subscription.paused) return 'status-paused';
    if (subscription.grace_active) return 'status-grace';
    if (subscription.at_limit) return 'status-limit';
    if (subscription.at_warning) return 'status-warning';
    return 'status-active';
  };

  const getStatusText = (subscription) => {
    if (!subscription) return 'No Subscription';
    if (subscription.paused) return 'Paused';
    if (subscription.grace_active) return 'Grace Period';
    if (subscription.at_limit) return 'Limit Reached';
    if (subscription.at_warning) return 'Near Limit';
    return 'Active';
  };

  const getProgressClass = (subscription) => {
    if (!subscription) return 'progress-none';
    if (subscription.paused) return 'progress-paused';
    if (subscription.grace_active) return 'progress-grace';
    if (subscription.at_limit) return 'progress-limit';
    if (subscription.at_warning) return 'progress-warning';
    return 'progress-normal';
  };

  const getUsageMessage = (subscription) => {
    if (!subscription) return 'No usage data available';
    
    const { 
      calculations_used = 0, 
      calculations_limit = 750, 
      usage_percentage = 0, 
      paused = false, 
      at_warning = false, 
      at_limit = false, 
      grace_active = false,
      resets_at 
    } = subscription;

    const resetText = resets_at 
      ? `Resets on ${new Date(resets_at).toLocaleDateString()}`
      : 'Resets on the 1st of next month';

    if (paused) {
      return `Usage is paused until next month. You used ${calculations_used}/${calculations_limit} calculations this month. ${resetText}.`;
    } else if (grace_active) {
      return `You've exceeded your limit but are in the grace period (${calculations_used}/${calculations_limit} calculations, ${usage_percentage.toFixed(1)}%). Upgrade to avoid service interruption.`;
    } else if (at_limit) {
      return `You've reached your monthly limit (${calculations_used}/${calculations_limit} calculations). Upgrade for more capacity or pause usage. ${resetText}.`;
    } else if (at_warning) {
      return `You've used ${calculations_used}/${calculations_limit} calculations this month (${usage_percentage.toFixed(1)}%). Consider upgrading to avoid interruptions.`;
    } else {
      return `You've used ${calculations_used}/${calculations_limit} calculations this month (${usage_percentage.toFixed(1)}%). ${resetText}.`;
    }
  };

  const handlePauseUsage = async (shopDomain) => {
    setLoading(true);
    try {
      await api.post('/usage/pause', { shop_domain: shopDomain });
      setSuccess('Usage paused successfully for the current month');
      loadSubscriptionData(); // Reload to reflect changes
    } catch (err) {
      setError('Failed to pause usage: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleResumeUsage = async (shopDomain) => {
    setLoading(true);
    try {
      await api.post('/usage/resume', { shop_domain: shopDomain });
      setSuccess('Usage resumed successfully');
      loadSubscriptionData(); // Reload to reflect changes
    } catch (err) {
      setError('Failed to resume usage: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDismissBanner = (shopDomain) => {
    setDismissedBanners(prev => new Set([...prev, shopDomain]));
  };

  const handleBannerUpgrade = (shopDomain) => {
    handleUpgrade(shopDomain);
    setDismissedBanners(prev => new Set([...prev, shopDomain]));
  };



  // Load subscription data
  useEffect(() => {
    if (stores.length > 0) {
      loadSubscriptionData();
      loadAvailablePlans();
    }
  }, [stores]);

  const loadSubscriptionData = async () => {
    setLoading(true);
    try {
      const subscriptionData = {};
      
      for (const store of stores) {
        try {
          // Use new billing status API
          const response = await api.get(`/billing/status/${store.shop_domain}`);
          if (response.data.success) {
            subscriptionData[store.shop_domain] = response.data;
          }
        } catch (err) {
          console.warn(`Failed to load billing status for ${store.shop_domain}:`, err);
          // Fallback to new usage API if available
          try {
            const fallbackResponse = await api.get(`/usage/current?shop=${store.shop_domain}`);
            if (fallbackResponse.data) {
              // Use mapper to ensure consistent data handling (no || 0 fallbacks)
              const billing = mapUsageResponseToBilling(fallbackResponse.data);
              
              subscriptionData[store.shop_domain] = {
                success: true,
                shop_domain: store.shop_domain,
                status: fallbackResponse.data.plan?.status || 'trial',
                plan_handle: billing.plan_handle,
                billing_period: 'monthly',
                calculations_used: billing.calculations_used,
                calculations_limit: billing.calculations_limit,
                usage_percentage: billing.usage_percentage,
                trial_active: fallbackResponse.data.plan?.status === 'trial',
                at_warning: billing.at_warning,
                at_limit: billing.at_limit,
                gating: billing.gating
              };
            }
          } catch (fallbackErr) {
            console.warn(`Fallback API also failed for ${store.shop_domain}:`, fallbackErr);
            // Set default data to prevent UI errors
            subscriptionData[store.shop_domain] = {
              success: true,
              shop_domain: store.shop_domain,
              status: 'trial',
              plan_handle: 'starter',
              billing_period: 'monthly',
              calculations_used: 0,
              calculations_limit: 750,
              usage_percentage: 0.0,
              trial_active: true,
              at_warning: false,
              at_limit: false
            };
          }
        }
      }
      
      setSubscriptions(subscriptionData);
      
    } catch (err) {
      setError('Failed to load subscription data');
      console.error('Subscription data loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailablePlans = async () => {
    try {
      // Use new billing plans API
      const response = await api.get('/billing/plans');
      if (response.data.success) {
        setAvailablePlans(response.data.plans);
      }
    } catch (err) {
      console.warn('Failed to load available plans:', err);
      // Fallback to old API
      try {
        const fallbackResponse = await api.get('/subscription/plans');
        if (fallbackResponse.data.success) {
          setAvailablePlans(fallbackResponse.data.plans);
        }
      } catch (fallbackErr) {
        console.warn('Fallback plans API also failed:', fallbackErr);
      }
    }
  };

  // Production Shopify Billing Subscription Creation with proper App Bridge and security
  const createBillingSubscription = async (shopDomain, planHandle, billingPeriod = 'monthly') => {
    try {
      // Generate correlation ID for tracking
      const correlationId = `billing_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Extract host parameter from URL for App Bridge continuity
      const urlParams = new URLSearchParams(window.location.search);
      const hostParam = urlParams.get('host');
      
      // Prevent double-clicks by immediately setting loading state
      setBillingStates(prev => ({
        ...prev,
        [shopDomain]: { loading: true, error: null, confirmationUrl: null }
      }));
      
      // Prepare request payload with host parameter for backend return URL
      const payload = {
        shop_domain: shopDomain,
        plan_handle: planHandle,
        billing_period: billingPeriod,
        correlation_id: correlationId
      };
      
      // Include host parameter if available for proper re-embedding
      if (hostParam) {
        payload.host = hostParam;
      }
      
      const response = await api.post('/billing/create', payload, {
        headers: getBillingHeaders()
      });
      
      if (response.data.success && response.data.confirmation_url) {
        // Log billing event
        
        const confirmationUrl = response.data.confirmation_url;
        
        // Store correlation ID for tracking
        setBillingStates(prev => ({
          ...prev,
          [shopDomain]: { 
            ...prev[shopDomain],
            confirmationUrl: confirmationUrl,
            correlationId: correlationId
          }
        }));
        
        // Show success message
        setSuccess(`Redirecting to Shopify for ${planHandle} plan confirmation...`);
        
        // Store correlation ID for tracking
        setBillingStates(prev => ({
          ...prev,
          [shopDomain]: { 
            ...prev[shopDomain],
            confirmationUrl: confirmationUrl,
            correlationId: correlationId
          }
        }));
        
        // Current App Bridge is loaded from Shopify's CDN. Keep the existing server-issued
        // redirect token as the single secure top-level transition to billing confirmation.
        try {
          
          const tokenResponse = await api.post('/billing/redirect', {
            confirmation_url: confirmationUrl,
            correlation_id: correlationId,
            shop_domain: shopDomain // Will be ignored on backend, using session
          }, {
            headers: getBillingHeaders()
          });
          
          if (tokenResponse.data.success && tokenResponse.data.redirect_token) {
            const tokenRedirectUrl = `${api.defaults.baseURL}/billing/redirect/${tokenResponse.data.redirect_token}`;
            
            
            window.open(tokenRedirectUrl, '_top');
            return;
            
          } else {
            throw new Error('Failed to create redirect token');
          }
        } catch (tokenError) {
          console.error(`❌ Tokenized redirect failed - ${correlationId}:`, tokenError);
          
          setBillingStates(prev => ({
            ...prev,
            [shopDomain]: { ...prev[shopDomain], loading: false, error: 'Unable to open Shopify plan confirmation. Please try again.' }
          }));
        }
        
      } else {
        // Handle specific error cases
        setBillingStates(prev => ({
          ...prev,
          [shopDomain]: { 
            loading: false, 
            error: getErrorMessage(response.data),
            confirmationUrl: null
          }
        }));
      }
      
    } catch (err) {
      console.error('Billing subscription creation error:', err);
      
      let errorMessage = 'Failed to create subscription';
      let shouldShowManage = false;
      
      if (err.response?.status === 404) {
        errorMessage = 'Shop not found. Please reinstall the app from Shopify App Store.';
      } else if (err.response?.status === 409 || err.response?.status === 400) {
        // Handle structured 409/400 responses properly (some CDNs may change status codes)
        const responseData = err.response.data;
        
        // Check if this is actually a billing conflict (even if status is 400)
        if (responseData && (responseData.error === 'duplicate_subscription' || 
            responseData.error === 'active_subscription_exists' || 
            responseData.error === 'existing_subscription_different_interval')) {
          
          if (responseData.error === 'duplicate_subscription') {
            errorMessage = responseData.message || 'You already have this exact subscription active.';
            shouldShowManage = true;
          } else if (responseData.error === 'active_subscription_exists') {
            errorMessage = responseData.message || 'You have an active subscription. Please manage your current subscription to change plans.';
            shouldShowManage = true;
          } else if (responseData.error === 'existing_subscription_different_interval') {
            errorMessage = responseData.message || `You have an existing subscription. This will replace your current subscription.`;
            shouldShowManage = false; // Allow the change for different intervals
          }
          
          // Log the conflict details for debugging
          
        } else if (err.response?.status === 400) {
          // Handle actual 400 validation errors
          errorMessage = responseData?.message || responseData?.detail || 'Invalid request. Please check your selection and try again.';
        }
        
      } else if (err.response?.status === 401) {
        errorMessage = 'Authentication required. Please refresh the page and try again.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setBillingStates(prev => ({
        ...prev,
        [shopDomain]: { 
          loading: false, 
          error: errorMessage, 
          confirmationUrl: null,
          shouldShowManage: shouldShowManage
        }
      }));
      
      // Show success message with manage instruction for conflicts
      if (shouldShowManage) {
        setSuccess('Subscription already active! Use the "Manage Plan" button to make changes.');
        // Refresh subscription data to update UI state
        setTimeout(() => {
          loadSubscriptionData();
        }, 500);
      }
    }
  };

  // Helper function to get user-friendly error messages
  const getErrorMessage = (responseData) => {
    if (responseData.error === 'duplicate_subscription') {
      return 'You already have this subscription. Use "Manage Plan" to make changes.';
    } else if (responseData.error === 'existing_subscription_different_interval') {
      return 'You have an existing subscription. This will upgrade/change your billing.';
    } else {
      return responseData.message || 'Subscription creation failed';
    }
  };

  // Retry billing subscription creation with fresh confirmation URL
  const retryBillingSubscription = async (shopDomain, planHandle, billingPeriod) => {
    // Clear previous error state
    setBillingStates(prev => ({
      ...prev,
      [shopDomain]: { loading: false, error: null, confirmationUrl: null }
    }));
    
    // Start fresh creation process
    await createBillingSubscription(shopDomain, planHandle, billingPeriod);
  };

  // Legacy subscription creation (fallback)
  const createSubscription = async (shopDomain, planHandle, billingPeriod = 'monthly') => {
    try {
      setLoading(true);
      const response = await api.post('/subscription/create', {
        shop_domain: shopDomain,
        plan_handle: planHandle,
        billing_period: billingPeriod
      });
      
      if (response.data.success) {
        setSuccess('Subscription created successfully! Redirecting to Shopify...');
        // Redirect to Shopify confirmation URL
        window.open(response.data.confirmation_url, '_blank');
        setTimeout(() => {
          loadSubscriptionData();
          setShowUpgradeModal(false);
        }, 2000);
      } else {
        // Handle structured error responses
        if (response.data.error === "shop_not_connected") {
          setError(`Shopify App Store Required: ${response.data.message}`);
        } else {
          setError(`Development Mode: ${response.data.message}`);
        }
      }
    } catch (err) {
      console.error('Subscription creation error:', err);
      
      if (err.response?.status === 400) {
        setError('Shopify App Store connection required. This app needs to be submitted to and installed from the Shopify App Store.');
      } else if (err.response?.status === 404) {
        setError('Shop not found. This shop needs to install the app from the Shopify App Store first.');
      } else if (err.response?.status === 422) {
        setError('App configuration required. Please configure the app with Shopify Partner Dashboard.');
      } else {
        setError('Subscription creation failed. This is expected until the app is listed on Shopify App Store.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelection = (planHandle, shopDomain, billingPeriod = selectedBillingPeriod) => {
    if (shopDomain) {
      // Use new production billing system first
      createBillingSubscription(shopDomain, planHandle, billingPeriod);
    } else if (stores.length === 1) {
      createBillingSubscription(stores[0].shop_domain, planHandle, billingPeriod);
    } else {
      setError('Please select a store first');
    }
  };

  const handleUpgrade = async (shopDomain) => {
    try {
      const response = await api.get(`/subscription/upgrade-options/${shopDomain}`);
      if (response.data.success) {
        setUpgradeOptions(response.data.upgrade_options);
        setSelectedStore(shopDomain);
        setShowUpgradeModal(true);
      }
    } catch (err) {
      setError('Failed to load upgrade options');
    }
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return '#dc2626';
    if (percentage >= 75) return '#f59e0b';
    return '#10b981';
  };

  const formatCurrency = (amount, currencyCode = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getYearlySavings = (monthlyPrice, yearlyPrice) => {
    if (!monthlyPrice || !yearlyPrice) return 0;
    const monthlyCost = monthlyPrice * 12;
    const savings = monthlyCost - yearlyPrice;
    // Use banker's rounding for currency
    return Math.round(savings * 100) / 100;
  };

  const getYearlySavingsPercentage = (monthlyPrice, yearlyPrice) => {
    if (!monthlyPrice || !yearlyPrice) return 0;
    const monthlyCost = monthlyPrice * 12;
    const savings = monthlyCost - yearlyPrice;
    const percentage = (savings / monthlyCost) * 100;
    // Round to 1 decimal place
    return Math.round(percentage * 10) / 10;
  };

  const getEffectiveMonthlyPrice = (plan, billingPeriod, currencyCode = 'USD') => {
    if (billingPeriod === 'yearly' && plan.yearly_price) {
      const effectiveMonthly = plan.yearly_price / 12;
      return Math.round(effectiveMonthly * 100) / 100; // Banker's rounding
    }
    return plan.monthly_price || 0;
  };

  const getSubscriptionActionText = (subscription, planHandle, billingPeriod) => {
    if (!subscription || subscription.status === 'trial' || subscription.status === 'trialing') {
      return `Start ${planHandle.charAt(0).toUpperCase() + planHandle.slice(1)} – ${billingPeriod.charAt(0).toUpperCase() + billingPeriod.slice(1)}`;
    }
    
    if (subscription.plan_handle === planHandle && subscription.billing_period === billingPeriod) {
      return 'Manage Plan';
    }
    
    return `Switch to ${planHandle.charAt(0).toUpperCase() + planHandle.slice(1)} – ${billingPeriod.charAt(0).toUpperCase() + billingPeriod.slice(1)}`;
  };

  const shouldShowManageLink = (subscription, planHandle, billingPeriod) => {
    return subscription && 
           subscription.status !== 'trial' && 
           subscription.status !== 'trialing' &&
           subscription.plan_handle === planHandle && 
           subscription.billing_period === billingPeriod;
  };

  // Feature comparison data (updated with Phase 2 data)
  const featureComparison = {
    'Countries Supported': {
      'starter': 'All 57 Countries',
      'growth': 'All 57 Countries',
      'enterprise': 'All 57 Countries'
    },
    'Product Categories': {
      'starter': '60+ Detailed Categories',
      'growth': '60+ Detailed Categories',
      'enterprise': 'Complete HS Database'
    },
    'Calculations/Month': {
      'starter': '750',
      'growth': '2,500',
      'enterprise': '10,000+'
    },
    'Exchange Rate Updates': {
      'starter': 'Daily',
      'growth': 'Daily',
      'enterprise': 'Daily'
    },
    'Support': {
      'starter': 'Email Support',
      'growth': 'Priority Support',
      'enterprise': 'Dedicated Manager'
    },
    'Trade Agreements': {
      'starter': '❌',
      'growth': '✅ Optimization',
      'enterprise': '✅ Full Coverage'
    },
    'Dashboard Access': {
      'starter': 'Full Access',
      'growth': 'Full Access',
      'enterprise': 'Full Access + Priority Features'
    }
  };

  if (loading) {
    return <div className="subscription-loading">Loading subscription data...</div>;
  }

  return (
    <div className="subscription-management">
      {/* Billing Success Banner */}
      {billingSuccess && (
        <div className="alert alert-success" style={{ marginBottom: '20px' }} aria-live="polite">
          <div className="alert-content">
            <span className="alert-icon">🎉</span>
            <div className="alert-text">
              <strong>Subscription Activated!</strong>
              <p>
                Your <strong>{billingSuccess.plan} plan</strong> is now active 
                with <strong>{billingSuccess.period}</strong> billing.
                {billingSuccess.test_mode && (
                  <span className="test-mode-badge" style={{ marginLeft: '8px', padding: '2px 6px', background: '#fef3c7', color: '#92400e', borderRadius: '4px', fontSize: '12px' }}>
                    Test Mode (Dev Store)
                  </span>
                )}
              </p>
            </div>
            <button 
              className="alert-close" 
              onClick={() => setBillingSuccess(null)}
              onKeyDown={(e) => e.key === 'Enter' && setBillingSuccess(null)}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}
              aria-label="Dismiss success message"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Billing Error Banner */}
      {billingError && (
        <div className="alert alert-error" style={{ marginBottom: '20px' }} aria-live="polite">
          <div className="alert-content">
            <span className="alert-icon">⚠️</span>
            <div className="alert-text">
              <strong>Billing Error</strong>
              <p>There was an issue with your subscription. Please try again or contact support.</p>
            </div>
            <button 
              className="alert-close" 
              onClick={() => setBillingError(null)}
              onKeyDown={(e) => e.key === 'Enter' && setBillingError(null)}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}
              aria-label="Dismiss error"
            >
              ×
            </button>
          </div>
          <div className="alert-actions" style={{ marginTop: '10px' }}>
            <button 
              className="btn btn-sm btn-primary" 
              onClick={() => {
                setBillingError(null);
                // Force fresh reload of subscription data instead of just page reload
                loadSubscriptionData();
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Test Mode Global Indicator */}
      {testMode && (
        <div className="test-mode-banner" style={{ 
          background: '#fef3c7', 
          border: '1px solid #f59e0b', 
          padding: '8px 12px', 
          borderRadius: '6px', 
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>🧪</span>
          <span><strong>Test Mode:</strong> You're using a development store. All charges are simulated.</span>
        </div>
      )}

      {/* Header Section */}
      <div className="subscription-header">
        <h2>🌍 Tax & Duty King 👑</h2>
        <p className="subscription-subtitle">Enterprise-grade international tax calculations for Shopify stores</p>
        <div className="subscription-badges">
          <span className="badge">57 Countries</span>
          <span className="badge">60+ Categories</span>
          <span className="badge">Government APIs</span>
          <span className="badge">Real-time Rates</span>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          ❌ {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          ✅ {success}
          <button onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      {/* Usage Banners */}
      {stores.map(store => {
        const subscription = subscriptions[store.shop_domain];
        const shouldShowBanner = subscription && 
          (subscription.at_warning || subscription.at_limit || subscription.grace_active) &&
          !dismissedBanners.has(store.shop_domain);
        
        if (!shouldShowBanner) return null;
        
        return (
          <UsageBanner
            key={`banner-${store.shop_domain}`}
            subscription={subscription}
            onDismiss={() => handleDismissBanner(store.shop_domain)}
            onUpgrade={() => handleBannerUpgrade(store.shop_domain)}
          />
        );
      })}

      {/* Current Subscription Status */}
      {Object.keys(subscriptions).length > 0 && (
        <div className="current-subscriptions">
          <h3>📊 Monthly Usage Tracking</h3>
          <div className="usage-cards-grid">
            {stores.map(store => {
              const subscription = subscriptions[store.shop_domain];
              const usagePercentage = subscription ? subscription.usage_percentage : 0;
              
              return (
                <div key={store.shop_domain} className="enhanced-usage-card">
                  {/* Shop Header with Status */}
                  <div className="usage-card-header">
                    <div className="shop-info">
                      <h4>{store.shop_domain}</h4>
                      <span className="usage-period">Period: {subscription?.period || 'Current Month'}</span>
                    </div>
                    <div className="status-badges">
                      <span className={`status-chip ${getStatusClass(subscription)}`}>
                        {getStatusText(subscription)}
                      </span>
                      {subscription?.grace_active && (
                        <span className="grace-chip">Grace Period</span>
                      )}
                    </div>
                  </div>
                  
                  {subscription ? (
                    <>
                      {/* Usage Progress Section */}
                      <div className="usage-progress-section">
                        <div className="usage-numbers">
                          <span className="usage-current">{subscription.calculations_used}</span>
                          <span className="usage-divider"> / </span>
                          <span className="usage-limit">{subscription.calculations_limit}</span>
                          <span className="usage-label"> calculations</span>
                        </div>
                        
                        <div className="usage-percentage-display">
                          {subscription.usage_percentage.toFixed(1)}% used
                        </div>
                      </div>

                      {/* Enhanced Progress Bar */}
                      <div className="enhanced-progress-bar">
                        <div className="progress-track">
                          <div 
                            className={`progress-fill ${getProgressClass(subscription)}`}
                            style={{ 
                              width: `${Math.min(subscription.usage_percentage, 100)}%`
                            }}
                          ></div>
                          {/* Grace period indicator */}
                          {subscription.grace_active && (
                            <div 
                              className="grace-fill"
                              style={{ 
                                left: '100%',
                                width: `${Math.max(0, subscription.usage_percentage - 100)}%`
                              }}
                            ></div>
                          )}
                        </div>
                        {/* Progress markers */}
                        <div className="progress-markers">
                          <div className="marker warning-marker" style={{ left: '80%' }}>
                            <div className="marker-line"></div>
                            <div className="marker-label">Warning</div>
                          </div>
                          <div className="marker limit-marker" style={{ left: '100%' }}>
                            <div className="marker-line"></div>
                            <div className="marker-label">Limit</div>
                          </div>
                        </div>
                      </div>

                      {/* Status Message */}
                      <div className="usage-status-message">
                        <p>{getUsageMessage(subscription)}</p>
                      </div>

                      {/* Action Buttons */}
                      <div className="usage-actions">
                        {subscription.paused ? (
                          <button 
                            className="btn btn-success btn-sm"
                            onClick={() => handleResumeUsage(store.shop_domain)}
                            disabled={loading}
                          >
                            ▶️ Resume Usage
                          </button>
                        ) : (
                          <>
                            {(subscription.at_warning || subscription.at_limit) && (
                              <button 
                                className="btn btn-secondary btn-sm"
                                onClick={() => handlePauseUsage(store.shop_domain)}
                                disabled={loading}
                              >
                                ⏸️ Pause Usage
                              </button>
                            )}
                          </>
                        )}

                        {(subscription.at_warning || subscription.at_limit || subscription.grace_active) && (
                          <button 
                            className="btn btn-primary btn-sm"
                            onClick={() => handleUpgrade(store.shop_domain)}
                            disabled={loading}
                          >
                            ⚡ Upgrade Plan
                          </button>
                        )}
                      </div>

                      {/* Additional Info */}
                      <div className="usage-meta">
                        <div className="meta-row">
                          <span>Plan: {subscription.plan_handle || 'Starter'}</span>
                          {subscription.resets_at && (
                            <span>Resets: {new Date(subscription.resets_at).toLocaleDateString()}</span>
                          )}
                        </div>
                        {subscription.grace_active && (
                          <div className="grace-info">
                            <small>⚠️ Grace period active (5% buffer) - upgrade to avoid service interruption</small>
                          </div>
                        )}
                      </div>
                    </>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Available Plans Section */}
      <div className="available-plans">
        <h3>💰 Choose Your Plan</h3>
        
        {/* Billing Period Toggle */}
        <div className="billing-period-toggle">
          <div className="toggle-container">
            <button 
              className={selectedBillingPeriod === 'monthly' ? 'active' : ''}
              onClick={() => setSelectedBillingPeriod('monthly')}
            >
              📅 Monthly
            </button>
            <button 
              className={selectedBillingPeriod === 'yearly' ? 'active' : ''}
              onClick={() => setSelectedBillingPeriod('yearly')}
            >
              💰 Yearly 
              <span className="yearly-savings-text">
                (Save up to 17%)
              </span>
            </button>
          </div>
          <div className="billing-period-note">
            {selectedBillingPeriod === 'yearly' 
              ? "💡 Billed annually - 2 months free!" 
              : "💡 Billed every 30 days"}
          </div>
        </div>

        <div className="plans-grid">
          {availablePlans.map(plan => {
            const price = selectedBillingPeriod === 'yearly' ? plan.yearly_price : plan.monthly_price;
            const period = selectedBillingPeriod === 'yearly' ? '/year' : '/month';
            const savings = selectedBillingPeriod === 'yearly' ? getYearlySavings(plan.monthly_price, plan.yearly_price) : 0;
            const savingsPercentage = selectedBillingPeriod === 'yearly' ? getYearlySavingsPercentage(plan.monthly_price, plan.yearly_price) : 0;
            const effectiveMonthlyPrice = getEffectiveMonthlyPrice(plan, selectedBillingPeriod);
            
            return (
              <div key={plan.handle} className={`plan-card ${plan.is_premium ? 'premium' : ''}`}>
                <div className="plan-header">
                  <h4>{plan.name}</h4>
                  {plan.is_premium && <span className="premium-badge">Premium</span>}
                  {plan.handle === 'growth' && <span className="popular-badge">Most Popular</span>}
                </div>
                
                <div className="plan-pricing">
                  <div className="price-display">
                    <span className="price">{formatCurrency(price, 'USD')}</span>
                    <span className="period">{period}</span>
                  </div>
                  {selectedBillingPeriod === 'yearly' && (
                    <div className="effective-monthly">
                      {formatCurrency(effectiveMonthlyPrice, 'USD')}/month billed annually
                    </div>
                  )}
                  {savings > 0 && (
                    <div className="savings-badge">
                      Save {formatCurrency(savings, 'USD')} ({savingsPercentage}%)
                    </div>
                  )}
                </div>
                
                <div className="plan-features">
                  <div className="calculation-limit">
                    <strong>{plan.calculation_limit?.toLocaleString() || '750'}</strong> calculations/month
                  </div>
                  <ul>
                    {plan.features?.map((feature, index) => (
                      <li key={index}>✅ {feature}</li>
                    )) || []}
                  </ul>
                </div>
                
                <div className="plan-footer">
                  <div className="trial-info">🎁 {plan.trial_days || 7}-day free trial</div>
                  <div className="checkout-info">Secure checkout on Shopify</div>
                  
                  {stores.length === 1 ? (
                    <div className="single-store-actions">
                      {(() => {
                        const subscription = subscriptions[stores[0].shop_domain];
                        const billingState = billingStates[stores[0].shop_domain];
                        const isManageMode = shouldShowManageLink(subscription, plan.handle, selectedBillingPeriod);
                        const buttonText = getSubscriptionActionText(subscription, plan.handle, selectedBillingPeriod);
                        const isLoading = billingState?.loading || loading;
                        
                        return (
                          <div>
                            {billingState?.error && (
                              <div className="billing-error" style={{ color: '#dc2626', fontSize: '12px', marginBottom: '8px' }} role="alert">
                                {billingState.error}
                                <button 
                                  className="retry-link" 
                                  style={{ 
                                    marginLeft: '8px', 
                                    color: '#2563eb', 
                                    background: 'none', 
                                    border: 'none', 
                                    textDecoration: 'underline', 
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                  onClick={() => retryBillingSubscription(stores[0].shop_domain, plan.handle, selectedBillingPeriod)}
                                >
                                  Retry
                                </button>
                              </div>
                            )}
                            <button 
                              className={`btn ${isManageMode ? 'btn-secondary' : 'btn-primary'} ${plan.handle === 'growth' ? 'popular-btn' : ''}`}
                              onClick={() => {
                                if (isManageMode) {
                                  // Redirect to Shopify's billing management
                                  window.open(`https://admin.shopify.com/charges`, '_blank');
                                } else {
                                  handlePlanSelection(plan.handle, stores[0].shop_domain, selectedBillingPeriod);
                                }
                              }}
                              disabled={isLoading}
                              aria-busy={isLoading}
                              aria-describedby={isLoading ? `loading-${plan.handle}` : undefined}
                            >
                              {isLoading ? 'Creating...' : buttonText}
                            </button>
                            {isLoading && (
                              <div id={`loading-${plan.handle}`} className="sr-only">
                                Creating subscription, please wait
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="multi-store-selector">
                      <select 
                        className="store-select"
                        onChange={(e) => {
                          if (e.target.value) {
                            handlePlanSelection(plan.handle, e.target.value, selectedBillingPeriod);
                            e.target.value = ""; // Reset selection
                          }
                        }}
                        defaultValue=""
                        disabled={loading}
                      >
                        <option value="">Select Store</option>
                        {stores.map(store => {
                          const subscription = subscriptions[store.shop_domain];
                          const actionText = getSubscriptionActionText(subscription, plan.handle, selectedBillingPeriod);
                          return (
                            <option key={store.shop_domain} value={store.shop_domain}>
                              {store.shop_domain} - {actionText}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Feature Comparison Table */}
      <div className="feature-comparison">
        <h3>📊 Detailed Feature Comparison</h3>
        <div className="comparison-table">
          <table>
            <thead>
              <tr>
                <th>Feature</th>
                <th>Starter</th>
                <th className="popular-header">Growth</th>
                <th>Enterprise</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(featureComparison).map(([feature, values]) => (
                <tr key={feature}>
                  <td className="feature-name">{feature}</td>
                  <td>{values.starter}</td>
                  <td className="popular-cell">{values.growth}</td>
                  <td>{values.enterprise}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Value Propositions */}
      <div className="value-propositions">
        <div className="value-prop">
          <h4>🚀 Fast Integration</h4>
          <p>Install in minutes with our Shopify app. No coding required.</p>
        </div>
        <div className="value-prop">
          <h4>🎯 Reduce Returns</h4>
          <p>Transparent import costs reduce customer surprises and returns.</p>
        </div>
        <div className="value-prop">
          <h4>🌍 Global Accuracy</h4>
          <p>Real-time government rates ensure compliance across 57 countries.</p>
        </div>
        <div className="value-prop">
          <h4>💰 Increase Sales</h4>
          <p>Show exact total costs upfront to boost international conversions.</p>
        </div>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>🚀 Upgrade Subscription</h3>
              <button className="modal-close" onClick={() => setShowUpgradeModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <p>Choose upgrade plan for <strong>{selectedStore}</strong>:</p>
              
              <div className="upgrade-options">
                {upgradeOptions.map(option => {
                  // Calculate yearly savings: (monthly * 12) - yearly
                  const yearlySavings = (option.monthly_price * 12) - option.yearly_price;
                  
                  return (
                    <div key={option.handle} className="upgrade-option">
                      <div className="option-header">
                        <h4>{option.name}</h4>
                        <div className="option-pricing">
                          <span className="monthly">{formatCurrency(option.monthly_price)}/month</span>
                          <span className="yearly">{formatCurrency(option.yearly_price)}/year</span>
                        </div>
                      </div>
                      
                      <div className="option-features">
                        <div className="calculation-limit">
                          <strong>{option.calculation_limit.toLocaleString()}</strong> calculations/month
                        </div>
                        <ul>
                          {option.features.slice(0, 3).map((feature, index) => (
                            <li key={index}>✅ {feature}</li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="option-actions">
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={() => createSubscription(selectedStore, option.handle, 'monthly')}
                        >
                          📅 Monthly
                        </button>
                        <button 
                          className="btn btn-primary btn-sm"
                          onClick={() => createSubscription(selectedStore, option.handle, 'yearly')}
                        >
                          💰 Yearly (Save {formatCurrency(yearlySavings)})
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// HS-code learning route — contextual guidance paired with live merchant actions.
// ─────────────────────────────────────────────────────────────────────────────
const HSCodesLearningGuide = ({ open, onToggle, onShowCatalogue, onShowReview, onShowWriteBack }) => {
  if (!open) {
    return <button type="button" className="hs-learning-reopen" onClick={onToggle}><span>i</span> How HS classification supports accurate estimates</button>;
  }

  const steps = [
    { number: '01', title: 'Sync products', detail: 'Bring your active Shopify catalogue into Tax & Duty King so every product can be reviewed in one place.', action: 'Show product sync', onClick: onShowCatalogue },
    { number: '02', title: 'Classify products', detail: 'Use a suggestion or add an expert code. The HS code describes the product for customs purposes; it is not a tax rate.', action: 'Show unclassified products', onClick: onShowCatalogue },
    { number: '03', title: 'Review and approve', detail: 'Check the description, material, use and code. Approval is your confirmation that the code is suitable for the product.', action: 'Show classification review', onClick: onShowReview },
    { number: '04', title: 'Write approved codes back', detail: 'Push only confirmed codes to Shopify so your product data stays aligned and future estimates have the information they need.', action: 'Show Shopify write-back', onClick: onShowWriteBack },
  ];

  return (
    <section className="hs-learning-guide" aria-label="HS code learning guide">
      <div className="hs-learning-guide__intro">
        <div><p className="hs-learning-guide__eyebrow">Product data / learning route</p><h2>Why classify products before you trust an estimate?</h2></div>
        <button type="button" onClick={onToggle} className="hs-learning-guide__close" aria-label="Hide HS code learning guide">×</button>
      </div>
      <p className="hs-learning-guide__lead"><strong>HS codes are a fundamental part of product information.</strong> Tax &amp; Duty King combines a product’s HS code, shipping origin and shopper destination to calculate the duty and tax information used in an import-cost estimate. Without a reviewed code, the estimate can be incomplete or based on weaker product data.</p>
      <div className="hs-learning-guide__notice"><span>✦</span><p><b>Merchant review matters.</b> Automated suggestions help you work faster, but they are not a substitute for checking the product’s correct customs classification. When a product is unusual, composite, regulated or uncertain, get specialist advice before approval.</p></div>
      <div className="hs-learning-guide__steps">
        {steps.map((step) => (
          <article key={step.number} className="hs-learning-guide__step">
            <span className="hs-learning-guide__number">{step.number}</span>
            <div><h3>{step.title}</h3><p>{step.detail}</p><button type="button" onClick={step.onClick}>{step.action} <span>↓</span></button></div>
          </article>
        ))}
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// HS Codes Manager — AI Classification + Merchant Override Dashboard Tab
// ─────────────────────────────────────────────────────────────────────────────
const HSCodesManager = ({ shopDomain, backendUrl }) => {
  const [classifications, setClassifications] = React.useState([]);
  const [usage, setUsage] = React.useState({ used: 0, limit: 50, unlimited: false, remaining: 50, plan: 'starter' });
  const [loading, setLoading] = React.useState(true);
  const [classifying, setClassifying] = React.useState({});
  const [editingId, setEditingId] = React.useState(null);
  const [editForm, setEditForm] = React.useState({ hs_code: '', hs_description: '', duty_rate_overrides: {} });
  const [manualForm, setManualForm] = React.useState({ product_id: '', product_title: '', hs_code: '', hs_description: '' });
  const [showManualAdd, setShowManualAdd] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [selectedClassifications, setSelectedClassifications] = React.useState(new Set());
  const [bulkApproving, setBulkApproving] = React.useState(false);

  // ── Product Browser state ──────────────────────────────────────────────────
  const [catalogue, setCatalogue] = React.useState({ status: 'missing', products: [], total_count: 0, total_pages: 1, updated_at: null });
  const [catPage, setCatPage] = React.useState(1);
  const [catSearch, setCatSearch] = React.useState('');
  const [catUnclassifiedOnly, setCatUnclassifiedOnly] = React.useState(false);
  const [catProductType, setCatProductType] = React.useState('');
  const [productTypes, setProductTypes] = React.useState([]);
  const [catLoading, setCatLoading] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);
  const [selectedProducts, setSelectedProducts] = React.useState(new Set());
  const [learningOpen, setLearningOpen] = React.useState(() => {
    try { return localStorage.getItem(`tdk_hs_learning_hidden_${shopDomain}`) !== 'true'; } catch (_) { return true; }
  });
  const [batchClassifying, setBatchClassifying] = React.useState(false);
  const [batchProgress, setBatchProgress] = React.useState({ done: 0, total: 0 });
  const [showBulkOverride, setShowBulkOverride] = React.useState(false);
  const [bulkOverrideForm, setBulkOverrideForm] = React.useState({ hs_code: '', hs_description: '', duty_rate_overrides: {} });
  const [showBrowser, setShowBrowser] = React.useState(true);

  // ── Shopify Sync state ────────────────────────────────────────────────────
  const [shopifySync, setShopifySync] = React.useState(null);
  const [shopifySyncing, setShopifySyncing] = React.useState(false);
  const [syncJob, setSyncJob] = React.useState(null); // active background job
  const syncPollRef = React.useRef(null);             // polling interval ref

  // ── Shopify HS Import (pull native HS codes from Shopify → TDK) ───────────
  const [importing, setImporting] = React.useState(false);
  const [importJobId, setImportJobId] = React.useState(null);
  const importPollRef = React.useRef(null);

  const API = backendUrl;
  const apiKey = process.env.REACT_APP_API_KEY || '';

  const headers = {
    'Content-Type': 'application/json',
    'x-api-key': apiKey,
  };

  const loadClassifications = React.useCallback(async () => {
    if (!shopDomain) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ shop_domain: shopDomain, page, page_size: 50 });
      if (statusFilter) params.append('status', statusFilter);
      const res = await fetch(`${API}/api/hs-classifications?${params}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setClassifications(data.classifications || []);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
      setUsage({ used: data.used, limit: data.limit, unlimited: data.unlimited, remaining: data.remaining, plan: data.plan });
    } catch (e) {
      setError('Failed to load classifications: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [shopDomain, page, statusFilter]);

  React.useEffect(() => { loadClassifications(); }, [loadClassifications]);

  const handleClassify = async (productId, productTitle, vendor = '', productType = '', tags = []) => {
    if (!shopDomain) return;
    setClassifying(prev => ({ ...prev, [productId]: true }));
    setError('');
    try {
      const res = await fetch(`${API}/api/hs-classify`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          shop_domain: shopDomain,
          product_id: productId,
          product_title: productTitle,
          vendor,
          product_type: productType,
          tags,
          force_reclassify: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 402) {
          setError(data.detail?.message || 'Classification limit reached. Please upgrade your plan.');
        } else {
          // data.detail may be an object — safely extract message string
          const errDetail = data.detail;
          const errMsg = typeof errDetail === 'string'
            ? errDetail
            : (errDetail?.message || errDetail?.error || JSON.stringify(errDetail) || 'Classification failed');
          setError(errMsg);
        }
        return;
      }
      setSuccess(`Classified "${productTitle}" as HS ${data.hs_code}`);
      if (data.used !== undefined) {
        setUsage({ used: data.used, limit: data.limit, unlimited: data.unlimited, remaining: data.remaining, plan: data.plan });
      }
      await loadClassifications();
      await loadCatalogue();
    } catch (e) {
      setError('Classification failed: ' + e.message);
    } finally {
      setClassifying(prev => ({ ...prev, [productId]: false }));
    }
  };

  const safeErrMsg = (data, fallback) => {
    if (!data) return fallback;
    if (typeof data === 'string') return data;
    return data.message || data.detail?.message || data.error || fallback;
  };

  const handleApprove = async (productId) => {
    try {
      const res = await fetch(`${API}/api/hs-classifications/${productId}/approve`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ shop_domain: shopDomain }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(safeErrMsg(d, `HTTP ${res.status}`)); }
      setSuccess('Classification approved');
      await loadClassifications();
      await loadCatalogue();
    } catch (e) {
      setError('Approve failed: ' + e.message);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedClassifications.size === 0) return;
    setBulkApproving(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/hs-classifications/bulk-approve`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          shop_domain: shopDomain,
          product_ids: Array.from(selectedClassifications),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(safeErrMsg(data, `HTTP ${res.status}`));
      setSuccess(`Approved ${data.approved} classification${data.approved !== 1 ? 's' : ''}${data.skipped > 0 ? ` (${data.skipped} skipped — already approved/overridden)` : ''}`);
      setSelectedClassifications(new Set());
      await loadClassifications();
    } catch (e) {
      setError('Bulk approve failed: ' + e.message);
    } finally {
      setBulkApproving(false);
    }
  };

  const handleStartEdit = (item) => {
    setEditingId(item.product_id);
    setEditForm({
      hs_code: item.hs_code || '',
      hs_description: item.hs_description || '',
      duty_rate_overrides: item.duty_rate_overrides || {},
    });
  };

  const handleSaveOverride = async (productId) => {
    try {
      const res = await fetch(`${API}/api/hs-classifications/${productId}/override`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          shop_domain: shopDomain,
          hs_code: editForm.hs_code,
          hs_description: editForm.hs_description,
          duty_rate_overrides: editForm.duty_rate_overrides,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSuccess('Override saved');
      setEditingId(null);
      await loadClassifications();
    } catch (e) {
      setError('Save failed: ' + e.message);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Remove this classification? The product can be re-classified.')) return;
    try {
      const res = await fetch(`${API}/api/hs-classifications/${productId}?shop_domain=${encodeURIComponent(shopDomain)}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSuccess('Classification removed');
      await loadClassifications();
    } catch (e) {
      setError('Delete failed: ' + e.message);
    }
  };

  const handleManualAdd = async () => {
    if (!manualForm.product_id || !manualForm.hs_code) {
      setError('Product ID and HS Code are required');
      return;
    }
    try {
      const res = await fetch(`${API}/api/hs-classifications/${manualForm.product_id}/override`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          shop_domain: shopDomain,
          hs_code: manualForm.hs_code,
          hs_description: manualForm.hs_description,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSuccess('Manual classification added');
      setManualForm({ product_id: '', product_title: '', hs_code: '', hs_description: '' });
      setShowManualAdd(false);
      await loadClassifications();
    } catch (e) {
      setError('Failed to add: ' + e.message);
    }
  };

  const updateDutyOverride = (country, value) => {
    setEditForm(prev => ({
      ...prev,
      duty_rate_overrides: { ...prev.duty_rate_overrides, [country]: parseFloat(value) || 0 },
    }));
  };

  const removeDutyOverride = (country) => {
    setEditForm(prev => {
      const updated = { ...prev.duty_rate_overrides };
      delete updated[country];
      return { ...prev, duty_rate_overrides: updated };
    });
  };

  // ── Product Browser handlers ──────────────────────────────────────────────

  const loadShopifySyncStatus = React.useCallback(async () => {
    if (!shopDomain) return;
    try {
      const res = await fetch(`${API}/api/hs-shopify-sync/status?shop=${encodeURIComponent(shopDomain)}`, { headers });
      if (!res.ok) return;
      const data = await res.json();
      setShopifySync(data);
    } catch (e) { /* ignore */ }
  }, [shopDomain]);

  // Poll a background sync job until it completes
  const startJobPolling = React.useCallback((jobId) => {
    if (syncPollRef.current) clearInterval(syncPollRef.current);
    // Capture stable values at poll-start time to avoid stale closure issues
    const pollUrl = `${API}/api/hs-shopify-sync/job/${jobId}`;
    const pollHeaders = {
      'Content-Type': 'application/json',
      'x-api-key': process.env.REACT_APP_API_KEY || '',
    };
    syncPollRef.current = setInterval(async () => {
      try {
        const res = await fetch(pollUrl, { headers: pollHeaders });
        if (!res.ok) return;
        const job = await res.json();
        setSyncJob(job);
        if (job.status === 'complete' || job.status === 'failed') {
          clearInterval(syncPollRef.current);
          syncPollRef.current = null;
          setShopifySyncing(false);
          if (job.status === 'complete') {
            setSuccess(
              `✅ Pushed ${job.synced} HS code${job.synced !== 1 ? 's' : ''} to Shopify` +
              (job.errors > 0 ? ` (${job.errors} errors)` : '') +
              ` — ${job.total} products processed.`
            );
          } else {
            setError('Shopify sync failed: ' + (job.error || 'Unknown error'));
          }
          await loadShopifySyncStatus();
          await loadClassifications();
          // Clear job banner after 8 seconds
          setTimeout(() => setSyncJob(null), 8000);
        }
      } catch (e) { /* ignore poll errors */ }
    }, 2000); // poll every 2 seconds
  }, [API, loadShopifySyncStatus, loadClassifications]);

  const handlePushAllToShopify = async () => {
    if (!shopDomain) return;
    setShopifySyncing(true);
    setSyncJob(null);
    setError('');
    try {
      const res = await fetch(`${API}/api/hs-shopify-sync`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ shop_domain: shopDomain, only_confirmed: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(safeErrMsg(data, `HTTP ${res.status}`));
      if (data.status === 'no_records') {
        setError('No confirmed classifications to sync. Approve some AI suggestions first.');
        setShopifySyncing(false);
      } else {
        // Background job started — begin polling
        setSyncJob({ job_id: data.job_id, status: 'queued', total: 0, processed: 0, synced: 0, errors: 0, percent: 0 });
        startJobPolling(data.job_id);
      }
    } catch (e) {
      setError('Shopify sync failed: ' + e.message);
      setShopifySyncing(false);
    }
  };

  const loadCatalogue = React.useCallback(async () => {
    if (!shopDomain) return;
    setCatLoading(true);
    try {
      const params = new URLSearchParams({
        shop_domain: shopDomain,
        page: catPage,
        limit: 50,
        search: catSearch,
        unclassified_only: catUnclassifiedOnly,
        product_type: catProductType,
      });
      const res = await fetch(`${API}/api/hs-products?${params}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCatalogue(data);
    } catch (e) {
      // Silently fail — catalogue is optional; show missing state
      setCatalogue(prev => ({ ...prev, status: 'missing' }));
    } finally {
      setCatLoading(false);
    }
  }, [shopDomain, catPage, catSearch, catUnclassifiedOnly, catProductType]);

  const loadProductTypes = React.useCallback(async () => {
    if (!shopDomain) return;
    try {
      const res = await fetch(`${API}/api/hs-products/types?shop_domain=${encodeURIComponent(shopDomain)}`, { headers });
      if (!res.ok) return;
      const data = await res.json();
      setProductTypes(data.product_types || []);
    } catch (e) { /* ignore */ }
  }, [shopDomain]);

  React.useEffect(() => { loadCatalogue(); }, [loadCatalogue]);
  React.useEffect(() => { loadProductTypes(); }, [loadProductTypes]);
  React.useEffect(() => { loadShopifySyncStatus(); }, [loadShopifySyncStatus]);

  // On mount, check if there's an active sync job on the server
  // (e.g. the user navigated away while a sync was running) and
  // resume polling so the progress bar updates correctly.
  // Note: HSCodesManager only mounts when the HS Codes tab is active,
  // so no activeTab check is needed here.
  React.useEffect(() => {
    // If we're already polling, don't start a second interval
    if (syncPollRef.current) return;
    const resumeIfActive = async () => {
      try {
        if (!shopDomain) return;
        const res = await fetch(
          `${API}/api/hs-shopify-sync/status?shop=${encodeURIComponent(shopDomain)}`,
          { headers }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (data.active_job && (data.active_job.status === 'running' || data.active_job.status === 'queued')) {
          setSyncJob(data.active_job);
          setShopifySyncing(true);
          startJobPolling(data.active_job.job_id);
        }
      } catch (e) { /* ignore */ }
    };
    resumeIfActive();
  }, [shopDomain]); // eslint-disable-line

  const handleImportFromShopify = async () => {
    if (!shopDomain) return;
    setImporting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/shopify-hs-import`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ shop_domain: shopDomain }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(safeErrMsg(data, `HTTP ${res.status}`));
      const jid = data.job_id;
      setImportJobId(jid);
      // Poll for completion
      if (importPollRef.current) clearInterval(importPollRef.current);
      importPollRef.current = setInterval(async () => {
        try {
          const pr = await fetch(`${API}/api/shopify-hs-import/job/${jid}`, { headers });
          if (!pr.ok) return;
          const pdata = await pr.json();
          if (pdata.status === 'complete' || pdata.status === 'failed') {
            clearInterval(importPollRef.current);
            importPollRef.current = null;
            setImporting(false);
            setImportJobId(null);
            if (pdata.status === 'complete') {
              setSuccess(`✅ Imported ${pdata.imported} HS code${pdata.imported !== 1 ? 's' : ''} from Shopify (${pdata.skipped} products had no HS code set)`);
              await loadClassifications();
              await loadCatalogue();
            } else {
              setError('Import failed: ' + (pdata.error || 'Unknown error'));
            }
          }
        } catch (e) { /* ignore poll errors */ }
      }, 2000);
    } catch (e) {
      setError('Import failed: ' + e.message);
      setImporting(false);
    }
  };

  const handleSync = async () => {
    if (!shopDomain) return;
    setSyncing(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/hs-products/sync?shop_domain=${encodeURIComponent(shopDomain)}`, {
        method: 'POST', headers,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSuccess(`Synced ${data.product_count} products in ${data.scan_stats?.duration_seconds || 0}s`);
      setSelectedProducts(new Set());
      await loadCatalogue();
      await loadProductTypes();
    } catch (e) {
      setError('Sync failed: ' + e.message);
    } finally {
      setSyncing(false);
    }
  };

  const toggleProduct = (id) => {
    setSelectedProducts(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedProducts.size === catalogue.products.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(catalogue.products.map(p => p.id)));
    }
  };

  const handleBatchClassify = async () => {
    if (selectedProducts.size === 0) return;
    const ids = Array.from(selectedProducts);
    setBatchClassifying(true);
    setBatchProgress({ done: 0, total: ids.length });
    setError('');
    // Process in batches of 10
    const batchSize = 10;
    let done = 0;
    let limitHit = false;
    for (let i = 0; i < ids.length; i += batchSize) {
      if (limitHit) break;
      const batch = ids.slice(i, i + batchSize);
      try {
        const res = await fetch(`${API}/api/hs-products/classify-batch`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ shop_domain: shopDomain, product_ids: batch }),
        });
        const data = await res.json();
        if (data.results) {
          done += data.results.filter(r => r.status === 'classified').length;
          setBatchProgress({ done, total: ids.length });
          if (data.usage) setUsage(data.usage);
          limitHit = data.results.some(r => r.status === 'limit_reached');
          if (limitHit) setError('Classification limit reached. Upgrade your plan to classify more products.');
        }
      } catch (e) {
        setError('Batch classify error: ' + e.message);
        break;
      }
    }
    setBatchClassifying(false);
    setSelectedProducts(new Set());
    if (!limitHit) setSuccess(`Classified ${done} product${done !== 1 ? 's' : ''} successfully`);
    await loadCatalogue();
    await loadClassifications();
  };

  const handleBulkOverride = async () => {
    if (selectedProducts.size === 0 || !bulkOverrideForm.hs_code) return;
    try {
      const res = await fetch(`${API}/api/hs-products/bulk-override`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          shop_domain: shopDomain,
          product_ids: Array.from(selectedProducts),
          hs_code: bulkOverrideForm.hs_code,
          hs_description: bulkOverrideForm.hs_description,
          duty_rate_overrides: bulkOverrideForm.duty_rate_overrides,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSuccess(`Applied HS ${bulkOverrideForm.hs_code} to ${data.summary?.overridden || 0} products`);
      setShowBulkOverride(false);
      setBulkOverrideForm({ hs_code: '', hs_description: '', duty_rate_overrides: {} });
      setSelectedProducts(new Set());
      await loadCatalogue();
      await loadClassifications();
    } catch (e) {
      setError('Bulk override failed: ' + e.message);
    }
  };

  const statusBadge = (status) => {
    const map = {
      ai_suggested: { label: 'AI Suggested', color: '#2563eb', bg: '#dbeafe' },
      merchant_approved: { label: 'Approved', color: '#16a34a', bg: '#dcfce7' },
      merchant_overridden: { label: 'Overridden', color: '#7c3aed', bg: '#ede9fe' },
      shopify_native: { label: '🛍️ Shopify Native', color: '#0d6efd', bg: '#e0f0ff' },
    };
    const s = map[status] || { label: status, color: '#6b7280', bg: '#f3f4f6' };
    return (
      <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '12px', color: s.color, background: s.bg }}>
        {s.label}
      </span>
    );
  };

  const confidenceBar = (confidence) => {
    const pct = Math.round((confidence || 0) * 100);
    const color = pct >= 85 ? '#16a34a' : pct >= 60 ? '#d97706' : '#dc2626';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <div style={{ width: '60px', height: '6px', background: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: '3px' }} />
        </div>
        <span style={{ fontSize: '11px', color: '#6b7280' }}>{pct}%</span>
      </div>
    );
  };

  const usagePct = usage.unlimited ? 0 : Math.min(100, Math.round((usage.used / (usage.limit || 1)) * 100));
  const usageColor = usagePct >= 90 ? '#dc2626' : usagePct >= 70 ? '#d97706' : '#16a34a';
  const toggleLearning = () => {
    setLearningOpen((wasOpen) => {
      const nextOpen = !wasOpen;
      try { localStorage.setItem(`tdk_hs_learning_hidden_${shopDomain}`, String(!nextOpen)); } catch (_) {}
      return nextOpen;
    });
  };
  const revealHsSection = (sectionId, options = {}) => {
    if (options.browser) setShowBrowser(true);
    if (options.unclassified) {
      setCatUnclassifiedOnly(true);
      setCatPage(1);
    }
    window.setTimeout(() => document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };

  if (!shopDomain) {
    return (
      <div className="card" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
        <p>No store connected. Please complete setup first.</p>
      </div>
    );
  }

  return (
    <div>
      <HSCodesLearningGuide
        open={learningOpen}
        onToggle={toggleLearning}
        onShowCatalogue={() => revealHsSection('tdk-hs-catalogue', { browser: true, unclassified: true })}
        onShowReview={() => revealHsSection('tdk-hs-review')}
        onShowWriteBack={() => revealHsSection('tdk-hs-shopify-sync')}
      />

      {/* ── Product Browser Panel ─────────────────────────────────────────── */}
      <div id="tdk-hs-catalogue" className="card" style={{ marginBottom: '16px', scrollMarginTop: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>Product Catalogue</h3>
            <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#6b7280' }}>
              {catalogue.status === 'missing'
                ? 'Not synced yet — click Sync Products to import your catalogue'
                : `${catalogue.total_count} product${catalogue.total_count !== 1 ? 's' : ''} · Last synced ${catalogue.updated_at ? new Date(catalogue.updated_at).toLocaleString() : 'unknown'}`
              }
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setShowBrowser(v => !v)}
              style={{ fontSize: '12px' }}
            >
              {showBrowser ? 'Hide Browser' : 'Show Browser'}
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleSync}
              disabled={syncing}
              style={{ minWidth: '130px' }}
            >
              {syncing ? '⏳ Syncing...' : '🔄 Sync Products'}
            </button>
          </div>
        </div>

        {showBrowser && (
          <>
            {/* Search + filters */}
            <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                className="form-control"
                placeholder="🔍 Search by title, vendor, type or tag..."
                value={catSearch}
                onChange={e => { setCatSearch(e.target.value); setCatPage(1); }}
                style={{ flex: '1', minWidth: '200px' }}
              />
              {productTypes.length > 0 && (
                <select
                  className="form-control"
                  value={catProductType}
                  onChange={e => { setCatProductType(e.target.value); setCatPage(1); }}
                  style={{ width: '180px' }}
                >
                  <option value="">All product types</option>
                  {productTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <input
                  type="checkbox"
                  checked={catUnclassifiedOnly}
                  onChange={e => { setCatUnclassifiedOnly(e.target.checked); setCatPage(1); }}
                />
                Unclassified only
              </label>
            </div>

            {/* Bulk action bar — shown when products are selected */}
            {selectedProducts.size > 0 && (
              <div style={{ marginTop: '10px', padding: '10px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#1d4ed8' }}>
                  {selectedProducts.size} product{selectedProducts.size !== 1 ? 's' : ''} selected
                </span>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleBatchClassify}
                  disabled={batchClassifying}
                >
                  {batchClassifying
                    ? `⏳ Classifying ${batchProgress.done}/${batchProgress.total}...`
                    : `✨ Classify with AI (${selectedProducts.size})`
                  }
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowBulkOverride(v => !v)}
                  style={{ color: '#7c3aed', borderColor: '#c4b5fd' }}
                >
                  🏷 Assign HS Code ({selectedProducts.size})
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setSelectedProducts(new Set())}
                  style={{ fontSize: '11px' }}
                >
                  Clear
                </button>
              </div>
            )}

            {/* Bulk override form */}
            {showBulkOverride && selectedProducts.size > 0 && (
              <div style={{ marginTop: '10px', padding: '14px', background: '#faf5ff', border: '2px solid #7c3aed', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 10px', fontSize: '14px', fontWeight: 700, color: '#7c3aed' }}>
                  Assign HS Code to {selectedProducts.size} selected products
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', marginBottom: '10px' }}>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '3px' }}>HS Code *</label>
                    <input
                      className="form-control"
                      placeholder="e.g. 8712.00"
                      value={bulkOverrideForm.hs_code}
                      onChange={e => setBulkOverrideForm(p => ({ ...p, hs_code: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '3px' }}>HS Description</label>
                    <input
                      className="form-control"
                      placeholder="e.g. Bicycles, not motorised"
                      value={bulkOverrideForm.hs_description}
                      onChange={e => setBulkOverrideForm(p => ({ ...p, hs_description: e.target.value }))}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-primary btn-sm" onClick={handleBulkOverride} disabled={!bulkOverrideForm.hs_code}>Apply to All Selected</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setShowBulkOverride(false)}>Cancel</button>
                </div>
              </div>
            )}

            {/* Product list */}
            {catalogue.status === 'missing' ? (
              <div style={{ marginTop: '16px', padding: '24px', textAlign: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: '8px', border: '1px dashed #d1d5db' }}>
                <p style={{ margin: 0, fontSize: '14px' }}>Click <strong>Sync Products</strong> to import your Shopify product catalogue.</p>
                <p style={{ margin: '4px 0 0', fontSize: '12px' }}>Only active products are imported. ~200 bytes per product stored in our database.</p>
              </div>
            ) : catLoading ? (
              <div style={{ marginTop: '16px', padding: '24px', textAlign: 'center', color: '#6b7280' }}>Loading products...</div>
            ) : catalogue.products.length === 0 ? (
              <div style={{ marginTop: '16px', padding: '24px', textAlign: 'center', color: '#9ca3af' }}>
                No products match your search.
              </div>
            ) : (
              <div style={{ marginTop: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      <th style={{ padding: '8px 12px', width: '36px' }}>
                        <input
                          type="checkbox"
                          checked={selectedProducts.size === catalogue.products.length && catalogue.products.length > 0}
                          onChange={toggleSelectAll}
                        />
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Product</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Type</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Status</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catalogue.products.map((p, idx) => (
                      <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6', background: selectedProducts.has(p.id) ? '#eff6ff' : idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={{ padding: '8px 12px' }}>
                          <input type="checkbox" checked={selectedProducts.has(p.id)} onChange={() => toggleProduct(p.id)} />
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <div style={{ fontWeight: 600, color: '#111827', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</div>
                          {p.vendor && <div style={{ fontSize: '11px', color: '#9ca3af' }}>{p.vendor}</div>}
                          <div style={{ fontSize: '10px', color: '#d1d5db', fontFamily: 'monospace' }}>ID: {p.id}</div>
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <span style={{ fontSize: '12px', background: '#f3f4f6', padding: '2px 8px', borderRadius: '4px', color: '#374151' }}>
                            {p.product_type || '—'}
                          </span>
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          {p.has_classification
                            ? <span style={{ fontSize: '11px', fontWeight: 600, color: '#16a34a', background: '#dcfce7', padding: '2px 8px', borderRadius: '12px' }}>✅ Classified</span>
                            : <span style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: '12px' }}>Unclassified</span>
                          }
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            style={{ fontSize: '11px', padding: '3px 8px' }}
                            disabled={classifying[p.id] || batchClassifying}
                            onClick={() => handleClassify(p.id, p.title, p.vendor, p.product_type, p.tags)}
                          >
                            {classifying[p.id] ? '⏳' : '✨ AI Classify'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Catalogue pagination */}
                {catalogue.total_pages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', padding: '10px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
                    <button className="btn btn-secondary btn-sm" disabled={catPage <= 1} onClick={() => setCatPage(p => p - 1)}>← Prev</button>
                    <span style={{ padding: '6px 12px', fontSize: '13px', color: '#6b7280' }}>Page {catPage} of {catalogue.total_pages}</span>
                    <button className="btn btn-secondary btn-sm" disabled={catPage >= catalogue.total_pages} onClick={() => setCatPage(p => p + 1)}>Next →</button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Header */}
      <div id="tdk-hs-review" className="card" style={{ marginBottom: '16px', scrollMarginTop: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700 }}>HS Code Classification</h2>
            <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>
              AI-powered product classification for accurate duty calculations. Approve or override AI suggestions.
            </p>
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowManualAdd(v => !v)}
          >
            + Add Manual Classification
          </button>
        </div>

        {/* Usage bar */}
        <div style={{ marginTop: '16px', padding: '12px 16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
              AI Classifications Used
            </span>
            <span style={{ fontSize: '13px', color: usageColor, fontWeight: 600 }}>
              {usage.unlimited ? `${usage.used} (Unlimited)` : `${usage.used} / ${usage.limit}`}
            </span>
          </div>
          {!usage.unlimited && (
            <>
              <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ width: `${usagePct}%`, height: '100%', background: usageColor, borderRadius: '4px', transition: 'width 0.3s' }} />
              </div>
              {usagePct >= 80 && (
                <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#d97706' }}>
                  ⚠️ {usage.remaining} classifications remaining on your {usage.plan} plan.
                  {usagePct >= 100 ? ' Upgrade to Growth for 500 classifications.' : ''}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Shopify Sync Status Card */}
      <div id="tdk-hs-shopify-sync" className="card" style={{ marginBottom: '16px', border: '1px solid #d1fae5', background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)', scrollMarginTop: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#065f46', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}>🛍️</span> Shopify Product Sync
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: '#047857' }}>
              {shopifySync
                ? `${shopifySync.synced_count} product${shopifySync.synced_count !== 1 ? 's' : ''} synced to Shopify · ${shopifySync.pending_count} pending · Last sync: ${shopifySync.last_sync_at ? new Date(shopifySync.last_sync_at).toLocaleString() : 'Never'}`
                : 'Push confirmed HS codes to Shopify product variants for accurate customs & shipping'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {shopifySync && shopifySync.pending_count > 0 && !shopifySyncing && (
              <span style={{ fontSize: '12px', fontWeight: 600, background: '#fef3c7', color: '#92400e', padding: '3px 10px', borderRadius: '12px', border: '1px solid #fcd34d' }}>
                {shopifySync.pending_count} not yet synced
              </span>
            )}
            <button
              className="btn btn-sm"
              onClick={handleImportFromShopify}
              disabled={importing || shopifySyncing}
              title="Pull HS codes already set on products in Shopify admin into TDK"
              style={{ background: importing ? '#6b7280' : '#0d6efd', color: '#fff', border: 'none', fontWeight: 600, minWidth: '180px', cursor: (importing || shopifySyncing) ? 'not-allowed' : 'pointer' }}
            >
              {importing ? '⏳ Importing…' : '⬇️ Import from Shopify'}
            </button>
            <button
              className="btn btn-sm"
              onClick={handlePushAllToShopify}
              disabled={shopifySyncing || importing}
              style={{ background: shopifySyncing ? '#6b7280' : '#059669', color: '#fff', border: 'none', fontWeight: 600, minWidth: '180px', cursor: (shopifySyncing || importing) ? 'not-allowed' : 'pointer' }}
            >
              {shopifySyncing ? '⏳ Syncing…' : '🔗 Push All to Shopify'}
            </button>
          </div>
        </div>

        {/* Progress banner — shown while job is running */}
        {syncJob && (syncJob.status === 'queued' || syncJob.status === 'running') && (
          <div style={{ marginTop: '12px', padding: '12px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e40af' }}>
                {syncJob.status === 'queued' ? '⏳ Preparing sync…' : `🔄 Syncing HS codes to Shopify — ${syncJob.processed} of ${syncJob.total || '?'} products`}
              </span>
              <span style={{ fontSize: '12px', color: '#3b82f6', fontWeight: 700 }}>{syncJob.percent || 0}%</span>
            </div>
            {/* Progress bar */}
            <div style={{ height: '8px', background: '#dbeafe', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${syncJob.percent || 0}%`,
                background: 'linear-gradient(90deg, #3b82f6, #5c6ac4)',
                borderRadius: '4px',
                transition: 'width 0.4s ease',
              }} />
            </div>
            <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#6b7280' }}>
              ✅ {syncJob.synced || 0} synced · ❌ {syncJob.errors || 0} errors · <strong style={{ color: '#1e40af' }}>Safe to navigate away</strong> — the sync runs on the server and will continue in the background.
            </p>
          </div>
        )}

        {/* Completion banner — shown briefly after job finishes */}
        {syncJob && syncJob.status === 'complete' && (
          <div style={{ marginTop: '12px', padding: '10px 14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', fontSize: '13px', color: '#166534', fontWeight: 600 }}>
            ✅ Sync complete — {syncJob.synced} product{syncJob.synced !== 1 ? 's' : ''} updated in Shopify{syncJob.errors > 0 ? ` · ${syncJob.errors} errors` : ''}.
          </div>
        )}

        {shopifySync && shopifySync.needs_reauth && (
          <div style={{ marginTop: '10px', padding: '8px 12px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', fontSize: '12px', color: '#991b1b' }}>
            ⚠️ <strong>Re-authorisation required.</strong> The app needs <code>write_inventory</code> permission to push HS codes to Shopify.
            Please reinstall the app from the Shopify App Store to grant this permission.
          </div>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error" style={{ marginBottom: '12px' }}>
          ❌ {error}
          <button onClick={() => setError('')} style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>×</button>
        </div>
      )}
      {success && (
        <div className="alert alert-success" style={{ marginBottom: '12px' }}>
          ✅ {success}
          <button onClick={() => setSuccess('')} style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>×</button>
        </div>
      )}

      {/* Manual Add Form */}
      {showManualAdd && (
        <div className="card" style={{ marginBottom: '16px', border: '2px solid #7c3aed' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: 600 }}>Add Manual Classification</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>Shopify Product ID *</label>
              <input
                className="form-control"
                placeholder="e.g. 8234567890123"
                value={manualForm.product_id}
                onChange={e => setManualForm(p => ({ ...p, product_id: e.target.value }))}
              />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>Product Title</label>
              <input
                className="form-control"
                placeholder="e.g. Specialized Tarmac SL8"
                value={manualForm.product_title}
                onChange={e => setManualForm(p => ({ ...p, product_title: e.target.value }))}
              />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>HS Code *</label>
              <input
                className="form-control"
                placeholder="e.g. 8712.00"
                value={manualForm.hs_code}
                onChange={e => setManualForm(p => ({ ...p, hs_code: e.target.value }))}
              />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>HS Description</label>
              <input
                className="form-control"
                placeholder="e.g. Bicycles, not motorised"
                value={manualForm.hs_description}
                onChange={e => setManualForm(p => ({ ...p, hs_description: e.target.value }))}
              />
            </div>
          </div>
          <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
            <button className="btn btn-primary btn-sm" onClick={handleManualAdd}>Save Classification</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowManualAdd(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Bulk action bar — shown when items are selected */}
      {selectedClassifications.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#1d4ed8' }}>
            {selectedClassifications.size} classification{selectedClassifications.size !== 1 ? 's' : ''} selected
          </span>
          <button
            className="btn btn-primary btn-sm"
            style={{ fontSize: '12px', padding: '4px 12px' }}
            onClick={handleBulkApprove}
            disabled={bulkApproving}
          >
            {bulkApproving ? 'Approving...' : `✓ Approve All Selected`}
          </button>
          <button
            className="btn btn-secondary btn-sm"
            style={{ fontSize: '12px', padding: '4px 10px' }}
            onClick={() => setSelectedClassifications(new Set())}
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Filters + count */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {['', 'ai_suggested', 'merchant_approved', 'merchant_overridden', 'shopify_native'].map(s => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              style={{
                padding: '4px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', border: '1px solid',
                background: statusFilter === s ? '#1d4ed8' : '#fff',
                color: statusFilter === s ? '#fff' : '#374151',
                borderColor: statusFilter === s ? '#1d4ed8' : '#d1d5db',
              }}
            >
              {s === '' ? 'All' : s === 'ai_suggested' ? 'AI Suggested' : s === 'merchant_approved' ? 'Approved' : s === 'merchant_overridden' ? 'Overridden' : '🛍️ Shopify Native'}
            </button>
          ))}
        </div>
        <span style={{ fontSize: '13px', color: '#6b7280' }}>{total} classification{total !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading classifications...</div>
        ) : classifications.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            <p style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>No classifications yet</p>
            <p style={{ fontSize: '14px' }}>Use the Calculator tab to classify products, or add them manually above.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '10px 12px', width: '36px' }}>
                    <input
                      type="checkbox"
                      title="Select all AI Suggested"
                      checked={classifications.filter(c => c.status === 'ai_suggested').length > 0 &&
                        classifications.filter(c => c.status === 'ai_suggested').every(c => selectedClassifications.has(c.product_id))}
                      onChange={e => {
                        const aiItems = classifications.filter(c => c.status === 'ai_suggested').map(c => c.product_id);
                        if (e.target.checked) {
                          setSelectedClassifications(prev => new Set([...prev, ...aiItems]));
                        } else {
                          setSelectedClassifications(prev => { const n = new Set(prev); aiItems.forEach(id => n.delete(id)); return n; });
                        }
                      }}
                    />
                  </th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Product</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>HS Code</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Category</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Confidence</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Status</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Shopify</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {classifications.map((item, idx) => (
                  <React.Fragment key={item.product_id}>
                    <tr style={{ borderBottom: '1px solid #f3f4f6', background: selectedClassifications.has(item.product_id) ? '#eff6ff' : idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '10px 12px', width: '36px' }}>
                        {item.status === 'ai_suggested' && (
                          <input
                            type="checkbox"
                            checked={selectedClassifications.has(item.product_id)}
                            onChange={e => {
                              setSelectedClassifications(prev => {
                                const n = new Set(prev);
                                if (e.target.checked) n.add(item.product_id); else n.delete(item.product_id);
                                return n;
                              });
                            }}
                          />
                        )}
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <div style={{ fontWeight: 600, color: '#111827', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.product_title || item.product_id}
                        </div>
                        {item.vendor && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{item.vendor}</div>}
                        <div style={{ fontSize: '10px', color: '#d1d5db', marginTop: '2px', fontFamily: 'monospace' }}>ID: {item.product_id}</div>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ fontWeight: 700, color: '#1d4ed8', fontFamily: 'monospace', fontSize: '14px' }}>{item.hs_code}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.hs_description}</div>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontSize: '12px', background: '#f3f4f6', padding: '2px 8px', borderRadius: '4px', color: '#374151' }}>{item.category}</span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>{confidenceBar(item.confidence)}</td>
                      <td style={{ padding: '10px 12px' }}>{statusBadge(item.status)}</td>
                      <td style={{ padding: '10px 12px' }}>
                        {item.shopify_synced_at ? (
                          <span title={`Synced ${new Date(item.shopify_synced_at).toLocaleString()}`} style={{ fontSize: '13px', color: '#059669', fontWeight: 600 }}>
                            ✅ Synced
                          </span>
                        ) : (item.status === 'merchant_approved' || item.status === 'merchant_overridden') ? (
                          <span style={{ fontSize: '12px', color: '#d97706', fontWeight: 600 }}>Pending</span>
                        ) : (
                          <span style={{ fontSize: '12px', color: '#9ca3af' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {item.status === 'ai_suggested' && (
                            <button
                              className="btn btn-primary btn-sm"
                              style={{ fontSize: '11px', padding: '3px 8px' }}
                              onClick={() => handleApprove(item.product_id)}
                            >
                              Approve
                            </button>
                          )}
                          <button
                            className="btn btn-secondary btn-sm"
                            style={{ fontSize: '11px', padding: '3px 8px' }}
                            onClick={() => editingId === item.product_id ? setEditingId(null) : handleStartEdit(item)}
                          >
                            {editingId === item.product_id ? 'Cancel' : 'Override'}
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            style={{ fontSize: '11px', padding: '3px 8px', color: '#dc2626', borderColor: '#fca5a5' }}
                            onClick={() => handleDelete(item.product_id)}
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                    {/* Inline edit row */}
                    {editingId === item.product_id && (
                      <tr style={{ background: '#faf5ff', borderBottom: '2px solid #7c3aed' }}>
                        <td colSpan={6} style={{ padding: '16px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '12px' }}>
                            <div>
                              <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>HS Code *</label>
                              <input
                                className="form-control"
                                value={editForm.hs_code}
                                onChange={e => setEditForm(p => ({ ...p, hs_code: e.target.value }))}
                                placeholder="e.g. 8712.00"
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>HS Description</label>
                              <input
                                className="form-control"
                                value={editForm.hs_description}
                                onChange={e => setEditForm(p => ({ ...p, hs_description: e.target.value }))}
                                placeholder="Official HS description"
                              />
                            </div>
                          </div>
                          {/* Duty rate overrides */}
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Per-Country Duty Rate Overrides (optional)</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                              {Object.entries(editForm.duty_rate_overrides).map(([country, rate]) => (
                                <div key={country} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#ede9fe', padding: '4px 8px', borderRadius: '6px', fontSize: '12px' }}>
                                  <span style={{ fontWeight: 600 }}>{country}:</span>
                                  <input
                                    type="number"
                                    min="0" max="200" step="0.1"
                                    value={rate}
                                    onChange={e => updateDutyOverride(country, e.target.value)}
                                    style={{ width: '52px', padding: '2px 4px', border: '1px solid #c4b5fd', borderRadius: '4px', fontSize: '12px' }}
                                  />
                                  <span>%</span>
                                  <button onClick={() => removeDutyOverride(country)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7c3aed', fontWeight: 700 }}>×</button>
                                </div>
                              ))}
                            </div>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                              <input
                                id={`new-country-${item.product_id}`}
                                className="form-control"
                                placeholder="Country code e.g. US"
                                style={{ width: '120px' }}
                                maxLength={2}
                              />
                              <input
                                id={`new-rate-${item.product_id}`}
                                className="form-control"
                                placeholder="Rate %"
                                type="number" min="0" max="200" step="0.1"
                                style={{ width: '80px' }}
                              />
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => {
                                  const c = document.getElementById(`new-country-${item.product_id}`)?.value?.toUpperCase();
                                  const r = document.getElementById(`new-rate-${item.product_id}`)?.value;
                                  if (c && r) updateDutyOverride(c, r);
                                }}
                              >Add</button>
                            </div>
                            <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Leave empty to use standard HS code rates. Overrides take precedence over all other rate sources.</p>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button className="btn btn-primary btn-sm" onClick={() => handleSaveOverride(item.product_id)}>Save Override</button>
                            <button className="btn btn-secondary btn-sm" onClick={() => setEditingId(null)}>Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
          <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span style={{ padding: '6px 12px', fontSize: '13px', color: '#6b7280' }}>Page {page} of {totalPages}</span>
          <button className="btn btn-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}

      {/* Info box */}
      <div style={{ marginTop: '16px', padding: '12px 16px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', fontSize: '13px', color: '#1e40af' }}>
        <strong>How it works:</strong> When a customer views a product page, the app uses the classified HS code to look up the exact duty rate for their country.
        AI suggestions are indicative — always review and approve classifications for your most important products.
        Manual overrides and per-country duty rate overrides take precedence over all other rate sources.
        <br /><br />
        <strong>Shopify Sync:</strong> Once you approve or override a classification, use <strong>Push All to Shopify</strong> to write the HS code directly to each product variant’s <code>harmonized_system_code</code> field in Shopify.
        This powers Shopify Markets duty estimates, carrier customs forms (DHL, FedEx, UPS), and any third-party integrations that read your product data.
      </div>
    </div>
  );
};

export default App;
import CourierProcessingFeeSettings from './components/CourierProcessingFeeSettings';
